// client/src/pages/Optimus.js
// (CLIENT – superadmin-aligned layout + LIVE where available + ingested fallback + platform tabs + ALL totals + campaign optional)
//
// ✅ Mirrors SUPERADMIN Optimus feature set:
// - Media Mix Insights & Benchmarks
// - Targeting & Flight Specs
// - Sherloc AI Recommendations next to Media Mix section (in flow)
// - Plan vs Live: Optimus Grade
// - Flight / 30D / 14D / 7D (Flight default)
// - Platform tabs + forced Meta breakdown tabs when Meta configured
// - Meta LIVE split accuracy (FB/IG/AN/Messenger) with TRUE ZERO handling
// - All Platforms prevents double-counting Meta by treating Meta as one bundle
// - API base hardening (never accidentally hits frontend origin unless needed)
//
// Advertiser scope is governed by the shared image-based selector and catalog.
//
// NOTE (CLIENT AUTH):
// This file tries multiple localStorage keys for tokens: authToken, clientAuthToken, token.
// If your client portal uses a different key, add it in the token lookup below.

import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import "./Optimus.css";
import "./ClientAIOptimizations.css";
import "./CampaignOptimizerDesktopWidth.css";
import PoweredByLogo from "../components/PoweredByLogo";
import OptimusSectionBoundary from "../components/OptimusSectionBoundary";
import OptimusPlatformIdentity from "../components/OptimusPlatformIdentity";
import {
  AdvertiserIdentityTrigger,
  GovernedAdvertiserSelectorModal,
} from "../components/GovernedAdvertiserSelector";
import useGovernedAdvertiserCatalog from "../hooks/useGovernedAdvertiserCatalog";
import facebookIcon from "../assets/facebook.jpg";
import instagramIcon from "../assets/instagram.jpg";
import metaIcon from "../assets/meta.jpg";
import tiktokIcon from "../assets/tiktok.jpg";
import youtubeIcon from "../assets/youtube.jpg";
import googleIcon from "../assets/google.jpg";
import openaiIcon from "../assets/openAI.png";
import redditIcon from "../assets/reddit.jpg";
import amazonIcon from "../assets/amazon.jpg";
import yahooIcon from "../assets/yahoo.png";
import viantIcon from "../assets/viant.png";
import { governedProviderSpend, formatProviderCurrency } from "../utils/governedProviderEvidence";
import { normalizeProviderSurfaceKey } from "../utils/providerSurfaceRegistry";
import audioIcon from "../assets/audio.jpg";
import ctvIcon from "../assets/ctv.jpg";
import videoIcon from "../assets/video.jpg";
import mobileIcon from "../assets/mobile.jpg";
import displayIcon from "../assets/display.jpg";
import doohIcon from "../assets/dooh.jpg";
import emailIcon from "../assets/email.jpg";
import rumbleIcon from "../assets/rumble-logo.jpg";
import truthSocialIcon from "../assets/truth-social-logo.jpg";
import { useLocation, useNavigate } from "react-router-dom";
import { useAdvertiser } from "../contexts/AdvertiserContext";
import usePageAwareness from "../hooks/usePageAwareness";
import { formatFlightRange, parseFlightDateSafe } from "../utils/flightDates";
/* THE governed Optimus money vocabulary — one "$", one rounding rule, both
   portals. Never re-implemented inline; see utils/optimusMoneyFormat.js. */
import {
  formatOptimusMoney,
  formatOptimusCount,
  formatOptimusPercent,
} from "../utils/optimusMoneyFormat";
import {
  resolveBlendedEcpmBadge,
  resolveBlendedEcpmSubline,
  resolveBlendedEcpmUnavailableReason,
  resolveBlendedEcpmDecomposition,
} from "../utils/blendedEcpmPresentation";
import {
  buildAdvertiserFallbackPlan,
  buildCampaignPlanInput,
  buildMergedApprovedPricingPlanInput,
  buildCanonicalPacing,
  deriveClientFacingEfficiencyMeta,
  deriveClientFacingSpendValue,
  isApprovedPlanCampaign,
  parseBudgetValue,
  resolveOptimizerRange,
  resolvePlannedPlatformAllocation,
  resolveClientBilledFinancialTruth,
  resolveOptimusPacingStatus,
  selectApprovedPlanCampaign,
  selectPlannerContextCampaign,
} from "../utils/optimusPacing";
import { formatCostPerCompletedView } from "../platformAnalytics/costPerCompletedView";
import {
  resolveSpendEfficiencyContract,
  resolveSpendEfficiencyMetric,
} from "../platformAnalytics/spendEfficiencyContract";
import {
  OPTIMUS_CONTRACT_STATE,
  buildOptimusContractFlightRange,
  contractMatchesSelection,
  describeOptimusContractDiagnostic,
  resolveOptimusContractPlatformTabs,
} from "../utils/optimusCampaignContract";
import {
  OPTIMUS_LIFECYCLE,
  resolveOptimusLifecycle,
  resolveFlightHold,
  canPublishOptimusError,
} from "../utils/optimusLifecycle";
import {
  computeReportingMetrics,
  resolveReportingTruth,
} from "../utils/reportingTruthResolver";
import {
  pickCanonicalRates,
  pickCanonicalSignalRates,
  resolveGovernedRate,
} from "../utils/optimusCanonicalRates";
import {
  extractImportedReportingCampaigns,
  IMPORTED_REPORTING_CONTEXT_PATH,
} from "../utils/optimusCampaignOptions";
import {
  buildOptimusCampaignOptions,
  formatCampaignOptionLabel,
  resolveOptimusSelectedCampaign,
} from "../utils/optimusGovernedCampaignOptions";
import { getCanonicalCampaignDisplayName } from "../utils/campaignDisplayName";
import {
  buildOptimizerPlatformTabs,
  expandOptimizerPlatformIds,
} from "../utils/optimusPlatformTabs";
import { getToplinePerformance } from "../services/analyticsToplineService";
import {
  normalizeOptimusPlatformKey,
} from "../utils/optimusGradingContract";
import { normalizeAdvertiserVertical } from "../config/verticalDashboardConfig";
import { serializeVerticalIntelligence } from "../config/verticalIntelligenceConfig";

// -----------------------------
// ✅ API base hardening (CRA-safe only)
// -----------------------------
const normalizeBase = (base) => {
  if (!base) return "";
  let b = String(base).trim();
  b = b.replace(/\/+$/, "");
  if (b.toLowerCase().endsWith("/api")) b = b.slice(0, -4);
  return b;
};

const DEFAULT_PRODUCTION_BACKEND_URL = "https://cphere-backend.onrender.com";
const API_BASE_URL = normalizeBase(
  process.env.REACT_APP_BACKEND_URL ||
    (process.env.NODE_ENV === "production" ? DEFAULT_PRODUCTION_BACKEND_URL : "")
);
const OVERVIEW_FETCH_TIMEOUT_MS = 18000;

const apiUrl = (path) => {
  const p = String(path || "");
  const safePath = p.startsWith("/") ? p : `/${p}`;

  if (API_BASE_URL) return `${API_BASE_URL}${safePath}`;

  if (typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin}${safePath}`;
  }

  return safePath;
};
const withOverviewTimeout = (promise, label = "performance overview request", timeoutMs = OVERVIEW_FETCH_TIMEOUT_MS) =>
  new Promise((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      settled = true;
      if (process.env.NODE_ENV !== "production") {
        console.warn(`[Optimus] ${label} timed out after ${timeoutMs}ms`);
      }
      resolve(null);
    }, timeoutMs);

    Promise.resolve(promise)
      .then((value) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(value);
      })
      .catch((error) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        reject(error);
      });
  });

const normalize = (v) => String(v || "").trim().toLowerCase();
const normalizePlatformKey = (v) =>
  normalizeProviderSurfaceKey(v) || String(v || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
const presetLabel = (p) => String(p || "").replace(/_/g, " ");
const safeNum = (v) => {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string" && v.trim()) {
    const parsed = Number(v.replace(/[$,%\s,]/g, ""));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};
/* Evidence fragments withhold themselves entirely when a figure is absent
   (unavailable: null), rather than rendering a half-empty sentence. */
const formatEvidenceCurrency = (value) =>
  safeNum(value) === null ? null : formatOptimusMoney(safeNum(value), { unavailable: null });
const formatEvidenceInteger = (value) =>
  safeNum(value) === null ? null : formatOptimusCount(safeNum(value), { unavailable: null });
const formatEvidencePercent = (value) => {
  const n = safeNum(value);
  if (n === null) return null;
  return `${n.toFixed(2)}%`;
};
const deriveEvidenceFacts = (metrics = {}) => {
  const spend = safeNum(metrics.spend);
  const impressions = safeNum(metrics.impressions);
  const clicks = safeNum(metrics.clicks);
  const ctr = safeNum(metrics.ctr);
  const cpm = safeNum(metrics.cpm);
  const cpc =
    safeNum(metrics.cpc) ??
    (spend !== null && clicks !== null && clicks > 0 ? spend / clicks : null);
  const conversions = safeNum(metrics.conversions ?? metrics.ticketSales);
  const completions = safeNum(
    metrics.videoCompletions ??
      metrics.video_completions ??
      metrics.completions ??
      metrics.video_completed
  );
  const completionRate = safeNum(
    metrics.videoCompletionRate ??
      metrics.video_completion_rate ??
      metrics.completionRate ??
      metrics.completion_rate
  );
  const engagements = safeNum(metrics.engagements);
  const evidence = [];

  if (spend !== null) evidence.push(`Spend ${formatEvidenceCurrency(spend)}`);
  if (impressions !== null) evidence.push(`impressions ${formatEvidenceInteger(impressions)}`);
  if (clicks !== null) evidence.push(`clicks ${formatEvidenceInteger(clicks)}`);
  if (ctr !== null) evidence.push(`CTR ${formatEvidencePercent(ctr)}`);
  if (cpc !== null) evidence.push(`CPC ${formatEvidenceCurrency(cpc)}`);
  if (cpm !== null) evidence.push(`CPM ${formatEvidenceCurrency(cpm)}`);
  if (conversions !== null) evidence.push(`conversions ${formatEvidenceInteger(conversions)}`);
  if (completions !== null) evidence.push(`completions ${formatEvidenceInteger(completions)}`);
  if (completionRate !== null) evidence.push(`completion rate ${formatEvidencePercent(completionRate)}`);
  if (engagements !== null) evidence.push(`engagements ${formatEvidenceInteger(engagements)}`);

  return {
    spend,
    impressions,
    clicks,
    ctr,
    cpm,
    cpc,
    conversions,
    completions,
    completionRate,
    engagements,
    evidenceLine: evidence.length ? `Based on ${evidence.slice(0, 6).join(", ")}` : "",
    hasDelivery: [spend, impressions, clicks, conversions, completions, engagements].some(
      (value) => value !== null && value > 0
    ),
  };
};
const hasOverviewKpiTotals = (metrics = {}) => {
  if (!metrics || typeof metrics !== "object") return false;
  return [
    metrics.spend,
    metrics.totalSpend,
    metrics.impressions,
    metrics.clicks,
    metrics.ctr,
    metrics.blendedCtr,
    metrics.cpm,
    metrics.blendedCpm,
    metrics.videoViews,
    metrics.engagements,
    metrics.videoCompletions,
    metrics.completions,
    metrics.videoCompletionRate,
    metrics.completionRate,
    metrics.ticketSales,
  ].some((value) => typeof value === "number" && Number.isFinite(value));
};
const metricsPayloadFromOverviewKpis = (state = {}) => ({
  ...pickCanonicalRates(state),
  spend: safeNum(state.totalSpend ?? state.spend),
  ctr: safeNum(state.blendedCtr ?? state.ctr),
  cpm: safeNum(state.blendedCpm ?? state.cpm),
  impressions: safeNum(state.impressions),
  clicks: safeNum(state.clicks),
  videoViews: safeNum(state.videoViews),
  qualifiedVideoViews: safeNum(state.qualifiedVideoViews),
  engagements: safeNum(state.engagements),
  completions: safeNum(state.videoCompletions ?? state.completions),
  completionRate: safeNum(state.videoCompletionRate ?? state.completionRate),
  ticketSales: safeNum(state.ticketSales),
});
const hasUsableOverviewSnapshot = (snapshot = {}) =>
  hasOverviewKpiTotals({
    spend: snapshot?.spend,
    totalSpend: snapshot?.totalSpend,
    impressions: snapshot?.impressions,
    clicks: snapshot?.clicks,
    ctr: snapshot?.ctr,
    blendedCtr: snapshot?.blendedCtr,
    cpm: snapshot?.cpm,
    blendedCpm: snapshot?.blendedCpm,
    videoViews: snapshot?.videoViews,
    engagements: snapshot?.engagements,
    completions: snapshot?.videoCompletions ?? snapshot?.completions,
    completionRate: snapshot?.videoCompletionRate ?? snapshot?.completionRate,
    ticketSales: snapshot?.ticketSales,
  });
const hasDeliverySignal = (metrics = {}) =>
  (safeNum(metrics.spend) || 0) > 0 || (safeNum(metrics.impressions) || 0) > 0;
const buildToplineTimeframeFromRange = ({ mode, datePreset, startDate, endDate } = {}) => {
  if (isFlightWindowSelection({ mode, datePreset, startDate, endDate })) {
    return { start: null, end: null, datePreset: "flight" };
  }

  const start = String(startDate || "").slice(0, 10);
  const end = String(endDate || "").slice(0, 10);
  if (start && end) {
    return { start, end, datePreset: "" };
  }
  return {
    start: null,
    end: null,
    datePreset: datePreset || "last_30d",
  };
};
const snapshotFromTopline = (topline, { platform = "all" } = {}) => {
  if (!topline || typeof topline !== "object") return null;
  const raw = topline.raw && typeof topline.raw === "object" ? topline.raw : {};
  const snapshot = {
    ...pickCanonicalRates(raw),
    spend: safeNum(raw.clientFacingSpend ?? raw.billedSpend ?? raw.spend),
    billedSpend: safeNum(raw.billedSpend ?? raw.clientFacingSpend),
    clientFacingSpend: safeNum(raw.clientFacingSpend ?? raw.billedSpend),
    ctr: safeNum(raw.ctr),
    cpm: safeNum(raw.clientFacingCpm ?? raw.billedCpm ?? raw.cpm),
    impressions: safeNum(raw.impressions),
    clicks: safeNum(raw.clicks),
    videoViews: safeNum(raw.videoViews),
    qualifiedVideoViews: safeNum(raw.qualifiedVideoViews ?? raw.qualified_video_views),
    engagements: safeNum(raw.engagements),
    completions: safeNum(raw.videoCompletions ?? raw.video_completions ?? raw.video_completed ?? raw.completions),
    completionRate: safeNum(raw.videoCompletionRate ?? raw.video_completion_rate ?? raw.vcr ?? raw.completionRate),
    ticketSales: safeNum(raw.conversions),
    byPlatform: null,
    dataSourcesByPlatform: null,
    dataSource: raw.snapshotDataSource || raw.snapshotSource || "shared_topline",
    clientBillingAttribution: null,
    sourceLabel: topline.raw?.snapshotSourceLabel || topline.raw?.snapshotSource || "Shared topline",
    platform,
  };
  const hasData =
    raw.usableDataFound === true ||
    raw.hasAnyData === true ||
    hasUsableOverviewSnapshot(snapshot);
  return hasData ? snapshot : null;
};
const normalizeGoalKey = (v) =>
  String(v || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
const isTicketSalesGoal = (v) => {
  const goal = normalizeGoalKey(v);
  return goal === "ticket_sales" || goal === "ticketsales";
};
const formatGoalLabel = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return raw;
  if (isTicketSalesGoal(raw)) return "Ticket Sales";
  return raw.replace(/_/g, " ");
};

const formatPlanSpecValue = (value) => {
  if (value === null || value === undefined) return null;
  if (Array.isArray(value)) {
    const items = value.map(formatPlanSpecValue).filter(Boolean);
    return items.length ? Array.from(new Set(items)).join(", ") : null;
  }
  if (typeof value === "object") {
    for (const key of ["label", "name", "value", "text", "summary", "description", "title"]) {
      const nested = formatPlanSpecValue(value?.[key]);
      if (nested) return nested;
    }
    return null;
  }

  const text = String(value).trim();
  if (!text) return null;
  const lower = text.toLowerCase();
  if (lower === "null" || lower === "undefined" || lower === "n/a") return null;
  return text;
};

const getPlanSpecSources = (planInput) => {
  const rawCampaign =
    planInput?.rawCampaign && typeof planInput.rawCampaign === "object"
      ? planInput.rawCampaign
      : planInput;

  return [
    rawCampaign,
    rawCampaign?.targeting,
    rawCampaign?.strategy_payload?.targeting,
    rawCampaign?.strategyPayload?.targeting,
    rawCampaign?.strategy_payload,
    rawCampaign?.strategyPayload,
    rawCampaign?.strategy_payload_contract,
    rawCampaign?.strategyPayloadContract,
    rawCampaign?.ai_plan,
    rawCampaign?.aiPlan,
    planInput,
  ].filter((source) => source && typeof source === "object");
};

const pickPlanSpecValue = (sources, keys) => {
  for (const source of sources) {
    for (const key of keys) {
      const formatted = formatPlanSpecValue(source?.[key]);
      if (formatted) return formatted;
    }
  }
  return null;
};

const formatPlanAudienceSpec = (sources) => {
  const direct = pickPlanSpecValue(sources, [
    "audience",
    "audienceTargeting",
    "audience_targeting",
    "targetAudience",
    "target_audience",
  ]);
  if (direct) return direct;

  const ageMin = pickPlanSpecValue(sources, ["ageMin", "age_min", "minAge", "min_age"]);
  const ageMax = pickPlanSpecValue(sources, ["ageMax", "age_max", "maxAge", "max_age"]);
  const gender = pickPlanSpecValue(sources, ["gender", "genderTargeting", "gender_targeting"]);
  const interests = pickPlanSpecValue(sources, [
    "interests",
    "interestTargeting",
    "interest_targeting",
  ]);
  const custom = pickPlanSpecValue(sources, ["customTargeting", "custom_targeting"]);

  const parts = [];
  if (ageMin || ageMax) parts.push(`Ages ${ageMin || "?"}${ageMax ? `-${ageMax}` : "+"}`);
  if (gender && gender.toLowerCase() !== "all") parts.push(`Gender: ${formatGoalLabel(gender)}`);
  if (interests) parts.push(`Interests: ${interests}`);
  if (custom) parts.push(`Custom: ${custom}`);
  return parts.length ? parts.join("; ") : null;
};

const formatPlanPlacementsSpec = (sources) => {
  const placements = pickPlanSpecValue(sources, [
    "placements",
    "placement",
    "targetPlacements",
    "target_placements",
    "placementNotes",
    "placement_notes",
    "inventory",
  ]);
  if (placements) return placements;

  const platforms = pickPlanSpecValue(sources, ["target_platforms", "targetPlatforms", "platforms"]);
  return platforms ? `Planned platforms: ${platforms}` : null;
};

const resolveApprovedPlanSpecFields = (planInput) => {
  const sources = getPlanSpecSources(planInput);
  if (!sources.length) return {};

  const primaryKpis = pickPlanSpecValue(sources, [
    "primary_kpi",
    "primaryKPI",
    "primaryKpi",
    "primary_hpi",
    "primaryHPI",
    "primaryHpi",
    "primaryHPIs",
    "primaryHpis",
    "kpi",
    "hpi",
    "planKpis",
    "planKPIs",
    "primaryMetric",
    "primaryMetrics",
  ]);
  const additionalKpis = pickPlanSpecValue(sources, [
    "additional_kpis",
    "additionalKPIs",
    "additionalKpis",
    "secondary_kpis",
    "secondaryKPIs",
    "measurement_kpis",
    "measurementKPIs",
  ]);
  const combinedKpis =
    primaryKpis && additionalKpis && primaryKpis !== additionalKpis
      ? `${primaryKpis}; Additional: ${additionalKpis}`
      : primaryKpis || additionalKpis;

  return {
    objective: pickPlanSpecValue(sources, ["objective", "goal_type", "goalType", "goal"]),
    primaryKPIs: combinedKpis,
    optimizationGoal:
      pickPlanSpecValue(sources, [
        "optimizationGoal",
        "optimization_goal",
        "optimizationFocus",
        "optimization_focus",
      ]) || combinedKpis,
    billing: pickPlanSpecValue(sources, [
      "pricing_basis",
      "pricingBasis",
      "billing",
      "billingType",
      "billing_type",
      "buyingModel",
      "buying_model",
      "rateType",
      "rate_type",
    ]),
    audience: formatPlanAudienceSpec(sources),
    /*
     * The PUBLISHED geography presentation wins over the stored note. For a
     * campaign with a governed cinema footprint the backend restates the note
     * as the footprint it describes ("Cinema footprint: 12 uploaded cinema
     * locations across the following ZIP codes: ..."), because the operator's
     * own phrasing ("these theater ZIPs: ...") reads on a client-facing spec
     * table as a claim about how the buy was executed — which the platform
     * does not record. The stored note stays available as the audit value and
     * is still the fallback for campaigns with no footprint.
     */
    geo: pickPlanSpecValue(sources, [
      "geoTargetingPresentation",
      "geo_targeting_presentation",
      "geoTargetingNotes",
      "geo_targeting_notes",
      "geo",
      "geos",
      "geoTargeting",
      "geo_targeting",
      "locationTargeting",
      "location_targeting",
    ]),
    placements: formatPlanPlacementsSpec(sources),
    devices: pickPlanSpecValue(sources, [
      "deviceTargetingNotes",
      "device_targeting_notes",
      "devices",
      "deviceTargeting",
      "device_targeting",
    ]),
    frequency: pickPlanSpecValue(sources, [
      "frequency",
      "frequencyCap",
      "frequency_cap",
      "frequencyNotes",
      "frequency_notes",
    ]),
    notes: pickPlanSpecValue(sources, [
      "campaignNotes",
      "campaign_notes",
      "additionalNotes",
      "additional_notes",
      "notes",
    ]),
  };
};

/*
 * Client-side backstop for internal-economics disclosure in free prose.
 *
 * Mirrors the server guard's segmenting rule (services/
 * internalEconomicsDisclosureGuard.js): split on sentence punctuation and on
 * the run-on boundary where a figure is followed by a capitalised word, then
 * drop any segment carrying a currency amount or net/gross/margin/fee/markup
 * wording. Fails closed -- if nothing survives, the note is suppressed.
 *
 * This is a SECOND line of defence. The server must never send such a value;
 * this exists so a stale cache, a hydrated historical payload, or a future
 * route that forgets the sanitizer cannot put net media on screen.
 */
const INTERNAL_ECONOMICS_PROSE = /\$\s?\d|(?:total\s+)?net\s+(?:working\s+)?media|net\s+(?:budget|spend|cost|cpm|rate)|gross\s+(?:budget|media|spend|cost|cpm|rate)|margin|mark[- ]?up|\bfee\b|rate\s+card/i;

const sanitizeClientFacingNotes = (value) => {
  if (typeof value !== "string" || !value.trim()) return "";
  if (!INTERNAL_ECONOMICS_PROSE.test(value)) return value;
  const kept = value
    .split(/(?<=[.;|\n])(?=\s|$)/)
    .flatMap((chunk) => chunk.split(/(?<=[\d%)])\s+(?=[A-Z][a-z])/))
    .map((segment) => segment.trim())
    .filter((segment) => segment && !INTERNAL_ECONOMICS_PROSE.test(segment));
  const next = kept.join(" ").replace(/\s{2,}/g, " ").trim();
  return next && !INTERNAL_ECONOMICS_PROSE.test(next) ? next : "";
};

const getOptimusPillClass = (tone) => {
  if (tone === "good") return "optx-pill optx-pill-good";
  if (tone === "ok") return "optx-pill optx-pill-ok";
  if (tone === "bad") return "optx-pill optx-pill-bad";
  return "optx-pill optx-pill-neutral";
};

/*
 * Backend presentation text is authored client-safe; this pass is defense in
 * depth for client-scoped viewers.
 */
const presentOptimusGradeText = (value) => scrubClientOptimusText(value, "");

const renderGradeDimensionCard = (card, key) => {
  if (!card) return null;
  const label = presentOptimusGradeText(card.label);
  const statusLabel = presentOptimusGradeText(card.statusLabel);
  const summary = presentOptimusGradeText(card.summary);
  return (
    <div className={`optx-grade-dimension optx-grade-dimension--${card.tone || "neutral"}`} key={key}>
      <div className="optx-grade-dimension-head">
        <span className="optx-grade-dimension-label">{label}</span>
        {statusLabel ? (
          <span className={`${getOptimusPillClass(card.tone)} optx-pill-compact`}>{statusLabel}</span>
        ) : null}
      </div>
      <div className="optx-grade-dimension-score">
        {Number.isFinite(card.score) ? card.score : "—"}
      </div>
      {summary ? <div className="optx-grade-dimension-summary">{summary}</div> : null}
    </div>
  );
};

const renderGradeInsight = (item, key) => {
  if (!item) return null;
  const statement = presentOptimusGradeText(item.statement);
  if (!statement) return null;
  return (
    <li className="optx-grade-insight" key={key}>
      {statement}
    </li>
  );
};

const renderGradeDetailLine = (label, value, key) => {
  const presentedValue = presentOptimusGradeText(value);
  if (!presentedValue) return null;
  return (
    <div className="optx-grade-detail-line" key={key}>
      <span className="optx-grade-detail-label">{presentOptimusGradeText(label)}</span>
      <span className="optx-grade-detail-value">{presentedValue}</span>
    </div>
  );
};

// ✅ Exclude non-media "platforms" that may be returned by backend
const EXCLUDED_PLATFORM_IDS = new Set(["cinema"]);
const isExcludedPlatformId = (id) => EXCLUDED_PLATFORM_IDS.has(normalize(id));

// ✅ Meta bundle ids (prevents double-counting in All Platforms)
const META_PLATFORM_IDS = new Set(["facebook", "instagram", "audience_network", "messenger"]);
const isMetaPlatformId = (id) => META_PLATFORM_IDS.has(normalize(id));
const CONTEXT_CONDITIONAL_PLATFORM_IDS = new Set(["audience_network", "messenger"]);
const isContextConditionalPlatformId = (id) => CONTEXT_CONDITIONAL_PLATFORM_IDS.has(normalize(id));
const PROGRAMMATIC_BILLABLE_KEYS = new Set([
  "audio",
  "ingame",
  "ctv",
  "video",
  "mobile",
  "display",
  "dooh",
  "email",
]);

const PLATFORM_MAP = {
  facebook: "Facebook",
  instagram: "Instagram",

  // ✅ Meta breakdown tabs
  audience_network: "Audience Network",
  messenger: "Messenger",

  tiktok: "TikTok",
  youtube: "YouTube",
  x: "X",
  truthsocial: "Truth Social",
  rumble: "Rumble",
  googlesearch: "Google Search",
  chatgptsearch: "ChatGPT Search",

  // ✅ New
  reddit: "Reddit",
  amazon: "Amazon",
  yahoo_dsp: "Yahoo!",
  viant: "Viant",

  audio: "Audio",
  ingame: "In-Game",
  ctv: "CTV",
  video: "Video",
  mobile: "Mobile",
  display: "Display",
  dooh: "DOOH",
  email: "Email",
  all: "All Platforms",
};

const platformLabel = (id) => PLATFORM_MAP[normalize(id)] || String(id || "").toUpperCase();

/*
 * Did the reporting layer answer through the canonical contract?
 *
 * Module-scoped because BOTH snapshot builders need the same answer: the
 * per-platform path uses it to decide which view wins, and the all-platforms
 * path uses it to decide whether a platform's ABSENCE from the payload is
 * evidence of zero delivery or merely evidence of a gap. Two copies of this
 * predicate would be two definitions of "complete".
 */
const isCanonicalSnapshot = (snapshot) =>
  Boolean(
    snapshot?.snapshotProvider === "canonical_performance_summary" ||
      snapshot?.sourceContract?.contract === "canonical_reporting_v1" ||
      String(snapshot?.dataSource || "")
        .trim()
        .toLowerCase()
        .includes("canonical_")
  );

const hasMeaningfulPlatformDelivery = (metrics = {}) => {
  if (!metrics || typeof metrics !== "object") return false;
  return [
    metrics.spend,
    metrics.impressions,
    metrics.clicks,
    metrics.videoViews,
    metrics.video_views,
    metrics.engagements,
    metrics.completions,
    metrics.conversions,
    metrics.ticketSales,
    metrics.ticket_sales,
  ].some((value) => {
    const n = safeNum(value);
    return typeof n === "number" && n > 0;
  });
};

const splitChannelValues = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    return value
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean);
  }
  return [];
};

const PLATFORM_HINT_KEYS = [
  "platforms",
  "target_platforms",
  "targetPlatforms",
  "planned_platforms",
  "plannedPlatforms",
  "channels",
  "channel",
  "placements",
  "placement",
  "planned_placements",
  "plannedPlacements",
  "publisher_platforms",
  "publisherPlatforms",
];

const collectCampaignPlatformHints = (campaign = {}) => {
  const sources = [
    campaign,
    campaign?.targeting,
    campaign?.strategy_payload,
    campaign?.strategyPayload,
    campaign?.strategy_payload?.targeting,
    campaign?.strategyPayload?.targeting,
    campaign?.strategy_payload_contract,
    campaign?.strategyPayloadContract,
  ].filter((source) => source && typeof source === "object");

  const values = [];
  for (const source of sources) {
    for (const key of PLATFORM_HINT_KEYS) {
      values.push(...splitChannelValues(source?.[key]));
    }
  }
  return values;
};

const PLATFORM_ICON_MAP = {
  facebook: facebookIcon,
  instagram: instagramIcon,
  audience_network: metaIcon,
  messenger: metaIcon,
  tiktok: tiktokIcon,
  youtube: youtubeIcon,
  googlesearch: googleIcon,
  chatgptsearch: openaiIcon,
  reddit: redditIcon,
  amazon: amazonIcon,
  yahoo: yahooIcon,
  yahoo_dsp: yahooIcon,
  viant: viantIcon,
  audio: audioIcon,
  ctv: ctvIcon,
  video: videoIcon,
  mobile: mobileIcon,
  display: displayIcon,
  dooh: doohIcon,
  email: emailIcon,
  rumble: rumbleIcon,
  truthsocial: truthSocialIcon,
};

const platformIconSrc = (id) => PLATFORM_ICON_MAP[normalize(id)] || null;

const PLATFORM_CAPS = {
  all: { clickable: false, ctrApplicable: true, cpmApplicable: true, completionApplicable: true },

  facebook: { clickable: true, ctrApplicable: true, cpmApplicable: true, completionApplicable: true },
  instagram: { clickable: true, ctrApplicable: true, cpmApplicable: true, completionApplicable: true },

  audience_network: { clickable: true, ctrApplicable: true, cpmApplicable: true, completionApplicable: false },
  messenger: { clickable: true, ctrApplicable: true, cpmApplicable: true, completionApplicable: false },

  tiktok: { clickable: true, ctrApplicable: true, cpmApplicable: true, completionApplicable: true },
  youtube: { clickable: true, ctrApplicable: true, cpmApplicable: true, completionApplicable: true },

  x: { clickable: true, ctrApplicable: true, cpmApplicable: true, completionApplicable: false },
  truthsocial: { clickable: true, ctrApplicable: true, cpmApplicable: true, completionApplicable: false },
  rumble: { clickable: true, ctrApplicable: true, cpmApplicable: true, completionApplicable: true },

  reddit: { clickable: true, ctrApplicable: true, cpmApplicable: true, completionApplicable: false },
  amazon: { clickable: true, ctrApplicable: true, cpmApplicable: true, completionApplicable: false },

  googlesearch: { clickable: true, ctrApplicable: true, cpmApplicable: false, completionApplicable: false },
  chatgptsearch: { clickable: true, ctrApplicable: true, cpmApplicable: false, completionApplicable: false },

  audio: { clickable: false, ctrApplicable: false, cpmApplicable: true, completionApplicable: false },
  ingame: { clickable: true, ctrApplicable: true, cpmApplicable: true, completionApplicable: true },
  ctv: { clickable: false, ctrApplicable: false, cpmApplicable: true, completionApplicable: true },
  video: { clickable: true, ctrApplicable: true, cpmApplicable: true, completionApplicable: true },
  mobile: { clickable: true, ctrApplicable: true, cpmApplicable: true, completionApplicable: false },
  display: { clickable: true, ctrApplicable: true, cpmApplicable: true, completionApplicable: false },
  dooh: { clickable: false, ctrApplicable: false, cpmApplicable: true, completionApplicable: false },
  email: { clickable: true, ctrApplicable: true, cpmApplicable: false, completionApplicable: false },
};

const FORBIDDEN_PHRASES_BY_PLATFORM = {
  dooh: [
    "thumb stop",
    "thumb-stop",
    "scroll",
    "swipe",
    "tap",
    "click",
    "ctr",
    "cpc",
    "landing page",
    "link click",
    "hook in first 3 seconds",
    "thumbnail",
    "reels",
    "stories",
  ],
  ctv: [
    "thumb stop",
    "thumb-stop",
    "scroll",
    "swipe",
    "tap",
    "click",
    "ctr",
    "cpc",
    "landing page",
    "link click",
    "thumbnail",
    "reels",
    "stories",
  ],
  audio: ["thumb stop", "scroll", "swipe", "thumbnail", "reels", "stories", "ctr", "cpc", "landing page"],
};

const buildChannelProfileForPrompt = (platformId) => {
  const p = normalizeProviderSurfaceKey(platformId) || normalize(platformId);
  if (p === "yahoo_dsp" || p === "viant") {
    return {
      platform: p,
      label: platformLabel(p),
      capabilities: { clickable: false, ctrApplicable: false, cpmApplicable: false, completionApplicable: false, recommendationEnabled: false, executionEnabled: false },
      primaryKPIs: ["spend", "impressions", "clicks"],
      forbiddenConcepts: [],
      playbook: "Provider identity spans inventory channels. Describe only scoped canonical reporting evidence; grading, recommendations and execution are unavailable until governed capabilities and benchmarks are established.",
    };
  }
  const caps = PLATFORM_CAPS[p] || PLATFORM_CAPS.all;

  const primaryKPIs =
    p === "ctv"
      ? ["spend", "cpm", "completions", "completionRate", "reach", "frequency"]
      : p === "video" || p === "youtube"
      ? ["spend", "cpm", "completions", "completionRate", "views", "vtr"]
      : p === "googlesearch"
      ? ["spend", "cpc", "conversions", "cvr", "impressionShare"]
      : p === "amazon"
      ? // Only what the Amazon Sponsored Ads import provides. Amazon
        // conversions, attributed sales, ROAS and detail page views are not
        // imported or totalled, so they are never asked for.
        ["spend", "impressions", "clicks", "ctr", "cpc", "cpm"]
      : p === "reddit"
      ? ["spend", "cpm", "ctr", "clicks", "conversions", "cpa"]
      : p === "dooh"
      ? ["spend", "cpm", "reach", "frequency", "geoLift", "dayparting"]
      : p === "audio"
      ? ["spend", "cpm", "reach", "frequency", "incrementalLift"]
      : p === "email"
      ? ["deliverability", "openRate", "ctr", "conversions", "unsubRate"]
      : ["spend", "ctr", "cpm", "conversions", "cpa", "roas"];

  const forbiddenConcepts = FORBIDDEN_PHRASES_BY_PLATFORM[p] || [];

  const playbook =
    p === "dooh"
      ? "DOOH is real-world media: focus on geo + venue mix, dayparting, creative legibility (distance), frequency management, and lift measurement. Do not reference swipes, clicks, or thumb-stopping."
      : p === "ctv"
      ? "CTV is big-screen video: focus on completion rate, reach, frequency, household coverage, creative length/rotation, and incremental lift. Avoid click-based language."
      : p === "audio"
      ? "Audio is message frequency + repetition: focus on reach, frequency, dayparting, context targeting, creative rotation, and lift measurement. Avoid visual/social mechanics."
      : p === "googlesearch"
      ? "Search is intent capture: focus on query coverage, match types, negatives, ad relevance, landing page alignment, and conversion rate. CPM language is not primary."
      : p === "amazon"
      ? "Amazon Ads is commerce-native: optimize product/landing relevance, audience signals, placement mix and creative clarity, judged on spend, impressions, clicks, CTR, CPC and CPM. Amazon conversions, sales and ROAS are not totalled, so do not cite them."
      : p === "reddit"
      ? "Reddit is community-contextual: align creative to subculture context, test headlines/copy variants, and optimize toward efficient attention + downstream action."
      : "Stay platform-native and recommend only tactics that match the channel’s mechanics and KPIs.";

  return {
    platform: p,
    label: platformLabel(p),
    capabilities: caps,
    primaryKPIs,
    forbiddenConcepts,
    playbook,
  };
};

const filterPlatformIncompatibleRecs = (platform, recs) => {
  const p = normalizeProviderSurfaceKey(platform) || normalize(platform);
  if (p === "yahoo_dsp" || p === "viant") return [];
  const forbidden = FORBIDDEN_PHRASES_BY_PLATFORM[p];
  if (!forbidden?.length || !Array.isArray(recs)) return recs;

  const bad = forbidden.map((s) => normalize(s)).filter(Boolean);

  return recs.filter((r) => {
    const text = normalize(`${r?.title || ""} ${r?.summary || ""} ${r?.category || ""}`);
    if (!text) return true;
    return !bad.some((phrase) => phrase && text.includes(phrase));
  });
};

// ✅ Tab de-dupe + stable ordering (All first)
const dedupeTabs = (rawTabs, preserveOrder = false) => {
  const seen = new Set();
  const list = [];

  const normalized = Array.isArray(rawTabs) ? rawTabs : [];
  for (const t of normalized) {
    const id = normalize(t?.id);
    if (!id) continue;
    if (isExcludedPlatformId(id)) continue;
    if (seen.has(id)) continue;
    seen.add(id);
    list.push({ id, label: t?.label || platformLabel(id) });
  }

  if (!list.some((t) => t.id === "all")) list.unshift({ id: "all", label: "All Platforms" });

  if (!preserveOrder) return buildOptimizerPlatformTabs({ tabs: list, isExcludedPlatformId });
  const all = list.find((t) => t.id === "all");
  const rest = list.filter((t) => t.id !== "all");
  return all ? [all, ...rest] : rest;
};

// ✅ Range presets (Flight default in state below)
const RANGE_PRESETS = {
  "7d": { label: "7D", mode: "preset", datePreset: "last_7d" },
  "14d": { label: "14D", mode: "preset", datePreset: "last_14d" },
  "30d": { label: "30D", mode: "preset", datePreset: "last_30d" },
  flight: { label: "Flight", mode: "flight" },
};
const RANGE_PRESET_RENDER_ORDER = ["flight", "30d", "14d", "7d"];
/* Diagnostics for the AUTOMATIC snapshot load. The manual "Run Optimizations"
   control it was named for is gone; only the saved-recommendation fallback
   path still logs here. */
const OPTIMUS_RUN_DEBUG_PREFIX = "[Client Optimus][Snapshot]";

const summarizeOptimusRunMetrics = (metrics = {}) => {
  if (!metrics || typeof metrics !== "object") return { hasMetrics: false };
  return {
    hasMetrics: hasOverviewKpiTotals(metrics),
    spend: safeNum(metrics.spend),
    impressions: safeNum(metrics.impressions),
    clicks: safeNum(metrics.clicks),
    ctr: safeNum(metrics.ctr),
    cpm: safeNum(metrics.cpm),
    videoViews: safeNum(metrics.videoViews),
    qualifiedVideoViews: safeNum(metrics.qualifiedVideoViews),
    engagements: safeNum(metrics.engagements),
    engagementRate: safeNum(metrics.engagementRate),
    videoViewRate: safeNum(metrics.videoViewRate),
    completions: safeNum(metrics.completions),
    completionRate: safeNum(metrics.completionRate),
    ticketSales: safeNum(metrics.ticketSales),
    hasPrior: Boolean(metrics.prior && typeof metrics.prior === "object"),
    signature: buildOptimusMetricSignature(metrics),
  };
};


const logOptimusRun = (event, details = {}, level = "info") => {
  const logger = level === "error" ? console.error : level === "warn" ? console.warn : console.info;
  logger(`${OPTIMUS_RUN_DEBUG_PREFIX} ${event}`, details);
};

/*
 * PHASE 1 GRADING SAFETY REMEDIATION
 *
 * The hardcoded PLATFORM_BENCHMARKS table that used to live here has been
 * removed. Benchmark applicability, provenance, and grading eligibility now
 * come from the canonical contract mirror (src/utils/optimusGradingContract),
 * whose authority is the backend module services/optimusGradingContract.js.
 *
 * What that removal fixes, concretely:
 *   - the generic ALL band (cpm [6,22] / ctr [1.0,2.0]) no longer grades a
 *     blended CTV + YouTube mix it has no authority over;
 *   - `PLATFORM_BENCHMARKS[key] || PLATFORM_BENCHMARKS.all` no longer silently
 *     grades uncovered platforms against that generic band;
 *   - "No data" and "Not applicable" no longer collapse into one neutral tone
 *     worth 65 points.
 *
 * Do not reintroduce a benchmark literal in this file. Cross-repo parity is
 * enforced by backend tests/optimusGradingContractParity.test.js.
 */

// ✅ Template specs
const TARGETING_SPECS_BY_PLATFORM = {
  all: {
    objective: "Omni-Channel Optimization",
    optimizationGoal: "Lift outcomes across active platforms",
    billing: "Channel-dependent",
    audience: "Active audience mix (by channel)",
    geo: "Configured geos (by channel / theaters / DMAs)",
    placements: "Active placements (by channel)",
    devices: "Cross-device",
    frequency: "Balanced",
    notes:
      "All Platforms is a blended view. Select a specific platform tab for channel-native targeting, placements, and pacing details.",
  },
  facebook: {
    objective: "Awareness / Consideration",
    optimizationGoal: "Efficient reach + engagement",
    billing: "Impressions (CPM)",
    audience: "Broad + lookalikes + retargeting layers",
    geo: "DMAs / states with theater density",
    placements: "Feeds + Reels + Stories (as eligible)",
    devices: "Mobile-first",
    frequency: "Managed to avoid early saturation",
    notes: "Use creative sequencing and retargeting windows to convert awareness into action.",
  },
  instagram: {
    objective: "Awareness / Engagement",
    optimizationGoal: "High-quality attention",
    billing: "Impressions (CPM)",
    audience: "Interest + lookalike + engagers",
    geo: "DMAs / states with theater density",
    placements: "Reels + Stories + Feed",
    devices: "Mobile-first",
    frequency: "Balanced with creative rotation",
    notes: "Reels/Stories need strong visual hooks and readable super text.",
  },
  audience_network: {
    objective: "Awareness / Consideration",
    optimizationGoal: "Efficient reach across off-platform inventory",
    billing: "Impressions (CPM) / Clicks (CPC) depending on delivery",
    audience: "Inherited from Meta targeting (broad + lookalikes + retargeting)",
    geo: "Meta configured geos (DMAs / states)",
    placements: "Audience Network placements (in-app / off-platform)",
    devices: "Mobile-heavy",
    frequency: "Managed (watch for saturation)",
    notes:
      "Audience Network is off-platform inventory. Validate brand safety and quality, and ensure placements align with creative legibility and KPI goals.",
  },
  messenger: {
    objective: "Awareness / Engagement",
    optimizationGoal: "Efficient attention in conversational surfaces",
    billing: "Impressions (CPM)",
    audience: "Inherited from Meta targeting (broad + retargeting + lookalikes)",
    geo: "Meta configured geos (DMAs / states)",
    placements: "Messenger placements (as eligible)",
    devices: "Mobile",
    frequency: "Balanced with creative rotation",
    notes: "Messenger placements can behave differently than Feed/Reels. Watch CTR + CPM and creative fatigue closely.",
  },
  tiktok: {
    objective: "Awareness / Engagement",
    optimizationGoal: "Completion + engagement density",
    billing: "Impressions (CPM)",
    audience: "Broad + interest + lookalike",
    geo: "Priority DMAs around theaters",
    placements: "In-feed",
    devices: "Mobile",
    frequency: "Moderate; rotate creatives aggressively",
    notes: "Refresh hooks and openers; optimize for completion and shares.",
  },
  youtube: {
    objective: "Awareness / Trailer Exposure",
    optimizationGoal: "Completed views at scale",
    billing: "CPM / CPV",
    audience: "Affinity + custom intent + retargeting",
    geo: "National + priority DMAs",
    placements: "Skippable in-stream + Shorts (optional)",
    devices: "CTV + mobile + desktop",
    frequency: "Balanced; cap if needed",
    notes: "Use sequential messaging and length-based creative routing.",
  },
  googlesearch: {
    objective: "Demand Capture",
    optimizationGoal: "High-intent actions (tickets / info)",
    billing: "CPC",
    audience: "Query intent segments",
    geo: "Priority DMAs / radius around theaters",
    placements: "Search",
    devices: "Cross-device",
    frequency: "N/A (query-driven)",
    notes: "Control with match types + negatives; optimize landing alignment.",
  },
  reddit: {
    objective: "Awareness / Consideration",
    optimizationGoal: "Contextual attention + efficient click intent",
    billing: "Impressions (CPM) / Clicks (CPC)",
    audience: "Interest + subreddit/context targeting + retargeting",
    geo: "Priority markets / DMAs",
    placements: "Feed + conversation placements (as available)",
    devices: "Cross-device",
    frequency: "Balanced; refresh creatives frequently",
    notes:
      "Reddit is community-contextual. Match creative language to subculture norms, test headlines heavily, and avoid overly polished ad tone.",
  },
  amazon: {
    objective: "Consideration / Action (Commerce)",
    optimizationGoal: "Product/offer relevance + click efficiency (CTR, CPC)",
    billing: "Clicks (CPC) / Impressions (CPM) depending on format",
    audience: "In-market + shopping signals + retargeting",
    geo: "National + priority markets (as applicable)",
    placements: "Amazon placements (format-dependent)",
    devices: "Cross-device",
    frequency: "Managed via placement mix + creative rotation",
    notes:
      "Amazon is commerce-native. Ensure landing/product page relevance and test creative clarity. Amazon conversions, sales and ROAS are not totalled.",
  },
  ctv: {
    objective: "Awareness / Consideration",
    optimizationGoal: "Completion rate + incremental lift",
    billing: "Impressions (CPM)",
    audience: "HH targeting + contextual + retargeting overlays",
    geo: "Priority markets",
    placements: "CTV apps / FAST / streaming inventory",
    devices: "CTV",
    frequency: "Managed to avoid waste",
    notes: "CTV is non-clickable; evaluate via reach, frequency, completion, and lift.",
  },
  video: {
    objective: "Awareness / Consideration",
    optimizationGoal: "Completion + attentive reach",
    billing: "Impressions (CPM)",
    audience: "Contextual + retargeting",
    geo: "Priority markets",
    placements: "OLV / premium video",
    devices: "Cross-device",
    frequency: "Balanced",
    notes: "Focus on completion and message retention rather than clicks.",
  },
  display: {
    objective: "Reinforcement",
    optimizationGoal: "Efficient reach + retargeting recall",
    billing: "Impressions (CPM)",
    audience: "Retargeting + lookalikes",
    geo: "Priority DMAs",
    placements: "Display networks / premium supply",
    devices: "Cross-device",
    frequency: "Managed",
    notes: "Use for continuity and reminder density; clicks are secondary.",
  },
  mobile: {
    objective: "Reinforcement / Action",
    optimizationGoal: "Efficient traffic + retargeting",
    billing: "Impressions (CPM)",
    audience: "Retargeting + lookalikes",
    geo: "Radius / geo-fence around theaters",
    placements: "In-app mobile inventory",
    devices: "Mobile",
    frequency: "Managed",
    notes: "Geo + dayparting can materially lift on-the-ground outcomes.",
  },
  dooh: {
    objective: "Awareness (Real-world)",
    optimizationGoal: "Geo lift + venue reach",
    billing: "Impressions (CPM)",
    audience: "Venue / geo-driven (not behavioral)",
    geo: "Theater corridors + high-traffic venues",
    placements: "OOH screens / networks",
    devices: "N/A",
    frequency: "Balanced via rotation / dayparting",
    notes: "DOOH is not click media; measure via geo lift / reach / frequency.",
  },
  audio: {
    objective: "Awareness / Recall",
    optimizationGoal: "Reach + frequency + lift",
    billing: "Impressions (CPM)",
    audience: "Context + geo + demo",
    geo: "Priority markets",
    placements: "Streaming audio inventory",
    devices: "Mobile + smart speakers",
    frequency: "Managed to avoid fatigue",
    notes: "Audio is repetition + recall; evaluate via reach/frequency and lift.",
  },
  ingame: {
    objective: "Awareness / Engagement",
    optimizationGoal: "High attention exposure",
    billing: "Impressions (CPM)",
    audience: "Game context + demo",
    geo: "Priority markets",
    placements: "In-game video/display",
    devices: "Console + mobile",
    frequency: "Balanced",
    notes: "Optimize for attention and completion where available.",
  },
  email: {
    objective: "Action / Retention",
    optimizationGoal: "Opens + clicks + conversions",
    billing: "N/A",
    audience: "Owned list / segments",
    geo: "N/A",
    placements: "Email",
    devices: "Mobile + desktop",
    frequency: "Cadence-based",
    notes: "Email success is cadence, segmentation, and offer clarity.",
  },
  x: {
    objective: "Awareness / Engagement",
    optimizationGoal: "Engagement density",
    billing: "Impressions (CPM)",
    audience: "Interest + follower lookalikes",
    geo: "Priority markets",
    placements: "Timeline",
    devices: "Cross-device",
    frequency: "Balanced",
    notes: "Keep creative concise; prioritize scroll-stopping copy clarity.",
  },
  truthsocial: {
    objective: "Awareness / Engagement",
    optimizationGoal: "Efficient reach + engagement",
    billing: "Impressions (CPM)",
    audience: "Interest + broad",
    geo: "Priority markets",
    placements: "Feed",
    devices: "Mobile",
    frequency: "Balanced",
    notes: "Test message frames; maintain brand safety controls.",
  },
  rumble: {
    objective: "Awareness",
    optimizationGoal: "Completion + attentive reach",
    billing: "Impressions (CPM)",
    audience: "Contextual + broad",
    geo: "Priority markets",
    placements: "Video placements",
    devices: "Cross-device",
    frequency: "Balanced",
    notes: "Focus on completion, viewability, and message retention.",
  },
};

// -----------------------------
// ✅ Range param helper (robust to backend naming expectations)
// -----------------------------
const appendRangeParams = (params, { mode, datePreset, startDate, endDate } = {}) => {
  if (isFlightWindowSelection({ mode, datePreset, startDate, endDate })) {
    params.append("datePreset", "flight");
    params.append("date_preset", "flight");
    return params;
  }

  if (startDate && endDate) {
    params.append("startDate", String(startDate));
    params.append("endDate", String(endDate));
    params.append("start_date", String(startDate));
    params.append("end_date", String(endDate));
    return params;
  }

  const preset = String(datePreset || "last_7d");
  params.append("datePreset", preset);
  params.append("date_preset", preset);
  return params;
};

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

const isFlightWindowSelection = ({ mode, datePreset, startDate, endDate } = {}) => {
  if (normalize(mode) === "flight" || normalize(datePreset) === "flight") return true;
  const start = String(startDate || "").trim();
  const end = String(endDate || "").trim();
  return !normalize(datePreset) && DATE_ONLY_RE.test(start) && DATE_ONLY_RE.test(end);
};

const resolveRecommendationDateScope = ({ mode, datePreset, startDate, endDate } = {}) => {
  if (isFlightWindowSelection({ mode, datePreset, startDate, endDate })) {
    return "flight";
  }

  const start = String(startDate || "").trim();
  const end = String(endDate || "").trim();
  if (DATE_ONLY_RE.test(start) && DATE_ONLY_RE.test(end)) {
    return `range:${start}:${end}`;
  }

  return normalize(datePreset || "last_7d");
};

const buildOptimusScopeKey = ({ advertiserId, campaignId, platform, range } = {}) =>
  JSON.stringify({
    advertiserId: String(advertiserId || ""),
    campaignId: String(campaignId || ""),
    platform: normalize(platform || "all"),
    rangeMode: String(range?.mode || ""),
    datePreset: resolveRecommendationDateScope({
      datePreset: range?.datePreset,
      startDate: range?.startDate,
      endDate: range?.endDate,
    }),
    startDate: String(range?.startDate || ""),
    endDate: String(range?.endDate || ""),
  });

const buildRecommendationWindowLabel = ({ mode, datePreset, startDate, endDate } = {}) => {
  if (isFlightWindowSelection({ mode, datePreset, startDate, endDate })) {
    return "Flight";
  }

  if (startDate && endDate) {
    return formatFlightRange(startDate, endDate) || `${startDate} to ${endDate}`;
  }
  return presetLabel(datePreset || "last_7d");
};




const formatRecommendationGeneratedAt = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const sourceLabelForRecommendationDoc = (doc) => {
  const rawSource = normalize(doc?.source || "");
  const generatedAt = formatRecommendationGeneratedAt(doc?.generatedAt || doc?.createdAt);
  const base =
    rawSource === "fallback" || rawSource === "heuristic"
      ? "Saved fallback analysis"
      : rawSource === "strategist" || rawSource === "strategist_synthesis"
      ? "Saved strategist analysis"
      : rawSource === "ai" || rawSource === "rules"
      ? "Saved rule analysis"
      : "Saved analysis";
  return generatedAt ? `${base} • ${generatedAt}` : base;
};

const OPTIMUS_METRIC_SIGNATURE_FIELDS = [
  "spend",
  "impressions",
  "clicks",
  "ctr",
  "cpm",
  "videoViews",
  "engagements",
  "completions",
  "completionRate",
  "ticketSales",
];

const isPlainObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value);

const normalizeOptimusMetricSignatureValue = (value) => {
  if (value == null || value === "") return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value).trim().toLowerCase() || null;
  return Number(numeric.toFixed(4));
};

const buildOptimusMetricSignature = (metrics = {}) => {
  if (!isPlainObject(metrics)) return "";
  const hasSignatureField = OPTIMUS_METRIC_SIGNATURE_FIELDS.some((field) =>
    Object.prototype.hasOwnProperty.call(metrics, field)
  );
  if (!hasSignatureField) return "";
  const normalized = {};
  OPTIMUS_METRIC_SIGNATURE_FIELDS.forEach((field) => {
    normalized[field] = normalizeOptimusMetricSignatureValue(metrics?.[field]);
  });
  return JSON.stringify(normalized);
};

const CLIENT_FORBIDDEN_OPTIMUS_TEXT_RE = new RegExp(
  [
    "internal",
    "reconciliation",
    "reconcile",
    "vendor\\s+cost",
    "provider\\s+cost",
    "cost\\s+basis",
    "raw\\s+(?:spend|cpm|cost|media)",
    "ingestion\\s+cpm",
    "platform\\s+cost",
    "mar(?:gin)",
    "mark(?:up)",
    "pro(?:fit)",
    /* Client financial-basis contract: NET_WORKING_MEDIA is internal-only and
       the words "net" and "gross" never render on a client surface. Word-
       bounded so "network"/"internet" survive. */
    "\\bnet\\b",
    "\\bgross\\b",
    "net[\\s_-]?working",
    "working[\\s_-]?media",
    "gross[\\s_-]?client",
    "data[_\\s-]?source",
    "live_meta",
    "providerstates?",
    "source\\s+diagnostics?",
    "billing\\s+diagnostics?",
    "contributor\\s+audit",
  ].join("|"),
  "i"
);

const scrubClientOptimusText = (value, fallback = "") => {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if (!text) return fallback;
  if (CLIENT_FORBIDDEN_OPTIMUS_TEXT_RE.test(text)) return fallback;
  return text
    .replace(/\bsupply sources\b/gi, "inventory pools")
    .trim();
};

const sanitizeClientRecommendation = (platform, rec, idx = 0) => {
  if (!rec) return null;
  const title = scrubClientOptimusText(rec.title, "");
  const summary = scrubClientOptimusText(rec.summary, "");
  if (!title || !summary) return null;
  return {
    id: rec.id || `client-rec-${normalize(platform || "all")}-${idx}`,
    category: scrubClientOptimusText(rec.category, "Optimization") || "Optimization",
    confidence: typeof rec.confidence === "number" ? rec.confidence : null,
    title,
    summary,
    evidence: scrubClientOptimusText(rec.evidence || rec.evidenceLine || rec.basis?.evidenceLine || "", ""),
    observation: scrubClientOptimusText(rec.observation, "") || null,
    reason: scrubClientOptimusText(rec.reason, "") || null,
    action: scrubClientOptimusText(rec.action, "") || null,
    sourceType: rec.sourceType || null,
    sourceLabel: rec.sourceLabel || null,
    generatedAt: rec.generatedAt || null,
    dataWindow: rec.dataWindow || null,
  };
};

const sanitizeClientRecommendationsStrict = (platform, recs) =>
  (Array.isArray(recs) ? recs : [])
    .map((rec, idx) => sanitizeClientRecommendation(platform, rec, idx))
    .filter(Boolean);

const recommendationDocMatchesCurrentMetrics = (doc, currentMetrics) => {
  const currentSignature = buildOptimusMetricSignature(currentMetrics);
  if (!currentSignature) return false;

  const savedFreshness =
    doc?.canonicalFreshness ||
    doc?.freshness ||
    doc?.kpis?._canonicalFreshness ||
    doc?.kpis?.freshness ||
    null;
  const savedSignature =
    savedFreshness?.metricSignature ||
    doc?.metricSignature ||
    doc?.metric_signature ||
    doc?.kpis?.metricSignature ||
    doc?.kpis?.metric_signature ||
    buildOptimusMetricSignature(doc?.kpis || {});

  return Boolean(savedSignature && savedSignature === currentSignature);
};

const recommendationDocMatchesScope = (
  doc,
  { advertiserId, campaignId, platform, datePreset, startDate, endDate } = {}
) => {
  if (!doc) return false;

  const expectedAdvertiser = String(advertiserId || "").trim();
  const actualAdvertiser = String(doc.advertiserId ?? doc.advertiser_id ?? "").trim();
  if (expectedAdvertiser && actualAdvertiser && expectedAdvertiser !== actualAdvertiser) return false;

  const expectedCampaign = String(campaignId || "").trim();
  const actualCampaign = String(doc.campaignId ?? doc.campaign_id ?? "").trim();
  if (expectedCampaign !== actualCampaign) return false;

  if (normalize(doc.platform) !== normalize(platform || "all")) return false;

  const expectedDateScope = resolveRecommendationDateScope({ datePreset, startDate, endDate });
  const actualDateScope = normalize(doc.datePreset ?? doc.date_preset ?? "");
  return actualDateScope === expectedDateScope;
};

const buildRangeObjectFromDates = (startValue, endValue) => {
  const start = startValue ? parseFlightDateSafe(startValue) : null;
  const end = endValue ? parseFlightDateSafe(endValue) : null;

  if (!start || Number.isNaN(start.getTime())) return null;
  if (!end || Number.isNaN(end.getTime())) return null;

  const toYMD = (d) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  return {
    start: toYMD(start),
    end: toYMD(end),
    startMs: start.getTime(),
    endMs: end.getTime(),
  };
};

// -----------------------------
// ✅ Meta breakdown normalization (FB/IG/AN/Messenger accuracy)
// -----------------------------
const normalizePublisherPlatform = (v) => {
  const s = normalize(v);
  if (!s) return "";

  if (s === "ig") return "instagram";
  if (s === "insta") return "instagram";
  if (s === "fb") return "facebook";
  if (s === "face book") return "facebook";

  if (s.includes("audience_network")) return "audience_network";
  if (s.includes("messenger")) return "messenger";
  if (s.includes("instagram")) return "instagram";
  if (s.includes("facebook")) return "facebook";

  return s;
};

/* -----------------------------------------------------------------------
 * ✅ Value formatters — MODULE scope, deliberately.
 *
 * These are pure: they close over nothing in the component. They used to be
 * declared as `const`s roughly 300 lines into the component body, which put
 * every one of them in the temporal dead zone for the whole first half of
 * the render pass. A `useMemo` factory runs synchronously during that pass,
 * so a hook declared ABOVE the formatters that passed one along (the blended
 * eCPM subline passes `formatCurrency` as `formatMoney`) threw
 * `ReferenceError: Cannot access 'formatCurrency' before initialization`
 * and took the whole /optimus route down through the route error boundary.
 *
 * Keeping them at module scope makes that class of ordering bug
 * unrepresentable, and stops re-allocating the closures on every render.
 * ----------------------------------------------------------------------- */
/* One money shape for every Optimus surface, from the ONE governed formatter
   (utils/optimusMoneyFormat.js), byte-identical in Cortex and Quantum. */
const formatCurrency = (value) => formatOptimusMoney(value);
const formatPercentage = (value) => formatOptimusPercent(value);
/*
 * Impression mix, stated deterministically.
 *
 * This used to interpolate both shares unconditionally, so a channel with
 * neither reading rendered "\u2014% vs \u2014% planned" — truthful, but it reads
 * as a formatting defect rather than as missing data. Each of the four states
 * now has one fixed phrasing, and a missing reading is NAMED rather than
 * punctuated:
 *
 *   both        \u2192 "1.7% actual vs 38% planned"
 *   no actual   \u2192 "Not measured \u00b7 14% planned"
 *   no plan     \u2192 "1.7% actual \u00b7 Plan unavailable"
 *   neither     \u2192 "Not measured"
 *
 * The underlying values are untouched: nothing is defaulted to zero and an
 * absent share is still absent.
 */
const formatImpressionMix = (actualShare, plannedShare) => {
  const hasActual = typeof actualShare === "number" && Number.isFinite(actualShare);
  const hasPlanned = typeof plannedShare === "number" && Number.isFinite(plannedShare);
  if (hasActual && hasPlanned) return `${actualShare}% actual vs ${plannedShare}% planned`;
  if (!hasActual && hasPlanned) return `Not measured \u00b7 ${plannedShare}% planned`;
  if (hasActual && !hasPlanned) return `${actualShare}% actual \u00b7 Plan unavailable`;
  return "Not measured";
};

const formatInteger = (value) => formatOptimusCount(value);

const Optimus = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    selectedAdvertiserId,
    selectedAdvertiserName,
    selectedAdvertiserVertical,
  } = useAdvertiser();

  const [platformTabs, setPlatformTabs] = useState([]);
  /*
   * Where the current tab strip came from: "campaign" means it IS the selected
   * campaign's approved planner platform set and is therefore already exact;
   * "advertiser" means the aggregate inventory, which still gets the
   * delivery/plan participation filter below.
   */
  const [platformTabsScope, setPlatformTabsScope] = useState("advertiser");
  const [selectedPlatformTab, setSelectedPlatformTab] = useState("all");

  const [selectedCampaignId, setSelectedCampaignId] = useState("auto");

  // ✅ Flight default (client aligned with superadmin)
  const [overviewRangeKey, setOverviewRangeKey] = useState("flight");
  const [overviewSourceLabel, setOverviewSourceLabel] = useState("");

  const [kpis, setKpis] = useState({
    totalSpend: null,
    // Wave 3: server-declared billed figures (billedSpend/clientFacingSpend)
    // carried off the served payloads. These — never totalSpend — are the
    // only spend-display fallbacks.
    billedSpend: null,
    clientFacingSpend: null,
    impressions: null,
    clicks: null,
    blendedCtr: null,
    blendedCpm: null,
    videoViews: null,
    qualifiedVideoViews: null,
    qualifiedVideoExplainer: null,
    engagements: null,
    engagementRate: null,
    videoViewRate: null,
    completions: null,
    completionRate: null,
    ticketSales: null,
    costPerQualifiedVideoView: null,
    qualifiedVideoSpendBasisLabel: null,
    qualifiedVideoComplete: null,
    canonicalReportingComplete: null,

    activePlatformsLabel: "All",
    totalSpendAll: null,
    spendSharePct: null,
    byPlatformMetrics: {},
    byPlatformAttribution: {},
    byPlatformDataSources: {},
  });

  const [recommendations, setRecommendations] = useState([]);
  const [recsSourceLabel, setRecsSourceLabel] = useState("");
  const [, setRecsLoading] = useState(false);
  // Backend-owned strategy-attainment/grading semantics. The portal renders
  // this contract verbatim and never manufactures a fallback grade.
  const [optimusGrading, setOptimusGrading] = useState(null);

  const [apiError, setApiError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [overviewLoading, setOverviewLoading] = useState(false);

  const currentOptimusScopeRef = useRef("");
  const optimusRequestSeqRef = useRef(0);
  const overviewRequestStateRef = useRef({ inFlightKey: "", settledKey: "" });

  /*
   * Campaign inventory is derived, never stored.
   *
   * The raw sources are held separately and merged in one memo, so the governed
   * plan rows (which load in their own effect) can never arrive too late to
   * produce an option — the previous code snapshotted the merged list inside the
   * live-campaign fetch, where planCampaigns was not yet in scope.
   */
  const [liveCampaignRows, setLiveCampaignRows] = useState([]);
  const [importedReportingRows, setImportedReportingRows] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [campaignsError, setCampaignsError] = useState(false);
  const [planCampaigns, setPlanCampaigns] = useState([]);
  const [, setPlanCampaignsError] = useState("");
  /*
   * Governed plan rows are what MAKE a governed campaign selectable, so "the
   * campaign inventory is still loading" is not answered by campaignsLoading
   * alone. Without this the tab strip rendered once from advertiser-wide
   * evidence before auto-selection landed — a real, if brief, frame of another
   * campaign's channels under this campaign's name.
   */
  const [planCampaignsLoading, setPlanCampaignsLoading] = useState(false);

  /*
   * The approved-campaign contract for whatever campaign is selected.
   *
   * One record, replaced wholesale on every campaign change. Nothing merges
   * into it and nothing survives a switch, so the flight, the platform tabs and
   * the run inputs can never be a blend of two campaigns.
   */
  const [campaignContractState, setCampaignContractState] = useState({
    state: OPTIMUS_CONTRACT_STATE.IDLE,
    contract: null,
    diagnostic: null,
    campaignId: null,
  });

  const clearGovernedAdvertiserScope = useCallback(() => {
    currentOptimusScopeRef.current = "";
    optimusRequestSeqRef.current += 1;
    overviewRequestStateRef.current = { inFlightKey: "", settledKey: "" };
    setSelectedCampaignId("auto");
    setLiveCampaignRows([]);
    setImportedReportingRows([]);
    setCampaignsLoading(false);
    setCampaignsError(false);
    setPlanCampaigns([]);
    setPlanCampaignsError("");
    setPlanCampaignsLoading(false);
    setCampaignContractState({
      state: OPTIMUS_CONTRACT_STATE.IDLE,
      contract: null,
      diagnostic: null,
      campaignId: null,
    });
    setPlatformTabs([]);
    setSelectedPlatformTab("all");
    setKpis({
      totalSpend: null,
      billedSpend: null,
      clientFacingSpend: null,
      impressions: null,
      clicks: null,
      blendedCtr: null,
      blendedCpm: null,
      videoViews: null,
      qualifiedVideoViews: null,
      qualifiedVideoExplainer: null,
      engagements: null,
      engagementRate: null,
      videoViewRate: null,
      completions: null,
      completionRate: null,
      ticketSales: null,
      activePlatformsLabel: "All",
      totalSpendAll: null,
      spendSharePct: null,
      byPlatformMetrics: {},
      byPlatformAttribution: {},
      byPlatformDataSources: {},
    });
    setRecommendations([]);
    setRecsSourceLabel("");
    setRecsLoading(false);
    setOptimusGrading(null);
    setApiError("");
    setOverviewSourceLabel("");
    setIsLoading(false);
    setOverviewLoading(false);
  }, []);

  const {
    visibleAdvertisers,
    currentAdvertiser,
    loading: advertiserCatalogLoading,
    selectorDisabled,
    selectorEmpty,
    modalOpen: advertiserSelectorOpen,
    openSelector: openAdvertiserSelector,
    closeSelector: closeAdvertiserSelector,
    handleAdvertiserSelect,
  } = useGovernedAdvertiserCatalog({
    apiBase: API_BASE_URL,
    role: "client",
    onAdvertiserChange: clearGovernedAdvertiserScope,
  });

  const governedAdvertiserName =
    currentAdvertiser?.name || selectedAdvertiserName || "";

  // ✅ CLIENT token (tries multiple keys)
  const token =
    localStorage.getItem("clientAuthToken") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("token") ||
    "";

  const authHeaders = useMemo(() => {
    const h = { "Content-Type": "application/json" };
    if (token) h.Authorization = `Bearer ${token}`;
    return h;
  }, [token]);

  const currentPreset = useMemo(() => RANGE_PRESETS[overviewRangeKey] || RANGE_PRESETS["7d"], [overviewRangeKey]);
  const currentRangeLabel = useMemo(() => currentPreset.label, [currentPreset]);
  const hasPerformanceOverviewStats = useMemo(
    () => hasOverviewKpiTotals(metricsPayloadFromOverviewKpis(kpis)),
    [kpis]
  );
  const overviewStatsBusy = Boolean(overviewLoading || isLoading);
  const showInitialOverviewLoading = Boolean(
    selectedAdvertiserId && overviewStatsBusy && !hasPerformanceOverviewStats
  );
  const showOverviewRefreshIndicator = Boolean(
    selectedAdvertiserId && overviewStatsBusy && hasPerformanceOverviewStats
  );
  const showOverviewNoData = Boolean(
    selectedAdvertiserId && !overviewStatsBusy && !hasPerformanceOverviewStats
  );

  // -----------------------------
  // Helpers: advertiser record, Meta config, flight range, planned budget
  // -----------------------------
  const getAdvertiserRecord = useMemo(() => {
    if (!selectedAdvertiserId || !currentAdvertiser) return null;
    return currentAdvertiser.raw || currentAdvertiser;
  }, [currentAdvertiser, selectedAdvertiserId]);

  const resolvedAdvertiserVertical = useMemo(
    () =>
      normalizeAdvertiserVertical(
        getAdvertiserRecord?.vertical ||
          selectedAdvertiserVertical ||
          (typeof window !== "undefined"
            ? window.localStorage.getItem("selectedAdvertiserVertical")
            : "")
      ),
    [getAdvertiserRecord, selectedAdvertiserVertical]
  );

  const hasMetaConfigured = useMemo(() => {
    const a = getAdvertiserRecord;
    if (!a) return false;

    const candidates = [
      a.meta_ad_account_id,
      a.metaAdAccountId,
      a.meta_ad_account,
      a.metaAdAccount,
      a.ad_account_id,
      a.adAccountId,
      a.facebook_ad_account_id,
      a.facebookAdAccountId,
    ];

    return candidates.some((v) => String(v || "").trim().length >= 3);
  }, [getAdvertiserRecord]);

  const advertiserFlightRange = useMemo(() => {
    const a = getAdvertiserRecord;
    if (!a) return null;

    const start =
      a.flightStart ||
      a.flight_start ||
      a.startDate ||
      a.start_date ||
      a.flight_start_date ||
      a.flightStartDate ||
      null;

    const end =
      a.flightEnd ||
      a.flight_end ||
      a.endDate ||
      a.end_date ||
      a.flight_end_date ||
      a.flightEndDate ||
      null;

    return buildRangeObjectFromDates(start, end);
  }, [getAdvertiserRecord]);

  const resetMetricsAndRecs = useCallback(() => {
    setOptimusGrading(null);
    setKpis({
      totalSpend: null,
      billedSpend: null,
      clientFacingSpend: null,
      impressions: null,
      clicks: null,
      blendedCtr: null,
      blendedCpm: null,
      videoViews: null,
      qualifiedVideoViews: null,
      qualifiedVideoExplainer: null,
      engagements: null,
      engagementRate: null,
      videoViewRate: null,
      completions: null,
      completionRate: null,
      ticketSales: null,
      activePlatformsLabel: platformLabel(selectedPlatformTab || "all"),
      totalSpendAll: null,
      spendSharePct: null,
      byPlatformMetrics: {},
      byPlatformAttribution: {},
      byPlatformDataSources: {},
    });
    setOverviewSourceLabel("");
    setRecommendations([]);
    setRecsSourceLabel("");
    setRecsLoading(false);
    setApiError("");
  }, [selectedPlatformTab]);

  const hasApprovedPlanCampaigns = useMemo(
    () => (Array.isArray(planCampaigns) ? planCampaigns.some(isApprovedPlanCampaign) : false),
    [planCampaigns]
  );

  const plannedPlatformTabs = useMemo(() => {
    const sourceCampaigns = Array.isArray(planCampaigns)
      ? hasApprovedPlanCampaigns
        ? planCampaigns.filter(isApprovedPlanCampaign)
        : planCampaigns
      : [];

    const channelSet = new Set();

    for (const campaign of sourceCampaigns) {
      for (const raw of collectCampaignPlatformHints(campaign)) {
        for (const canon of expandOptimizerPlatformIds(raw)) {
          if (canon && !isExcludedPlatformId(canon)) channelSet.add(canon);
        }
      }

      const pricingSummary = Array.isArray(campaign?.programmatic_pricing_summary)
        ? campaign.programmatic_pricing_summary
        : Array.isArray(campaign?.programmaticPricingSummary)
        ? campaign.programmaticPricingSummary
        : [];

      for (const item of pricingSummary) {
        const lineItemKey = item?.lineItemKey || item?.line_item_key || item?.channel || null;
        for (const canon of expandOptimizerPlatformIds(lineItemKey)) {
          if (canon && !isExcludedPlatformId(canon)) channelSet.add(canon);
        }
      }
    }

    return Array.from(channelSet).map((id) => ({ id, label: platformLabel(id) }));
  }, [hasApprovedPlanCampaigns, planCampaigns]);

  const plannedPlatformIdSet = useMemo(
    () => new Set((plannedPlatformTabs || []).map((tab) => normalize(tab?.id)).filter(Boolean)),
    [plannedPlatformTabs]
  );

  const visiblePlatformTabs = useMemo(() => {
    if (!Array.isArray(platformTabs) || platformTabs.length === 0) return [];

    /*
     * A campaign-scoped strip is already exactly the approved planner
     * selection. It is rendered verbatim: a selected platform with no delivery
     * yet is still a selected platform, and hiding it would misreport the plan
     * as smaller than it is.
     */
    if (platformTabsScope === "campaign") return dedupeTabs(platformTabs, true);

    const selectedId = normalize(selectedPlatformTab || "all");
    const byPlatformMetrics =
      kpis.byPlatformMetrics && typeof kpis.byPlatformMetrics === "object"
        ? kpis.byPlatformMetrics
        : {};

    return dedupeTabs(
      (platformTabs || []).filter((tab) => {
        const id = normalize(tab?.id);
        if (!id) return false;
        if (id === "all" || id === selectedId) return true;
        if (!isContextConditionalPlatformId(id)) return true;
        return plannedPlatformIdSet.has(id) || hasMeaningfulPlatformDelivery(byPlatformMetrics[id]);
      })
    );
  }, [
    kpis.byPlatformMetrics,
    plannedPlatformIdSet,
    platformTabs,
    platformTabsScope,
    selectedPlatformTab,
  ]);

  // -----------------------------
  // Platform tabs loader + force Meta sub-tabs when Meta configured
  // -----------------------------
  /*
   * ADVERTISER-AGGREGATE tabs only.
   *
   * This assembles the advertiser's inventory from connected providers, stored
   * snapshot channels and the advertiser's planned channels — a reasonable
   * answer to "what does this advertiser run", and the wrong answer to "what
   * did THIS campaign's approved plan select". Campaign mode never reaches it;
   * see the tab-strip owner below.
   */
  const loadPlatformTabs = useCallback(
    async (advertiserId, signal) => {
      if (!advertiserId) {
        setPlatformTabs([]);
        setSelectedPlatformTab("all");
        return;
      }

      try {
        const params = new URLSearchParams({
          advertiserId: String(advertiserId),
          datePreset: "last_30d",
        });

        const res = await fetch(apiUrl(`/api/optimus/platforms?${params.toString()}`), {
          method: "GET",
          headers: authHeaders,
          signal,
        });

        let rawTabs = [];
        let metaProviderState = null;
        let canonicalPlatformKeys = [];
        if (res?.ok) {
          const json = await res.json().catch(() => ({}));
          rawTabs = Array.isArray(json.tabs) ? json.tabs : [];
          metaProviderState = json?.providerStates?.meta || null;
          canonicalPlatformKeys = Array.isArray(json?.canonicalPlatformKeys)
            ? json.canonicalPlatformKeys
            : [];
        }

        // Force only Meta surfaces that are generally available from account connection.
        // Context-specific surfaces such as Audience Network/Messenger must come from
        // explicit plan config or observed current delivery before they render.
        const forced = [];
        const shouldForceMetaTabs = metaProviderState
          ? Boolean(metaProviderState.reportingAvailable || metaProviderState.connected)
          : hasMetaConfigured;
        if (shouldForceMetaTabs) {
          forced.push({ id: "facebook", label: "Facebook" });
          forced.push({ id: "instagram", label: "Instagram" });
        }

        // Optional: force Reddit/Amazon tabs if advertiser has links configured
        const adv = getAdvertiserRecord;
        const hasRedditLink = !!String(adv?.reddit || "").trim();
        const hasAmazonLink = !!String(adv?.amazon || "").trim();

        if (hasRedditLink) forced.push({ id: "reddit", label: "Reddit" });
        if (hasAmazonLink) forced.push({ id: "amazon", label: "Amazon" });

        const selectedId = normalize(selectedPlatformTab || "all");
        let tabs = buildOptimizerPlatformTabs({
          tabs: rawTabs,
          plannedTabs: plannedPlatformTabs,
          forcedTabs: forced,
          extraPlatformKeys: canonicalPlatformKeys,
          selectedId,
          labelForPlatform: platformLabel,
          isExcludedPlatformId,
        });
        if (selectedId && selectedId !== "all" && !tabs.some((tab) => tab.id === selectedId)) {
          tabs = dedupeTabs([...tabs, { id: selectedId, label: platformLabel(selectedId) }]);
        }

        if (signal?.aborted) return;
        setPlatformTabs(tabs);
        setPlatformTabsScope("advertiser");

        const prev = normalize(selectedPlatformTab || "all");
        if (tabs.some((t) => t.id === prev)) setSelectedPlatformTab(prev);
        else setSelectedPlatformTab("all");
      } catch (e) {
        if (e?.name === "AbortError") return;

        const forced = [];
        if (hasMetaConfigured) {
          forced.push(
            { id: "all", label: "All Platforms" },
            { id: "facebook", label: "Facebook" },
            { id: "instagram", label: "Instagram" }
          );
        }
        const selectedId = normalize(selectedPlatformTab || "all");
        let tabs = buildOptimizerPlatformTabs({
          plannedTabs: plannedPlatformTabs,
          forcedTabs: forced,
          selectedId,
          labelForPlatform: platformLabel,
          isExcludedPlatformId,
        });
        if (selectedId && selectedId !== "all" && !tabs.some((tab) => tab.id === selectedId)) {
          tabs = dedupeTabs([...tabs, { id: selectedId, label: platformLabel(selectedId) }]);
        }
        if (signal?.aborted) return;
        setPlatformTabs(tabs);
        setPlatformTabsScope("advertiser");
        if (tabs.some((tab) => tab.id === selectedId)) setSelectedPlatformTab(selectedId);
        else setSelectedPlatformTab("all");
      }
    },
    [authHeaders, selectedPlatformTab, hasMetaConfigured, getAdvertiserRecord, plannedPlatformTabs]
  );

  // -----------------------------
  // Load campaigns (best-effort)
  // -----------------------------
  useEffect(() => {
    const controller = new AbortController();

    const loadCampaigns = async () => {
      if (!selectedAdvertiserId) {
        setLiveCampaignRows([]);
        setImportedReportingRows([]);
        setCampaignsError(false);
        setSelectedCampaignId("auto");
        return;
      }

      setCampaignsLoading(true);
      setCampaignsError(false);
      setLiveCampaignRows([]);
      setImportedReportingRows([]);
      // Changing advertiser always returns the selector to governed
      // auto-selection; a campaign id from the previous advertiser must never
      // survive the switch.
      setSelectedCampaignId("auto");

      try {
        const params = new URLSearchParams({ advertiserId: String(selectedAdvertiserId) });

        const res = await fetch(apiUrl(`/api/meta/campaigns?${params.toString()}`), {
          method: "GET",
          headers: authHeaders,
          signal: controller.signal,
        });

        /*
         * A failed ad-platform campaign lookup is an ERROR, not an empty
         * advertiser. Recording it separately keeps the selector from
         * presenting a lookup failure as "this advertiser has no campaigns"
         * and quietly falling back to advertiser totals.
         */
        if (!res.ok) setCampaignsError(true);

        const json = res.ok ? await res.json().catch(() => ({})) : {};
        const data = Array.isArray(json.data) ? json.data : [];

        let importedReportingCampaigns = [];
        if (!data.length) {
          const contextRes = await fetch(
            apiUrl(`${IMPORTED_REPORTING_CONTEXT_PATH}?${params.toString()}`),
            {
              method: "GET",
              headers: authHeaders,
              signal: controller.signal,
            }
          );
          if (contextRes.ok) {
            const contextJson = await contextRes.json().catch(() => ({}));
            importedReportingCampaigns = extractImportedReportingCampaigns(contextJson);
          }
        }

        setLiveCampaignRows(data);
        setImportedReportingRows(importedReportingCampaigns);
      } catch (err) {
        if (err?.name === "AbortError") return;
        setLiveCampaignRows([]);
        setImportedReportingRows([]);
        setCampaignsError(true);
      } finally {
        setCampaignsLoading(false);
      }
    };

    loadCampaigns();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAdvertiserId, authHeaders]);

  /*
   * The one campaign inventory for this page. Ad-platform campaigns, imported
   * reporting campaigns and the advertiser's governed approved plans all resolve
   * through the canonical identity resolver, so a campaign that exists in more
   * than one of them is still exactly one option.
   */
  const campaigns = useMemo(
    () =>
      buildOptimusCampaignOptions({
        liveCampaigns: liveCampaignRows,
        importedReportingCampaigns: importedReportingRows,
        mediaCampaigns: planCampaigns,
      }),
    [liveCampaignRows, importedReportingRows, planCampaigns]
  );

  const hasCampaignOptions = campaigns.length > 0;

  /*
   * A campaign named by the route or by page state.
   *
   * This is the ONLY input allowed to override governed auto-selection, and
   * only when it names one of this advertiser's own eligible campaigns.
   * Anything else — a stale id from another advertiser, a value a browser
   * restored into the <select> across a reload — is discarded below rather than
   * addressed.
   */
  const requestedRouteCampaignId = useMemo(() => {
    const fromQuery = new URLSearchParams(location.search || "").get("campaignId");
    const fromState = location.state?.campaignId;
    const raw = String(fromQuery ?? fromState ?? "").trim();
    if (!raw || raw === "auto" || raw === "all") return null;
    return raw;
  }, [location.search, location.state]);

  /*
   * Governed auto-selection. The rule lives in resolveOptimusSelectedCampaign
   * and always decides when the advertiser has an eligible campaign —
   * advertiser totals is a scope the operator chooses, never the resting state
   * of a page opened to optimize a campaign.
   */
  const autoCampaignResolution = useMemo(
    () =>
      resolveOptimusSelectedCampaign({
        options: campaigns,
        requestedCampaignId: requestedRouteCampaignId,
      }),
    [campaigns, requestedRouteCampaignId]
  );
  const autoSelectedCampaign = autoCampaignResolution.campaign;

  const selectedCampaign = useMemo(() => {
    if (selectedCampaignId === "auto") return autoSelectedCampaign;
    return campaigns.find((c) => c.id === selectedCampaignId) || null;
  }, [autoSelectedCampaign, campaigns, selectedCampaignId]);

  /*
   * Governed auto-selection is PERSISTED as the campaign's own id, not left as
   * "auto". The optimizer then addresses a specific governed campaign in page
   * state and in every downstream request, and the selector names the campaign
   * rather than a mode. Ambiguous advertisers are deliberately left unresolved
   * so the operator picks.
   */
  useEffect(() => {
    if (campaignsLoading) return;
    if (selectedCampaignId !== "auto") return;
    if (!autoSelectedCampaign?.id) return;
    setSelectedCampaignId(autoSelectedCampaign.id);
  }, [autoSelectedCampaign, campaignsLoading, selectedCampaignId]);

  /*
   * A selected campaign id that no longer exists in the inventory (advertiser
   * changed, campaign withdrawn) falls back to governed auto-selection instead
   * of silently addressing a campaign this advertiser does not have.
   */
  useEffect(() => {
    if (campaignsLoading) return;
    if (selectedCampaignId === "auto" || selectedCampaignId === "") return;
    if (!campaigns.some((c) => c.id === selectedCampaignId)) {
      setSelectedCampaignId("auto");
    }
  }, [campaigns, campaignsLoading, selectedCampaignId]);

  /*
   * Advertiser totals ("") is a deliberate choice, and a deliberate choice does
   * not survive the thing it was made about.
   *
   * A browser that restores form state across a reload or a back navigation can
   * re-assert the empty option after React has mounted with governed
   * auto-selection, which is how the Optimizer came up on ADVERTISER TOTALS (NO
   * CAMPAIGN) for an advertiser with two perfectly eligible approved campaigns.
   * The aggregate scope is only honoured while it is offered — that is, while
   * there is more than one campaign to aggregate — and only for the advertiser
   * it was chosen under.
   */
  const aggregateScopeAdvertiserRef = useRef(null);
  useEffect(() => {
    if (selectedCampaignId === "") {
      aggregateScopeAdvertiserRef.current = selectedAdvertiserId || null;
      return;
    }
    aggregateScopeAdvertiserRef.current = null;
  }, [selectedAdvertiserId, selectedCampaignId]);

  useEffect(() => {
    if (campaignsLoading) return;
    if (selectedCampaignId !== "") return;
    const chosenUnder = aggregateScopeAdvertiserRef.current;
    const stillThisAdvertiser =
      chosenUnder != null && String(chosenUnder) === String(selectedAdvertiserId || "");
    if (stillThisAdvertiser && campaigns.length > 1) return;
    setSelectedCampaignId("auto");
  }, [campaigns.length, campaignsLoading, selectedAdvertiserId, selectedCampaignId]);

  const effectiveSelectedCampaignId = useMemo(() => {
    if (selectedCampaign?.reportingOnly) return null;
    if (selectedCampaignId === "auto") return selectedCampaign?.id || null;
    return selectedCampaignId || null;
  }, [selectedCampaign, selectedCampaignId]);

  useEffect(() => {
    const controller = new AbortController();

    const loadPlanCampaigns = async () => {
      if (!selectedAdvertiserId) {
        setPlanCampaigns([]);
        setPlanCampaignsError("");
        setPlanCampaignsLoading(false);
        return;
      }

      setPlanCampaignsError("");
      setPlanCampaignsLoading(true);

      try {
        const params = new URLSearchParams({
          advertiserId: String(selectedAdvertiserId),
          // Reporting/optimizer context: never receive internal supervised-flight
          // authorization/proof rows as plan candidates.
          reportingScope: "1",
        });
        const res = await fetch(apiUrl(`/api/media-campaigns?${params.toString()}`), {
          method: "GET",
          headers: authHeaders,
          signal: controller.signal,
        });

        if (!res.ok) {
          setPlanCampaigns([]);
          setPlanCampaignsError("Approved media plan data unavailable.");
          return;
        }

        const json = await res.json().catch(() => ({}));
        const data = Array.isArray(json.campaigns) ? json.campaigns : [];
        setPlanCampaigns(data);
      } catch (err) {
        if (err?.name === "AbortError") return;
        setPlanCampaigns([]);
        setPlanCampaignsError("Approved media plan data unavailable.");
      }
    };

    loadPlanCampaigns().finally(() => {
      if (!controller.signal.aborted) setPlanCampaignsLoading(false);
    });
    return () => controller.abort();
  }, [selectedAdvertiserId, authHeaders]);

  /*
   * The campaign name is whatever the SELECTED campaign is called — nothing
   * else. It previously fell back to the advertiser's approved plan name, which
   * is how the page could truthfully report "No campaigns found" in the selector
   * while naming a campaign in the overview panel. If no campaign is
   * selected, no campaign is named.
   */
  const selectedCampaignNameForContext = useMemo(
    () => (selectedCampaign ? getCanonicalCampaignDisplayName(selectedCampaign) || null : null),
    [selectedCampaign]
  );

  const autoCampaignOptionLabel = useMemo(() => {
    if (!selectedAdvertiserId) return "Select advertiser first";
    if (campaignsLoading) return "Loading campaigns…";
    /*
     * A failed lookup and an advertiser with no campaigns are different facts
     * and must read differently. Neither may present itself as advertiser
     * totals.
     */
    if (campaignsError && !hasCampaignOptions) return "Campaign list unavailable";
    if (!hasCampaignOptions) return "No campaigns available";
    /*
     * Whatever remains is a PROMPT, not a mode.
     *
     * Governed auto-selection persists the campaign's own id, so "auto" is only
     * ever the transient state before that lands, or the ambiguous state where
     * the operator must choose. Both read the same, because both mean the same
     * thing: no campaign is addressed yet. Naming the auto-detected campaign
     * here instead rendered a second row for a campaign already listed below it
     * — "Auto — X" and "X" side by side, one of which was not a real choice.
     */
    return "Select a campaign";
  }, [
    campaignsError,
    campaignsLoading,
    hasCampaignOptions,
    selectedAdvertiserId,
  ]);

  /*
   * Fetch the approved-campaign contract for the selected campaign.
   *
   * The request is keyed on (advertiser, campaign) and every response is
   * checked against the selection that is current when it lands. A campaign
   * switch abandons the in-flight request AND blanks the record, so the
   * previous campaign's flight and platform tabs cannot be on screen for even
   * one frame after the switch.
   */
  useEffect(() => {
    const controller = new AbortController();

    if (!selectedAdvertiserId || !effectiveSelectedCampaignId) {
      setCampaignContractState({
        state: OPTIMUS_CONTRACT_STATE.IDLE,
        contract: null,
        diagnostic: null,
        campaignId: null,
      });
      return () => controller.abort();
    }

    const advertiserId = selectedAdvertiserId;
    const campaignId = effectiveSelectedCampaignId;

    setCampaignContractState({
      state: OPTIMUS_CONTRACT_STATE.LOADING,
      contract: null,
      diagnostic: null,
      campaignId,
    });

    const unresolved = (diagnostic) =>
      setCampaignContractState({
        state: OPTIMUS_CONTRACT_STATE.UNRESOLVED,
        contract: null,
        diagnostic,
        campaignId,
      });

    const requestFailed = {
      code: "CONTRACT_REQUEST_FAILED",
      campaignId: String(campaignId),
      advertiserId: String(advertiserId),
      reason: "The approved campaign contract could not be retrieved.",
    };

    const loadContract = async () => {
      try {
        const params = new URLSearchParams({
          advertiserId: String(advertiserId),
          campaignId: String(campaignId),
        });
        const res = await fetch(apiUrl(`/api/optimus/campaign-contract?${params.toString()}`), {
          method: "GET",
          headers: authHeaders,
          signal: controller.signal,
        });
        const json = await res.json().catch(() => ({}));

        if (
          res.ok &&
          json?.ok &&
          contractMatchesSelection(json.contract, { advertiserId, campaignId })
        ) {
          setCampaignContractState({
            state: OPTIMUS_CONTRACT_STATE.RESOLVED,
            contract: json.contract,
            diagnostic: null,
            campaignId,
          });
          return;
        }

        unresolved(json?.diagnostic || requestFailed);
      } catch (err) {
        if (err?.name === "AbortError") return;
        unresolved(requestFailed);
      }
    };

    loadContract();
    return () => controller.abort();
  }, [authHeaders, effectiveSelectedCampaignId, selectedAdvertiserId]);

  /*
   * The contract, but only when it describes the campaign on screen RIGHT NOW.
   *
   * Everything downstream reads this rather than the raw record, so a response
   * that lands after a campaign switch is inert instead of contaminating.
   */
  const activeCampaignContract = useMemo(() => {
    const { contract } = campaignContractState;
    if (!contract) return null;
    return contractMatchesSelection(contract, {
      advertiserId: selectedAdvertiserId,
      campaignId: effectiveSelectedCampaignId,
    })
      ? contract
      : null;
  }, [campaignContractState, effectiveSelectedCampaignId, selectedAdvertiserId]);

  const activeContractDiagnostic = useMemo(() => {
    if (!effectiveSelectedCampaignId) return null;
    if (campaignContractState.state !== OPTIMUS_CONTRACT_STATE.UNRESOLVED) return null;
    if (String(campaignContractState.campaignId ?? "") !== String(effectiveSelectedCampaignId)) {
      return null;
    }
    return campaignContractState.diagnostic;
  }, [campaignContractState, effectiveSelectedCampaignId]);

  /*
   * The selected campaign's platform tab strip, straight from the contract.
   *
   * The order is the approved allocation's order; the membership is the
   * approved allowlist. Nothing is added and nothing is dropped.
   */
  const contractPlatformTabs = useMemo(
    () => resolveOptimusContractPlatformTabs(activeCampaignContract, platformLabel),
    [activeCampaignContract]
  );

  /*
   * ONE owner for the platform tab strip.
   *
   * In campaign mode the strip IS the contract — recomputed synchronously on
   * every campaign change, so the previous campaign's tabs are replaced rather
   * than unioned with, and never linger for a request's duration. Only
   * advertiser-totals mode falls through to the aggregate inventory, and it is
   * reached through a ref because `loadPlatformTabs` changes identity with the
   * selected TAB — refetching the strip on every tab click is how a stale strip
   * gets a chance to reappear.
   */
  const loadPlatformTabsRef = useRef(loadPlatformTabs);
  useEffect(() => {
    loadPlatformTabsRef.current = loadPlatformTabs;
  });

  useEffect(() => {
    if (!selectedAdvertiserId) {
      setPlatformTabs([]);
      setPlatformTabsScope("advertiser");
      setSelectedPlatformTab("all");
      return undefined;
    }

    // Until the inventory is known, "no campaign selected" is not yet a fact.
    // Loading advertiser-wide tabs on that assumption paints another campaign's
    // channels for a frame.
    if (campaignsLoading || planCampaignsLoading) return undefined;

    if (effectiveSelectedCampaignId) {
      const tabs = dedupeTabs(
        contractPlatformTabs.map((tab) => ({ id: tab.id, label: tab.label })),
        true
      );
      setPlatformTabs(tabs);
      setPlatformTabsScope("campaign");
      setSelectedPlatformTab((previous) =>
        tabs.some((tab) => tab.id === normalize(previous || "all")) ? previous : "all"
      );
      return undefined;
    }

    const controller = new AbortController();
    loadPlatformTabsRef.current?.(selectedAdvertiserId, controller.signal);
    return () => controller.abort();
  }, [
    campaignsLoading,
    contractPlatformTabs,
    effectiveSelectedCampaignId,
    planCampaignsLoading,
    selectedAdvertiserId,
  ]);

  const selectedApprovedPlanCampaign = useMemo(
    () =>
      selectApprovedPlanCampaign({
        campaigns: planCampaigns,
        // Identity leads; the name follows only as a legacy clue, so renaming a
        // campaign cannot orphan its approved plan.
        selectedCampaignId: effectiveSelectedCampaignId,
        selectedCampaignName: selectedCampaignNameForContext,
        allowSingleApprovedFallback: true,
      }),
    [planCampaigns, effectiveSelectedCampaignId, selectedCampaignNameForContext]
  );

  const selectedPlannerContextCampaign = useMemo(
    () =>
      selectPlannerContextCampaign({
        campaigns: planCampaigns,
        selectedCampaignName: selectedCampaignNameForContext,
        allowSingleFallback: true,
      }),
    [planCampaigns, selectedCampaignNameForContext]
  );

  const advertiserFallbackPlan = useMemo(
    () => buildAdvertiserFallbackPlan(getAdvertiserRecord),
    [getAdvertiserRecord]
  );

  const effectiveAdvertiserFallbackPlan = useMemo(() => {
    if (selectedCampaignNameForContext || effectiveSelectedCampaignId) return null;
    return advertiserFallbackPlan;
  }, [selectedCampaignNameForContext, effectiveSelectedCampaignId, advertiserFallbackPlan]);

  const selectedPlanInput = useMemo(
    () => buildCampaignPlanInput(selectedApprovedPlanCampaign),
    [selectedApprovedPlanCampaign]
  );

  const plannerCampaignContextInput = useMemo(
    () => buildCampaignPlanInput(selectedPlannerContextCampaign),
    [selectedPlannerContextCampaign]
  );

  const contributorProgrammaticKeys = useMemo(() => {
    const byPlatform = kpis.byPlatformMetrics && typeof kpis.byPlatformMetrics === "object" ? kpis.byPlatformMetrics : {};
    const attribution =
      kpis.byPlatformAttribution && typeof kpis.byPlatformAttribution === "object"
        ? kpis.byPlatformAttribution
        : {};

    return Object.entries(byPlatform)
      .filter(([, metrics]) =>
        ["spend", "impressions", "clicks", "conversions"].some((key) => {
          const value = parseBudgetValue(metrics?.[key]);
          return typeof value === "number";
        })
      )
      .map(([key]) => normalizePlatformKey(key))
      .filter((key) => {
        const mode = String(
          attribution?.[key]?.clientBilledMode || attribution?.[key]?.client_billed_mode || ""
        )
          .trim()
          .toLowerCase();
        return mode === "approved_programmatic_pricing" || PROGRAMMATIC_BILLABLE_KEYS.has(key);
      });
  }, [kpis.byPlatformAttribution, kpis.byPlatformMetrics]);

  const selectedClientFinancialPlanInput = useMemo(
    () => {
      if (selectedCampaignNameForContext || effectiveSelectedCampaignId) {
        return buildCampaignPlanInput(selectedApprovedPlanCampaign);
      }

      if (!contributorProgrammaticKeys.length) {
        return buildCampaignPlanInput(selectedApprovedPlanCampaign);
      }

      return buildMergedApprovedPricingPlanInput({
        campaigns: planCampaigns,
        preferredCampaign: selectedApprovedPlanCampaign,
        sourceLabel: "Approved campaign pricing",
      });
    },
    [
      contributorProgrammaticKeys.length,
      effectiveSelectedCampaignId,
      planCampaigns,
      selectedApprovedPlanCampaign,
      selectedCampaignNameForContext,
    ]
  );

  const clientFinancialTruth = useMemo(
    () =>
      resolveClientBilledFinancialTruth({
        planInput: selectedClientFinancialPlanInput,
        platformKey: selectedPlatformTab || "all",
        liveMetrics: {
          currency: kpis.currency,
          spendBasis: kpis.spendBasis,
          financialAuthority: kpis.financialAuthority,
          spend: kpis.totalSpend,
          // Wave 3: the truth resolver builds billed truth from these
          // server-declared billed fields — never from spend + config.
          billedSpend: kpis.billedSpend,
          clientFacingSpend: kpis.clientFacingSpend,
          impressions: kpis.impressions,
          clicks: kpis.clicks,
          conversions: kpis.ticketSales,
        },
        liveDataSource:
          kpis.byPlatformDataSources?.[normalizePlatformKey(selectedPlatformTab || "all")] || null,
        liveMetricsByPlatform: kpis.byPlatformMetrics,
        liveAttributionByPlatform: kpis.byPlatformAttribution,
        liveDataSourcesByPlatform: kpis.byPlatformDataSources,
      }),
    [
      selectedClientFinancialPlanInput,
      selectedPlatformTab,
      kpis.totalSpend,
      kpis.currency,
      kpis.spendBasis,
      kpis.financialAuthority,
      kpis.billedSpend,
      kpis.clientFacingSpend,
      kpis.impressions,
      kpis.clicks,
      kpis.ticketSales,
      kpis.byPlatformMetrics,
      kpis.byPlatformAttribution,
      kpis.byPlatformDataSources,
    ]
  );

  const selectedProviderKey = normalizeProviderSurfaceKey(selectedPlatformTab);
  const selectedProviderCurrency = selectedProviderKey ? kpis.currency || null : null;
  const selectedSpendComparableToUsd = !selectedProviderKey || selectedProviderCurrency === "USD";
  const formatSelectedSpend = useCallback(
    (value) => selectedProviderKey ? formatProviderCurrency(value, selectedProviderCurrency) : formatCurrency(value),
    [selectedProviderCurrency, selectedProviderKey]
  );

  const canonicalPacing = useMemo(
    () =>
      buildCanonicalPacing({
        planInput: selectedPlanInput,
        advertiserFallbackPlan: effectiveAdvertiserFallbackPlan,
        selectedCampaignName: selectedCampaignNameForContext,
        // Wave 3: the pacing NUMERATOR is served-billed-or-null. The pacing
        // denominator stays the approved gross budget; the live numerator is
        // never the provider-basis totalSpend.
        liveSpend: selectedSpendComparableToUsd ? (
          deriveClientFacingSpendValue(clientFinancialTruth) ??
          kpis.billedSpend ??
          kpis.clientFacingSpend ??
          null
        ) : null,
      }),
    [
      selectedPlanInput,
      effectiveAdvertiserFallbackPlan,
      selectedCampaignNameForContext,
      selectedSpendComparableToUsd,
      clientFinancialTruth,
      kpis.billedSpend,
      kpis.clientFacingSpend,
    ]
  );

  const resolvedPlannerObjective = useMemo(
    () =>
      canonicalPacing.objective ||
      plannerCampaignContextInput?.objective ||
      (selectedCampaign?.objective ? String(selectedCampaign.objective) : null),
    [canonicalPacing.objective, plannerCampaignContextInput, selectedCampaign]
  );

  const resolvedPlannerSourceLabel = useMemo(
    () => canonicalPacing.sourceLabel || plannerCampaignContextInput?.sourceLabel || null,
    [canonicalPacing.sourceLabel, plannerCampaignContextInput]
  );

  /*
   * The FLIGHT window.
   *
   * With a campaign selected, the server-resolved contract is the only source.
   * It is a single typed field pair taken from that campaign's approved planner
   * artifact — not a client-side ladder over thirty field aliases, not the
   * advertiser's dates, not another campaign's plan, and never a rolling
   * window. If the contract does not resolve, this is null and the page holds.
   *
   * The advertiser-level flight survives only for advertiser-totals mode, where
   * there is no campaign whose flight could be meant instead.
   */
  const getFlightRange = useMemo(() => {
    if (effectiveSelectedCampaignId) {
      return buildOptimusContractFlightRange(activeCampaignContract);
    }
    return effectiveAdvertiserFallbackPlan && !hasApprovedPlanCampaigns ? advertiserFlightRange : null;
  }, [
    activeCampaignContract,
    advertiserFlightRange,
    effectiveAdvertiserFallbackPlan,
    effectiveSelectedCampaignId,
    hasApprovedPlanCampaigns,
  ]);

  const optimusFlightLabel = useMemo(() => {
    const flight = getFlightRange;
    return flight?.start && flight?.end ? formatFlightRange(flight.start, flight.end) : "—";
  }, [getFlightRange]);

  const campaignPlannedSpendToDate = useMemo(
    () => (canonicalPacing.hasPlan ? canonicalPacing.plannedSpendToDate : null),
    [canonicalPacing.hasPlan, canonicalPacing.plannedSpendToDate]
  );
  const clientFacingSpend = useMemo(
    () => deriveClientFacingSpendValue(clientFinancialTruth),
    [clientFinancialTruth]
  );
  // Wave 3 contract: the displayed spend is served-billed-or-null. The billed
  // truth resolver wins; otherwise the server-declared billed fields carried
  // on the KPI payload. `kpis.totalSpend` (provider-basis sum) is NEVER a
  // spend display fallback — when the server declares no billed basis the
  // page shows its unavailable state instead of a raw figure.
  const clientDisplaySpend = useMemo(() => {
    if (typeof clientFacingSpend === "number" && Number.isFinite(clientFacingSpend)) {
      return clientFacingSpend;
    }
    const servedBilled = kpis.billedSpend ?? kpis.clientFacingSpend;
    return typeof servedBilled === "number" && Number.isFinite(servedBilled)
      ? servedBilled
      : null;
  }, [clientFacingSpend, kpis.billedSpend, kpis.clientFacingSpend]);
  const clientFacingCpm = useMemo(
    () =>
      clientFinancialTruth?.available && typeof clientFinancialTruth.billedCpm === "number"
        ? clientFinancialTruth.billedCpm
        : null,
    [clientFinancialTruth]
  );
  const isAllPlatformsView = useMemo(
    () => normalize(selectedPlatformTab || "all") === "all",
    [selectedPlatformTab]
  );
  const plannedPlatformAllocation = useMemo(() => {
    if (isAllPlatformsView) return null;
    const pacingBudget =
      typeof canonicalPacing.grossBudget === "number"
        ? canonicalPacing.grossBudget
        : canonicalPacing.pacingBudget;
    const planInputs = [selectedPlanInput, plannerCampaignContextInput].filter(Boolean);
    for (const planInput of planInputs) {
      const allocation = resolvePlannedPlatformAllocation({
        planInput,
        platformKey: selectedPlatformTab,
        pacingBudget,
      });
      if (allocation) return allocation;
    }
    return null;
  }, [
    canonicalPacing.grossBudget,
    canonicalPacing.pacingBudget,
    isAllPlatformsView,
    plannerCampaignContextInput,
    selectedPlanInput,
    selectedPlatformTab,
  ]);
  const hasScopedPlatformPacing = Boolean(!isAllPlatformsView && plannedPlatformAllocation);
  const scopedPacingBasis = useMemo(() => {
    if (hasScopedPlatformPacing && typeof plannedPlatformAllocation?.plannedBudget === "number") {
      return {
        label: "Approved Budget",
        budget: plannedPlatformAllocation.plannedBudget,
        note: `${plannedPlatformAllocation.label} platform allocation`,
        platformLabel: plannedPlatformAllocation.label,
      };
    }
    if (isAllPlatformsView && typeof canonicalPacing.grossBudget === "number") {
      return {
        label: "Approved Budget",
        budget: canonicalPacing.grossBudget,
        note: "Approved budget",
        platformLabel: null,
      };
    }
    return null;
  }, [
    canonicalPacing.grossBudget,
    hasScopedPlatformPacing,
    isAllPlatformsView,
    plannedPlatformAllocation,
  ]);
  const scopedElapsedSpendRatio = useMemo(() => {
    if (
      Number.isFinite(canonicalPacing.elapsedActiveFlightDays) &&
      Number.isFinite(canonicalPacing.totalFlightDays) &&
      canonicalPacing.totalFlightDays > 0
    ) {
      return Math.max(
        0,
        Math.min(1, canonicalPacing.elapsedActiveFlightDays / canonicalPacing.totalFlightDays)
      );
    }
    return typeof canonicalPacing.elapsedPct === "number" ? canonicalPacing.elapsedPct : null;
  }, [
    canonicalPacing.elapsedActiveFlightDays,
    canonicalPacing.elapsedPct,
    canonicalPacing.totalFlightDays,
  ]);
  const plannedSpendToDate = useMemo(() => {
    if (
      scopedPacingBasis &&
      typeof scopedPacingBasis.budget === "number" &&
      typeof scopedElapsedSpendRatio === "number"
    ) {
      return scopedPacingBasis.budget * scopedElapsedSpendRatio;
    }
    if (isAllPlatformsView) return campaignPlannedSpendToDate;
    return null;
  }, [
    campaignPlannedSpendToDate,
    isAllPlatformsView,
    scopedElapsedSpendRatio,
    scopedPacingBasis,
  ]);
  const grossPlannedSpendToDate = useMemo(() => {
    if (
      isAllPlatformsView &&
      typeof canonicalPacing.grossBudget === "number" &&
      typeof scopedElapsedSpendRatio === "number"
    ) {
      return canonicalPacing.grossBudget * scopedElapsedSpendRatio;
    }
    return null;
  }, [canonicalPacing.grossBudget, isAllPlatformsView, scopedElapsedSpendRatio]);
  const plannedSpendLabel = useMemo(() => {
    return "Planned Spend To Date";
  }, []);
  const isClientBilledSpendDisplay =
    clientFinancialTruth?.available === true &&
    typeof clientFacingSpend === "number" &&
    Number.isFinite(clientFacingSpend);
  const spendComparison = useMemo(() => {
    if (!selectedSpendComparableToUsd) return { value: null, label: "Currency comparison unavailable" };
    if (
      isClientBilledSpendDisplay &&
      typeof grossPlannedSpendToDate === "number" &&
      Number.isFinite(grossPlannedSpendToDate)
    ) {
      return {
        value: grossPlannedSpendToDate,
        label: "Planned Spend To Date",
      };
    }
    return {
      value: plannedSpendToDate,
      label: plannedSpendLabel,
    };
  }, [
    grossPlannedSpendToDate,
    selectedSpendComparableToUsd,
    isClientBilledSpendDisplay,
    plannedSpendLabel,
    plannedSpendToDate,
  ]);
  const clientFacingEfficiencyCpm = useMemo(() => {
    if (
      isAllPlatformsView &&
      typeof clientFacingSpend === "number" &&
      typeof kpis.impressions === "number" &&
      kpis.impressions > 0
    ) {
      return (clientFacingSpend / kpis.impressions) * 1000;
    }
    return typeof clientFacingCpm === "number" ? clientFacingCpm : kpis.blendedCpm;
  }, [clientFacingCpm, clientFacingSpend, isAllPlatformsView, kpis.blendedCpm, kpis.impressions]);
  const clientFacingEfficiencyMeta = useMemo(
    () =>
      deriveClientFacingEfficiencyMeta({
        isAllPlatformsView,
        clientFacingSpend,
        clientFacingCpm,
        impressions: kpis.impressions,
        clientFinancialTruth,
      }),
    [clientFacingCpm, clientFacingSpend, clientFinancialTruth, isAllPlatformsView, kpis.impressions]
  );
  const clientFacingCpmLabel = useMemo(
    () => (isAllPlatformsView ? "Blended eCPM" : "CPM"),
    [isAllPlatformsView]
  );
  const clientFinancialScopeReason = useMemo(() => {
    if (clientFinancialTruth?.available) return null;
    if (clientFinancialTruth?.reasonDetail || clientFinancialTruth?.reason || canonicalPacing.hasPlan) {
      return "Spend is shown for the current available campaign window.";
    }

    return null;
  }, [canonicalPacing.hasPlan, clientFinancialTruth]);
  const clientFacingEfficiencyReasonText = useMemo(
    () =>
      clientFacingEfficiencyMeta?.available
        ? null
        : clientFinancialScopeReason || "Efficiency will appear when delivery data is available.",
    [clientFacingEfficiencyMeta, clientFinancialScopeReason]
  );

  const buildClientSafeMetricsPayload = useCallback(
    ({ platform, metrics, allContext = null } = {}) => {
      const p = normalize(platform || "all") || "all";
      const base = metrics && typeof metrics === "object" ? metrics : {};
      const truth = resolveClientBilledFinancialTruth({
        planInput: selectedClientFinancialPlanInput,
        platformKey: p,
        liveMetrics: {
          currency: base.currency,
          spendBasis: base.spendBasis,
          financialAuthority: base.financialAuthority,
          spend: base.spend,
          billedSpend: base.billedSpend ?? null,
          clientFacingSpend: base.clientFacingSpend ?? null,
          impressions: base.impressions,
          clicks: base.clicks,
          conversions: base.ticketSales ?? base.conversions ?? null,
        },
        liveDataSource:
          base.dataSource ||
          base.data_source ||
          allContext?.byPlatformDataSources?.[normalizePlatformKey(p)] ||
          null,
        liveMetricsByPlatform: allContext?.byPlatform || null,
        liveAttributionByPlatform: allContext?.attributionByPlatform || null,
        liveDataSourcesByPlatform: allContext?.byPlatformDataSources || null,
      });

      const clientSpend =
        truth?.available && typeof truth.billedSpend === "number" ? truth.billedSpend : null;
      const clientCpm =
        truth?.available && typeof truth.billedCpm === "number"
          ? truth.billedCpm
          : typeof clientSpend === "number" &&
            typeof base.impressions === "number" &&
            base.impressions > 0
          ? (clientSpend / base.impressions) * 1000
          : null;

      return {
        ...base,
        spend: clientSpend,
        cpm: clientCpm,
        cpc: truth?.available && typeof truth.billedCpc === "number" ? truth.billedCpc : null,
        clientFacingSpend: clientSpend,
        clientFacingCpm: clientCpm,
        clientFacingCpc:
          truth?.available && typeof truth.billedCpc === "number" ? truth.billedCpc : null,
      };
    },
    [selectedClientFinancialPlanInput]
  );

  // -----------------------------
  // Snapshot (ingested)
  // -----------------------------
  const loadSnapshot = useCallback(
    async ({
      advertiserId,
      campaignId,
      platform,
      datePreset,
      startDate,
      endDate,
      signal,
      allowSharedToplineFallback = true,
    } = {}) => {
      if (!advertiserId || !platform) return null;

      let directSnapshot = null;

      try {
        const hasBoundedReportingWindow = Boolean(startDate || endDate || datePreset);
        const params = new URLSearchParams({
          advertiserId: String(advertiserId),
          channel: String(platform),
          allowLatestFallback: hasBoundedReportingWindow ? "0" : "1",
        });
        if (campaignId) params.append("campaignId", String(campaignId));
        if (selectedCampaignNameForContext) {
          params.append("campaignName", String(selectedCampaignNameForContext));
        }

        appendRangeParams(params, { datePreset, startDate, endDate });

        const res = await withOverviewTimeout(
          fetch(apiUrl(`/api/optimus/snapshot?${params.toString()}`), {
            method: "GET",
            headers: authHeaders,
            signal,
            cache: "no-store",
          }),
          "optimus snapshot"
        );

        if (res.ok) {
          const json = await res.json().catch(() => ({}));
          const k = json?.kpis || null;
          if (!k || typeof k !== "object") directSnapshot = null;

          if (k && typeof k === "object") {
            const spendEfficiency = resolveSpendEfficiencyContract(json);
            const blendedCpqv = resolveSpendEfficiencyMetric(spendEfficiency, {
              metricKey: "cpqv",
            });
            directSnapshot = {
              currency: k.currency || null,
              reportingEvidence: k.reportingEvidence || null,
              financialAuthority: k.financialAuthority || null,
              basisProvenance: k.basisProvenance || null,
              spend: safeNum(k.spend),
              // Wave 3: carry the server-declared billed fields through —
              // these are the only values the page may display as spend.
              billedSpend: safeNum(k.billedSpend ?? k.billed_spend ?? k.clientFacingSpend ?? k.client_facing_spend),
              clientFacingSpend: safeNum(k.clientFacingSpend ?? k.client_facing_spend ?? k.billedSpend ?? k.billed_spend),
              billedCpm: safeNum(k.billedCpm ?? k.billed_cpm ?? k.clientFacingCpm ?? k.client_facing_cpm),
              billedCpc: safeNum(k.billedCpc ?? k.billed_cpc),
              billedCpa: safeNum(k.billedCpa ?? k.billed_cpa),
              spendBasis:
                typeof k.spendBasis === "string" && k.spendBasis.trim()
                  ? k.spendBasis.trim()
                  : typeof k.spend_basis === "string" && k.spend_basis.trim()
                  ? k.spend_basis.trim()
                  : null,
              ctr: safeNum(k.ctr),
              cpm: safeNum(k.cpm),
              cpc: safeNum(k.cpc),
              impressions: safeNum(k.impressions),
              clicks: safeNum(k.clicks),
              videoViews: safeNum(k.videoViews) ?? safeNum(k.video_views),
              engagements: safeNum(k.engagements),
              completions: safeNum(k.completions),
              completionRate: safeNum(k.completionRate),
              /* `engagement_rate` is GA4's SITE rate (engaged sessions over
                 sessions), not a snake_case spelling of the governed paid rate.
                 Reading it here is how a session figure reached a paid card. */
              engagementRate: safeNum(k.engagementRate),
              videoViewRate: safeNum(k.videoViewRate ?? k.video_view_rate),
              videoStarted: safeNum(k.videoStarted ?? k.video_started),
              videoCompleted: safeNum(k.videoCompleted ?? k.video_completed),
              users: safeNum(k.users),
              newUsers: safeNum(k.newUsers ?? k.new_users),
              sessions: safeNum(k.sessions),
              engagedSessions: safeNum(k.engagedSessions ?? k.engaged_sessions),
              eventCount: safeNum(k.eventCount ?? k.event_count),
              averageSessionDuration: safeNum(
                k.averageSessionDuration ?? k.average_session_duration
              ),
              ticketSales: safeNum(k.ticketSales) ?? safeNum(k.ticket_sales),
              // Consumed as published. Optimus never re-derives either: CPQV's
              // numerator is the spend of the platforms carrying a governed
              // qualified signal, not the campaign's whole spend.
              qualifiedVideoViews:
                safeNum(k.qualifiedVideoViews) ?? safeNum(k.qualified_video_views),
              costPerQualifiedVideoView:
                safeNum(blendedCpqv?.value),
              spendEfficiency,
              // Client-facing basis vocabulary only: the contract's client
              // label ("Client-billed"), never its internal gross/net label.
              qualifiedVideoSpendBasisLabel:
                typeof spendEfficiency?.spendBasisClientLabel === "string"
                  ? spendEfficiency.spendBasisClientLabel
                  : null,
              qualifiedVideoComplete:
                typeof k.qualifiedVideoComplete === "boolean" ? k.qualifiedVideoComplete : null,
              byPlatform: json?.byPlatform && typeof json.byPlatform === "object" ? json.byPlatform : null,
              dataSourcesByPlatform:
                json?.dataSourcesByPlatform && typeof json.dataSourcesByPlatform === "object"
                  ? json.dataSourcesByPlatform
                  : null,
              dataSource:
                typeof json?.snapshot?.data_source === "string" && json.snapshot.data_source.trim()
                  ? json.snapshot.data_source.trim()
                  : null,
              sourceLabel:
                typeof json?.sourceLabel === "string" && json.sourceLabel.trim()
                  ? json.sourceLabel.trim()
                  : null,
              snapshotProvider:
                typeof json?.snapshot?.provider === "string" && json.snapshot.provider.trim()
                  ? json.snapshot.provider.trim()
                  : null,
              sourceContract:
                json?.snapshot?.sourceContract && typeof json.snapshot.sourceContract === "object"
                  ? json.snapshot.sourceContract
                  : null,
              canonicalReporting:
                json?.canonicalReporting && typeof json.canonicalReporting === "object"
                  ? json.canonicalReporting
                  : json?.canonicalSummary && typeof json.canonicalSummary === "object"
                  ? json.canonicalSummary
                  : null,
              clientBillingAttribution:
                json?.clientBillingAttribution && typeof json.clientBillingAttribution === "object"
                  ? json.clientBillingAttribution
                  : null,
              optimusGrading:
                json?.optimusGrading && typeof json.optimusGrading === "object"
                  ? json.optimusGrading
                  : null,
            };
          }
        }

        if (isCanonicalSnapshot(directSnapshot) || hasUsableOverviewSnapshot(directSnapshot)) return directSnapshot;
      } catch (e) {
        if (e?.name === "AbortError") return null;
      }

      // A missing/revoked provider snapshot cannot fall back to advertiser totals.
      if (normalizeProviderSurfaceKey(platform)) return directSnapshot;
      if (!allowSharedToplineFallback) return null;

      try {
        const sharedTopline = await withOverviewTimeout(
          getToplinePerformance({
            advertiserId,
            timeframe: buildToplineTimeframeFromRange({ datePreset, startDate, endDate }),
            advertiserFlightRange,
            platformKey: normalize(platform) === "all" ? "" : platform,
            signal,
            applyProgrammaticCampaignPricing: true,
          }),
          "shared topline fallback"
        );
        const fallbackSnapshot = snapshotFromTopline(sharedTopline, { platform });
        if (fallbackSnapshot) return fallbackSnapshot;
      } catch (e) {
        if (e?.name === "AbortError") return null;
      }

      return hasUsableOverviewSnapshot(directSnapshot) ? directSnapshot : null;
    },
    [advertiserFlightRange, authHeaders, selectedCampaignNameForContext]
  );

  // -----------------------------
  // Saved recs (latest)
  // -----------------------------
  const fetchSavedRecommendations = useCallback(
    async ({
      advertiserId,
      campaignId,
      platform,
      datePreset,
      startDate,
      endDate,
      signal,
      currentMetrics,
      debugContext,
    } = {}) => {
      if (!advertiserId || !platform) {
        if (debugContext) {
          logOptimusRun("saved_fallback_skipped", {
            ...debugContext,
            reason: "missing_context",
            advertiserId,
            platform,
          }, "warn");
        }
        return { recommendations: [], sourceLabel: "", reason: "missing_context" };
      }

      if (debugContext) {
        logOptimusRun("saved_fallback_started", {
          ...debugContext,
          advertiserId,
          campaignId: campaignId || null,
          platform,
          window: { datePreset: datePreset || null, startDate: startDate || null, endDate: endDate || null },
          currentMetrics: summarizeOptimusRunMetrics(currentMetrics),
        });
      }

      const params = new URLSearchParams({
        advertiserId: String(advertiserId),
        platform: String(platform),
        _ts: String(Date.now()),
      });

      appendRangeParams(params, { datePreset, startDate, endDate });

      if (campaignId) params.append("campaignId", String(campaignId));

      const res = await fetch(apiUrl(`/api/optimus/recommendations/latest?${params.toString()}`), {
        method: "GET",
        headers: authHeaders,
        signal,
        cache: "no-store",
      });

      if (!res.ok) {
        if (debugContext) {
          logOptimusRun("saved_fallback_rejected", {
            ...debugContext,
            status: res.status,
            reason: `saved_fetch_${res.status}`,
          }, "warn");
        }
        return { recommendations: [], sourceLabel: "", reason: `saved_fetch_${res.status}` };
      }

      const json = await res.json().catch(() => ({}));
      const recDoc = json?.found === true ? json?.data : json?.data ? json.data : null;

      if (!recDoc?.recommendations || !Array.isArray(recDoc.recommendations)) {
        if (debugContext) {
          logOptimusRun("saved_fallback_rejected", {
            ...debugContext,
            reason: "saved_missing_recommendations",
          }, "warn");
        }
        return { recommendations: [], sourceLabel: "", reason: "saved_missing_recommendations" };
      }

      if (
        !recommendationDocMatchesScope(recDoc, {
          advertiserId,
          campaignId,
          platform,
          datePreset,
          startDate,
          endDate,
        })
      ) {
        if (debugContext) {
          logOptimusRun("saved_fallback_rejected", {
            ...debugContext,
            reason: "saved_scope_mismatch",
            savedScope: {
              advertiserId: recDoc.advertiserId ?? recDoc.advertiser_id ?? null,
              campaignId: recDoc.campaignId ?? recDoc.campaign_id ?? null,
              platform: recDoc.platform ?? null,
              datePreset: recDoc.datePreset ?? recDoc.date_preset ?? null,
            },
          }, "warn");
        }
        return { recommendations: [], sourceLabel: "", reason: "saved_scope_mismatch" };
      }

      if (!recommendationDocMatchesCurrentMetrics(recDoc, currentMetrics)) {
        if (debugContext) {
          logOptimusRun("saved_fallback_rejected", {
            ...debugContext,
            reason: "saved_metric_mismatch",
            currentSignature: buildOptimusMetricSignature(currentMetrics),
            savedSignature:
              recDoc?.canonicalFreshness?.metricSignature ||
              recDoc?.freshness?.metricSignature ||
              recDoc?.kpis?._canonicalFreshness?.metricSignature ||
              recDoc?.kpis?.freshness?.metricSignature ||
              recDoc?.metricSignature ||
              recDoc?.metric_signature ||
              recDoc?.kpis?.metricSignature ||
              recDoc?.kpis?.metric_signature ||
              buildOptimusMetricSignature(recDoc?.kpis || {}),
          }, "warn");
        }
        return { recommendations: [], sourceLabel: "", reason: "saved_metric_mismatch" };
      }

      const savedSourceLabel = sourceLabelForRecommendationDoc(recDoc);
      const savedWindowLabel = buildRecommendationWindowLabel({ datePreset, startDate, endDate });
      const savedGeneratedAt = recDoc.generatedAt || recDoc.createdAt || null;
      const savedEvidenceLine = deriveEvidenceFacts(recDoc.kpis || currentMetrics || {}).evidenceLine;
      const mappedRaw = recDoc.recommendations.map((r, idx) => ({
        id: r.id || `saved-rec-${idx}`,
        category: r.category || "Optimization",
        confidence: typeof r.confidence === "number" ? r.confidence : null,
        title: r.title || "Optimization opportunity",
        summary: r.summary || "",
        evidence: r.evidence || r.evidenceLine || r.basis?.evidenceLine || savedEvidenceLine,
        observation: r.observation || null,
        reason: r.reason || null,
        action: r.action || null,
        sourceType: r.sourceType || recDoc.source || "saved",
        sourceLabel: savedSourceLabel,
        generatedAt: r.generatedAt || savedGeneratedAt,
        dataWindow: r.dataWindow || savedWindowLabel,
      }));

      const recommendations = sanitizeClientRecommendationsStrict(
        platform,
        filterPlatformIncompatibleRecs(platform, mappedRaw)
      );

      if (!recommendations.length) {
        if (debugContext) {
          logOptimusRun("saved_fallback_rejected", {
            ...debugContext,
            reason: "saved_recommendations_unusable",
            rawCount: recDoc.recommendations.length,
          }, "warn");
        }
        return { recommendations: [], sourceLabel: "", reason: "saved_recommendations_unusable" };
      }

      if (debugContext) {
        logOptimusRun("saved_fallback_accepted", {
          ...debugContext,
          sourceLabel: savedSourceLabel,
          recommendationCount: recommendations.length,
          generatedAt: savedGeneratedAt,
        });
      }

      return {
        recommendations,
        sourceLabel: savedSourceLabel,
        generatedAt: savedGeneratedAt,
        recDoc,
        reason: "",
      };
    },
    [authHeaders]
  );

  const loadSavedRecommendations = useCallback(
    async ({
      advertiserId,
      campaignId,
      platform,
      datePreset,
      startDate,
      endDate,
      signal,
      scopeKey,
      requestSeq,
      currentMetrics,
    } = {}) => {
      const canApply = () => {
        if (signal?.aborted) return false;
        if (scopeKey && currentOptimusScopeRef.current !== scopeKey) return false;
        if (
          typeof requestSeq === "number" &&
          optimusRequestSeqRef.current !== requestSeq
        ) {
          return false;
        }
        return true;
      };

      if (!advertiserId || !platform) {
        if (canApply()) {
          setRecommendations([]);
          setRecsSourceLabel("");
        }
        return;
      }

      if (!canApply()) return;

      setRecsLoading(true);
      setRecsSourceLabel("");

      try {
        const saved = await fetchSavedRecommendations({
          advertiserId,
          campaignId,
          platform,
          datePreset,
          startDate,
          endDate,
          signal,
          currentMetrics,
        });

        if (!canApply()) return;

        if (!saved.recommendations.length) {
          setRecommendations([]);
          setRecsSourceLabel("");
          return;
        }

        setRecommendations(saved.recommendations);
        setRecsSourceLabel(saved.sourceLabel);
      } catch (err) {
        if (err?.name === "AbortError") return;
        if (canApply()) {
          setRecommendations([]);
          setRecsSourceLabel("");
        }
      } finally {
        if (canApply()) setRecsLoading(false);
      }
    },
    [fetchSavedRecommendations]
  );



  // -----------------------------
  // Spend share helper
  // -----------------------------
  const computeSpendSharePct = (platformSpend, totalAllSpend) => {
    if (typeof platformSpend !== "number" || typeof totalAllSpend !== "number" || totalAllSpend <= 0) return null;
    const pct = (platformSpend / totalAllSpend) * 100;
    if (!Number.isFinite(pct)) return null;
    return pct;
  };

  // -----------------------------
  // LIVE Meta helper (TRUE ZERO FIX)
  // -----------------------------
  const fetchMetaInsights = useCallback(
    async ({ advertiserId, campaignId, datePreset, startDate, endDate, breakdowns, signal } = {}) => {
      if (!advertiserId) return { ok: false, rows: [], reason: "missing advertiserId" };

      try {
        const params = new URLSearchParams({ advertiserId: String(advertiserId) });

        if (campaignId) {
          params.append("level", "campaign");
          params.append("campaignId", String(campaignId));
        } else {
          params.append("level", "account");
        }

        appendRangeParams(params, { datePreset, startDate, endDate });

        if (breakdowns) params.append("breakdowns", String(breakdowns));

        const response = await withOverviewTimeout(
          fetch(apiUrl(`/api/meta/insights?${params.toString()}`), {
            method: "GET",
            headers: authHeaders,
            signal,
            cache: "no-store",
          }),
          "meta insights"
        );

        if (!response?.ok) {
          const text = await response.text().catch(() => "");
          return {
            ok: false,
            rows: [],
            reason: response
              ? `meta insights ${response.status}: ${text || response.statusText}`
              : "meta insights timed out",
          };
        }

        const json = await response.json().catch(() => ({}));
        const rows = Array.isArray(json.data) ? json.data : [];
        if (!rows.length) return { ok: false, rows: [], reason: "meta insights returned 0 rows" };

        return { ok: true, rows, reason: "" };
      } catch (e) {
        if (e?.name === "AbortError") return { ok: false, rows: [], reason: "aborted" };
        return { ok: false, rows: [], reason: e?.message || "meta insights error" };
      }
    },
    [authHeaders]
  );

  const sumMetaRows = (rows) => {
    let totalSpend = 0;
    let totalImpressions = 0;
    let totalClicks = 0;

    for (const row of rows || []) {
      const spend = parseFloat(row.spend ?? row.spend_amount ?? row.total_spend ?? 0);
      const impressions = parseFloat(row.impressions ?? 0);
      const clicks = parseFloat(row.clicks ?? 0);

      if (!Number.isNaN(spend)) totalSpend += spend;
      if (!Number.isNaN(impressions)) totalImpressions += impressions;
      if (!Number.isNaN(clicks)) totalClicks += clicks;
    }

    const blendedCtr = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : null;
    const blendedCpm = totalImpressions > 0 ? (totalSpend / totalImpressions) * 1000 : null;

    return {
      spend: Number.isFinite(totalSpend) ? totalSpend : null,
      impressions: Number.isFinite(totalImpressions) ? totalImpressions : null,
      clicks: Number.isFinite(totalClicks) ? totalClicks : null,
      ctr: typeof blendedCtr === "number" ? blendedCtr : null,
      cpm: typeof blendedCpm === "number" ? blendedCpm : null,
      videoViews: null,
      qualifiedVideoViews: null,
      qualifiedVideoExplainer: null,
      engagements: null,
      completions: null,
      completionRate: null,
    };
  };

  const zeroMetaMetrics = () => ({
    spend: 0,
    impressions: 0,
    clicks: 0,
    ctr: null,
    cpm: null,
    videoViews: null,
    qualifiedVideoViews: null,
    qualifiedVideoExplainer: null,
    engagements: null,
    completions: null,
    completionRate: null,
  });

  const tryLoadLiveMetaTotals = useCallback(
    async ({ advertiserId, campaignId, datePreset, startDate, endDate, platform, signal } = {}) => {
      if (!advertiserId) return { ok: false, metrics: null, reason: "missing advertiserId" };

      const want = normalize(platform);

      if (!want || want === "all") {
        const res = await fetchMetaInsights({
          advertiserId,
          campaignId,
          datePreset,
          startDate,
          endDate,
          breakdowns: null,
          signal,
        });

        if (!res.ok) return { ok: false, metrics: null, reason: res.reason || "live meta unavailable" };
        return { ok: true, metrics: sumMetaRows(res.rows), reason: "" };
      }

      if (want === "facebook" || want === "instagram" || want === "audience_network" || want === "messenger") {
        const res = await fetchMetaInsights({
          advertiserId,
          campaignId,
          datePreset,
          startDate,
          endDate,
          breakdowns: "publisher_platform",
          signal,
        });

        if (!res.ok) return { ok: false, metrics: null, reason: res.reason || "live meta unavailable" };

        const filtered = (res.rows || []).filter((r) => {
          const pub = normalizePublisherPlatform(r.publisher_platform || r.publisherPlatform || r.publisher || "");
          return pub === want;
        });

        // ✅ TRUE ZERO for missing split rows
        if (!filtered.length) return { ok: true, metrics: zeroMetaMetrics(), reason: `live meta: 0 delivery for ${want}` };

        return { ok: true, metrics: sumMetaRows(filtered), reason: "" };
      }

      return { ok: false, metrics: null, reason: "not a meta platform" };
    },
    [fetchMetaInsights]
  );

  // -----------------------------
  // Metrics builder
  // -----------------------------
  const buildMetricsForPlatform = useCallback(
    async ({ platform, advertiserId, campaignId, datePreset, startDate, endDate, signal } = {}) => {
      const p = normalize(platform);

      const empty = {
        spend: null,
        billedSpend: null,
        clientFacingSpend: null,
        ctr: null,
        cpm: null,
        impressions: null,
        clicks: null,
        videoViews: null,
        qualifiedVideoViews: null,
        qualifiedVideoExplainer: null,
        engagements: null,
        engagementRate: null,
        videoViewRate: null,
        completions: null,
        completionRate: null,
        ticketSales: null,
        dataSource: null,
      };

      const isMetaBreakdownTab = isMetaPlatformId(p);
      const liveByChannel = {};
      const ingestByChannel = {};

      if (isMetaBreakdownTab) {
        const live = await tryLoadLiveMetaTotals({
          advertiserId,
          campaignId,
          datePreset,
          startDate,
          endDate,
          platform: p,
          signal,
        });

        if (live.ok && live.metrics) liveByChannel[p] = { ...live.metrics, data_source: "live_meta" };

        const snap = await loadSnapshot({
          advertiserId,
          campaignId,
          platform: p,
          datePreset,
          startDate,
          endDate,
          signal,
        });
        if (isCanonicalSnapshot(snap)) {
          return {
            metrics: {
              ...empty,
              ...pickCanonicalRates(snap),
            currency: snap?.currency ?? null,
            spendBasis: snap?.spendBasis ?? null,
            financialAuthority: snap?.financialAuthority ?? null,
            basisProvenance: snap?.basisProvenance ?? null,
            reportingEvidence: snap?.reportingEvidence ?? null,
              spend: snap?.spend ?? null,
              // Canonical summary spend is client-billed basis by contract.
              billedSpend: normalizeProviderSurfaceKey(p) ? governedProviderSpend(snap) : snap?.billedSpend ?? snap?.spend ?? null,
              clientFacingSpend: normalizeProviderSurfaceKey(p) ? governedProviderSpend(snap) : snap?.clientFacingSpend ?? snap?.spend ?? null,
              ctr: snap?.ctr ?? null,
              cpm: snap?.cpm ?? null,
              impressions: snap?.impressions ?? null,
              clicks: snap?.clicks ?? null,
              videoViews: snap?.videoViews ?? null,
              engagements: snap?.engagements ?? null,
              completions: snap?.completions ?? null,
              completionRate: snap?.completionRate ?? null,
              ticketSales: snap?.ticketSales ?? null,
              dataSource: snap?.dataSource || "canonical_reporting_summary",
            },
            optimusGrading: snap?.optimusGrading || null,
            source: "CANONICAL",
            reason: snap?.sourceLabel || "Canonical Performance Summary",
          };
        }
        if (snap) ingestByChannel[p] = { ...snap, data_source: snap.dataSource || "ingested" };

        const { resolvedMetrics, resolvedByChannel } = resolveReportingTruth({
          liveByChannel,
          ingestByChannel,
        });
        const resolvedChannel = resolvedByChannel[p] || null;
        if (!resolvedChannel) {
          return { metrics: empty, source: snap ? "INGESTED" : "EMPTY", reason: live.reason || "live unavailable" };
        }

        return {
          metrics: {
            ...empty,
            ...(resolvedMetrics || {}),
            completions: resolvedChannel?.completions ?? resolvedChannel?.conversions ?? null,
            completionRate: resolvedChannel?.completionRate ?? null,
            dataSource: resolvedChannel?.data_source || null,
          },
          source: live.ok ? "LIVE_META" : snap ? "INGESTED" : "EMPTY",
          reason: live.ok ? live.reason || "" : live.reason || "live unavailable",
        };
      }

      const snap = await loadSnapshot({
        advertiserId,
        campaignId,
        platform: p,
        datePreset,
        startDate,
        endDate,
        signal,
      });
      if (isCanonicalSnapshot(snap)) {
        return {
          metrics: {
            ...empty,
            ...pickCanonicalRates(snap),
            currency: snap?.currency ?? null,
            spendBasis: snap?.spendBasis ?? null,
            financialAuthority: snap?.financialAuthority ?? null,
            basisProvenance: snap?.basisProvenance ?? null,
            reportingEvidence: snap?.reportingEvidence ?? null,
            spend: snap?.spend ?? null,
            // Canonical summary spend is client-billed basis by contract.
            billedSpend: normalizeProviderSurfaceKey(p) ? governedProviderSpend(snap) : snap?.billedSpend ?? snap?.spend ?? null,
            clientFacingSpend: normalizeProviderSurfaceKey(p) ? governedProviderSpend(snap) : snap?.clientFacingSpend ?? snap?.spend ?? null,
            ctr: snap?.ctr ?? null,
            cpm: snap?.cpm ?? null,
            impressions: snap?.impressions ?? null,
            clicks: snap?.clicks ?? null,
            videoViews: snap?.videoViews ?? null,
            engagements: snap?.engagements ?? null,
            completions: snap?.completions ?? null,
            completionRate: snap?.completionRate ?? null,
            ticketSales: snap?.ticketSales ?? null,
            dataSource: snap?.dataSource || "canonical_reporting_summary",
          },
          optimusGrading: snap?.optimusGrading || null,
          source: "CANONICAL",
          reason: snap?.sourceLabel || "Canonical Performance Summary",
        };
      }
      if (snap) ingestByChannel[p] = { ...snap, data_source: snap.dataSource || "ingested" };

      const { resolvedMetrics, resolvedByChannel } = resolveReportingTruth({
        liveByChannel,
        ingestByChannel,
      });
      const resolvedChannel = resolvedByChannel[p] || null;
      if (resolvedChannel && resolvedMetrics) {
        return {
          metrics: {
            ...empty,
            ...resolvedMetrics,
            completions: resolvedChannel?.completions ?? resolvedChannel?.conversions ?? null,
            completionRate: resolvedChannel?.completionRate ?? null,
            dataSource: resolvedChannel?.data_source || null,
          },
          source: snap ? "INGESTED" : "EMPTY",
          reason: "",
        };
      }

      return { metrics: empty, source: "EMPTY", reason: "" };
    },
    [loadSnapshot, tryLoadLiveMetaTotals]
  );

  // -----------------------------
  // Build “All Platforms” totals (Meta counted ONCE)
  // -----------------------------
  const buildAllPlatformsTotals = useCallback(
    async ({ advertiserId, campaignId, datePreset, startDate, endDate, signal } = {}) => {
      const ids = Array.isArray(platformTabs)
        ? platformTabs.map((t) => t.id).filter((id) => id && id !== "all" && !isExcludedPlatformId(id))
        : [];

      if (!ids.length) {
        const snap = await loadSnapshot({
          advertiserId,
          campaignId,
          platform: "all",
          datePreset,
          startDate,
          endDate,
          signal,
        });
        return {
          totals: {
            ...pickCanonicalRates(snap),
            currency: snap?.currency ?? null,
            spendBasis: snap?.spendBasis ?? null,
            financialAuthority: snap?.financialAuthority ?? null,
            basisProvenance: snap?.basisProvenance ?? null,
            reportingEvidence: snap?.reportingEvidence ?? null,
            spend: snap?.spend ?? null,
            billedSpend: snap?.billedSpend ?? null,
            clientFacingSpend: snap?.clientFacingSpend ?? null,
            impressions: snap?.impressions ?? null,
            clicks: snap?.clicks ?? null,
            ctr: snap?.ctr ?? null,
            cpm: snap?.cpm ?? null,
            videoViews: snap?.videoViews ?? null,
            engagements: snap?.engagements ?? null,
            completions: snap?.completions ?? null,
            completionRate: snap?.completionRate ?? null,
            ticketSales: snap?.ticketSales ?? null,
          },
          byPlatform: snap?.byPlatform && typeof snap.byPlatform === "object" ? snap.byPlatform : {},
          byPlatformDataSources:
            snap?.dataSourcesByPlatform && typeof snap.dataSourcesByPlatform === "object"
              ? snap.dataSourcesByPlatform
              : {},
          attributionByPlatform:
            snap?.clientBillingAttribution?.byPlatform &&
            typeof snap.clientBillingAttribution.byPlatform === "object"
              ? snap.clientBillingAttribution.byPlatform
              : {},
          sources: { usedLiveMeta: false, usedIngested: !!snap, liveReason: "" },
          optimusGrading: snap?.optimusGrading || null,
        };
      }

      let usedLiveMeta = false;
      let usedIngested = false;
      let liveReason = "";
      const liveByChannel = {};
      const ingestByChannel = {};
      const byPlatform = {};
      const allSnapshot = await loadSnapshot({
        advertiserId,
        campaignId,
        platform: "all",
        datePreset,
        startDate,
        endDate,
        signal,
      });
      const canonicalAllTotals = {
        ...pickCanonicalRates(allSnapshot),
        spend: allSnapshot?.spend ?? null,
        billedSpend: allSnapshot?.billedSpend ?? null,
        clientFacingSpend: allSnapshot?.clientFacingSpend ?? null,
        impressions: allSnapshot?.impressions ?? null,
        clicks: allSnapshot?.clicks ?? null,
        ctr: allSnapshot?.ctr ?? null,
        cpm: allSnapshot?.cpm ?? null,
        videoViews: allSnapshot?.videoViews ?? null,
        /* Campaign-level only — the ALL view is the one scope where a blended
           video KPI is the honest headline. Per-platform cards keep their own
           native counts and never carry this. */
        qualifiedVideoViews:
          allSnapshot?.qualifiedVideoViews ?? allSnapshot?.qualified_video_views ?? null,
        qualifiedVideoExplainer:
          allSnapshot?.qualifiedVideo?.explainer ?? allSnapshot?.qualifiedVideoExplainer ?? null,
        engagements: allSnapshot?.engagements ?? null,
        completions: allSnapshot?.completions ?? null,
        completionRate: allSnapshot?.completionRate ?? null,
        ticketSales: allSnapshot?.ticketSales ?? null,
        costPerQualifiedVideoView: allSnapshot?.costPerQualifiedVideoView ?? null,
        spendEfficiency: allSnapshot?.spendEfficiency ?? null,
        qualifiedVideoSpendBasisLabel:
          allSnapshot?.qualifiedVideoSpendBasisLabel ?? null,
        qualifiedVideoComplete: allSnapshot?.qualifiedVideoComplete ?? null,
      };
      const hasCanonicalAllDelivery = hasDeliverySignal(canonicalAllTotals);
      /*
       * Whether the reporting layer answered COMPLETELY for this campaign.
       * Only a canonical contract licenses reading a platform's absence from
       * the payload as "it did not deliver" — see optimusPlatformActivation.js.
       */
      const canonicalReportingComplete = isCanonicalSnapshot(allSnapshot);
      const byPlatformDataSources = {
        ...(allSnapshot?.dataSourcesByPlatform && typeof allSnapshot.dataSourcesByPlatform === "object"
          ? allSnapshot.dataSourcesByPlatform
          : {}),
      };
      const attributionByPlatform =
        allSnapshot?.clientBillingAttribution?.byPlatform &&
        typeof allSnapshot.clientBillingAttribution.byPlatform === "object"
          ? allSnapshot.clientBillingAttribution.byPlatform
          : {};

      if (
        canonicalReportingComplete || hasCanonicalAllDelivery
      ) {
        return {
          totals: canonicalAllTotals,
          canonicalReportingComplete,
          byPlatform: allSnapshot?.byPlatform && typeof allSnapshot.byPlatform === "object" ? allSnapshot.byPlatform : {},
          byPlatformDataSources,
          attributionByPlatform,
          optimusGrading: allSnapshot?.optimusGrading || null,
          sources: {
            usedLiveMeta: false,
            usedIngested: true,
            liveReason: allSnapshot?.sourceLabel || allSnapshot?.dataSource || "current overview source",
          },
        };
      }

      if (allSnapshot?.byPlatform && typeof allSnapshot.byPlatform === "object") {
        for (const [channelKey, channelMetrics] of Object.entries(allSnapshot.byPlatform)) {
          ingestByChannel[channelKey] = {
            ...(channelMetrics || {}),
            data_source:
              (allSnapshot?.dataSourcesByPlatform &&
                allSnapshot.dataSourcesByPlatform[channelKey]) ||
              "ingested",
          };
        }
        if (Object.keys(allSnapshot.byPlatform).length > 0) usedIngested = true;
      }
      if (hasCanonicalAllDelivery) usedIngested = true;

      const metaIds = ["facebook", "instagram", "audience_network", "messenger"].filter((id) =>
        ids.includes(id)
      );
      const idsToLoop = ids.filter((id) => !isMetaPlatformId(id));

      for (const metaId of metaIds) {
        const live = await tryLoadLiveMetaTotals({
          advertiserId,
          campaignId,
          datePreset,
          startDate,
          endDate,
          platform: metaId,
          signal,
        });

        if (live.ok && live.metrics) {
          usedLiveMeta = true;
          liveByChannel[metaId] = { ...live.metrics, data_source: "live_meta" };
        } else if (!liveReason) {
          liveReason = live.reason || "live unavailable";
        }

        const snap = await loadSnapshot({
          advertiserId,
          campaignId,
          platform: metaId,
          datePreset,
          startDate,
          endDate,
          signal,
          allowSharedToplineFallback: false,
        });
        if (snap) {
          usedIngested = true;
          ingestByChannel[metaId] = { ...snap, data_source: snap.dataSource || "ingested" };
        }
      }

      for (const id of idsToLoop) {
        if (!ingestByChannel[id]) {
          const snap = await loadSnapshot({
            advertiserId,
            campaignId,
            platform: id,
            datePreset,
            startDate,
            endDate,
            signal,
            allowSharedToplineFallback: false,
          });
          if (snap) {
            usedIngested = true;
            ingestByChannel[id] = { ...snap, data_source: snap.dataSource || "ingested" };
          }
        }
      }
      const { resolvedByChannel, resolvedMetrics } = resolveReportingTruth({
        liveByChannel,
        ingestByChannel,
      });
      const hasResolvedChannels = Object.keys(resolvedByChannel || {}).length > 0;
      /*
       * The campaign completion pair comes from the governed fold, not from
       * this loop.
       *
       * What stood here added every channel's completion count together and
       * then took the completion RATE from whichever channel happened to
       * report one first. Two different platform populations, presented as a
       * count and its rate: on Never Stop Chasing that is CTV's 165,084 events
       * at a 95% threshold plus YouTube's 52,901 at 100% — 217,985, a figure
       * belonging to no measurement and unsupportable by the 167,806 starts
       * the campaign recorded — beside CTV's own 98.38%. The shared engine
       * scopes both halves to one population and names who it left out.
       */
      const totalCompletions = resolvedMetrics?.videoCompletions ?? null;
      const hasCompletions = totalCompletions !== null;
      const completionRate = resolvedMetrics?.completionRate ?? null;

      for (const id of ids) {
        const resolvedChannel = resolvedByChannel[id] || null;
        const channelMetrics = computeReportingMetrics(resolvedByChannel[id] || null);
        /* Still read per channel — a platform's own card states its own native
           count, which is never in question. Only the cross-channel total was. */
        const channelCompletions = safeNum(
          resolvedChannel?.completions ?? resolvedChannel?.conversions ?? null
        );
        byPlatform[id] = {
          ...pickCanonicalSignalRates(resolvedChannel),
          spend: channelMetrics?.spend ?? null,
          impressions: channelMetrics?.impressions ?? null,
          clicks: channelMetrics?.clicks ?? null,
          ctr: channelMetrics?.ctr ?? null,
          cpm: channelMetrics?.cpm ?? null,
          videoViews: channelMetrics?.videoViews ?? null,
          engagements: channelMetrics?.engagements ?? null,
          completions: channelCompletions,
          completionRate: safeNum(resolvedChannel?.completionRate ?? null),
          ticketSales: channelMetrics?.ticketSales ?? null,
          dataSource: resolvedChannel?.data_source || null,
        };
        byPlatformDataSources[id] = resolvedChannel?.data_source || null;
      }

      return {
        totals: {
          /*
           * The governed rates ride along with the totals they describe. When
           * the canonical rollup delivered, its eligibility-scoped rates are
           * the answer; the per-channel resolve path carries whatever rates
           * those channels published. Neither is ever re-divided here.
           */
          ...pickCanonicalRates(hasCanonicalAllDelivery ? canonicalAllTotals : resolvedMetrics),
          spend: hasCanonicalAllDelivery
            ? canonicalAllTotals.spend
            : hasResolvedChannels
            ? resolvedMetrics?.spend ?? null
            : null,
          // Wave 3: billed figures come only from served/declared values —
          // the snapshot's billed fields, or the billed-first merge output
          // (whose channel bags are declared client-billed sources).
          billedSpend: hasCanonicalAllDelivery
            ? canonicalAllTotals.billedSpend ?? canonicalAllTotals.spend
            : hasResolvedChannels
            ? resolvedMetrics?.clientFacingSpend ?? null
            : null,
          clientFacingSpend: hasCanonicalAllDelivery
            ? canonicalAllTotals.clientFacingSpend ?? canonicalAllTotals.spend
            : hasResolvedChannels
            ? resolvedMetrics?.clientFacingSpend ?? null
            : null,
          impressions: hasCanonicalAllDelivery
            ? canonicalAllTotals.impressions
            : hasResolvedChannels
            ? resolvedMetrics?.impressions ?? null
            : null,
          clicks: hasCanonicalAllDelivery
            ? canonicalAllTotals.clicks
            : hasResolvedChannels
            ? resolvedMetrics?.clicks ?? null
            : null,
          ctr: hasCanonicalAllDelivery
            ? canonicalAllTotals.ctr
            : hasResolvedChannels
            ? resolvedMetrics?.ctr ?? null
            : null,
          cpm: hasCanonicalAllDelivery
            ? canonicalAllTotals.cpm
            : hasResolvedChannels
            ? resolvedMetrics?.cpm ?? null
            : null,
          videoViews: hasCanonicalAllDelivery
            ? canonicalAllTotals.videoViews
            : hasResolvedChannels
            ? resolvedMetrics?.videoViews ?? null
            : null,
          /*
           * The campaign-level video KPI, taken from whichever layer resolved
           * it and never assembled here. `resolveReportingTruth` folds it
           * through the same registry the canonical rollup uses, so the two
           * branches cannot disagree about what counts as a qualified view.
           */
          qualifiedVideoViews: hasCanonicalAllDelivery
            ? canonicalAllTotals.qualifiedVideoViews
            : hasResolvedChannels
            ? resolvedMetrics?.qualifiedVideoViews ?? null
            : null,
          qualifiedVideoExplainer: hasCanonicalAllDelivery
            ? canonicalAllTotals.qualifiedVideoExplainer
            : hasResolvedChannels
            ? resolvedMetrics?.qualifiedVideo?.explainer ?? null
            : null,
          engagements: hasCanonicalAllDelivery
            ? canonicalAllTotals.engagements
            : hasResolvedChannels
            ? resolvedMetrics?.engagements ?? null
            : null,
          completions: hasCanonicalAllDelivery
            ? canonicalAllTotals.completions
            : hasCompletions
            ? totalCompletions
            : null,
          completionRate: hasCanonicalAllDelivery ? canonicalAllTotals.completionRate : completionRate,
          ticketSales:
            typeof canonicalAllTotals.ticketSales === "number"
              ? canonicalAllTotals.ticketSales
              : typeof resolvedMetrics?.ticketSales === "number"
              ? resolvedMetrics.ticketSales
              : null,
          costPerQualifiedVideoView: canonicalAllTotals.costPerQualifiedVideoView ?? null,
          spendEfficiency: canonicalAllTotals.spendEfficiency ?? null,
          qualifiedVideoSpendBasisLabel:
            canonicalAllTotals.qualifiedVideoSpendBasisLabel ?? null,
          qualifiedVideoComplete: canonicalAllTotals.qualifiedVideoComplete ?? null,
        },
        byPlatform,
        byPlatformDataSources,
        attributionByPlatform,
        canonicalReportingComplete,
        optimusGrading: allSnapshot?.optimusGrading || null,
        sources: { usedLiveMeta, usedIngested, liveReason },
      };
    },
    [platformTabs, loadSnapshot, tryLoadLiveMetaTotals]
  );

  const buildMetricsForPlatformRef = useRef(buildMetricsForPlatform);
  const buildAllPlatformsTotalsRef = useRef(buildAllPlatformsTotals);
  const buildClientSafeMetricsPayloadRef = useRef(buildClientSafeMetricsPayload);
  const loadSavedRecommendationsRef = useRef(loadSavedRecommendations);
  const resetMetricsAndRecsRef = useRef(resetMetricsAndRecs);

  useEffect(() => {
    buildMetricsForPlatformRef.current = buildMetricsForPlatform;
    buildAllPlatformsTotalsRef.current = buildAllPlatformsTotals;
    buildClientSafeMetricsPayloadRef.current = buildClientSafeMetricsPayload;
    loadSavedRecommendationsRef.current = loadSavedRecommendations;
    resetMetricsAndRecsRef.current = resetMetricsAndRecs;
  });

  // -----------------------------
  // Date range resolver
  // -----------------------------
  const resolvedRange = useMemo(
    () =>
      resolveOptimizerRange({
        presetMode: currentPreset.mode,
        presetDatePreset: currentPreset.datePreset,
        flightRange: getFlightRange,
      }),
    [currentPreset, getFlightRange]
  );

  // A FLIGHT selection whose approved flight will not resolve is a governed
  // stop, not a licence to evaluate some other window.
  //
  // While the contract request is still in flight the page is not yet in a
  // failure state — it simply does not know yet. Showing the red hold there was
  // a lie of timing, and it is the reason the message read as permanent for a
  // campaign whose flight resolves perfectly well a moment later.
  /*
   * The page's own state, resolved once and shared with the sibling portal.
   *
   * Derived in two steps because the flight hold both READS the lifecycle and
   * CONTRIBUTES the only error it can reach: settled-ness is established first,
   * the hold is judged against it, and an unresolvable flight then promotes the
   * lifecycle to ERROR. Folding those together would be circular.
   */
  const baseOptimusLifecycle = resolveOptimusLifecycle({
    advertiserId: selectedAdvertiserId,
    selectionsLoading: planCampaignsLoading || campaignsLoading,
    campaignSelected: Boolean(effectiveSelectedCampaignId),
    contractState: campaignContractState.state,
    reportLoading: overviewLoading,
    hasReport: hasPerformanceOverviewStats,
  });

  /*
   * "Not known yet" and "known, and unresolvable" are different states, and
   * only the second is a failure. PENDING holds the request without erroring;
   * UNRESOLVED is the governed stop that earns the red banner.
   */
  const flightHold = resolveFlightHold({
    rangeMode: resolvedRange.mode,
    rangeResolved: resolvedRange.resolved,
    lifecycle: baseOptimusLifecycle,
  });
  const isFlightPending = flightHold.pending;
  const isFlightUnresolved = flightHold.unresolved;

  const optimusLifecycle = isFlightUnresolved
    ? OPTIMUS_LIFECYCLE.ERROR
    : baseOptimusLifecycle;

  /*
   * The hold names its own cause.
   *
   * A structured diagnostic from the contract says which campaign, which field
   * and why; only a genuinely unattributable hold falls back to the generic
   * sentence.
   */
  const flightUnresolvedNotice = !isFlightUnresolved
    ? ""
    : describeOptimusContractDiagnostic(activeContractDiagnostic) ||
      "Flight selected, but this campaign's approved flight dates could not be resolved. Optimizations are held rather than run against a different window. Choose 30D / 14D / 7D to run an explicit range.";

  const optimusScopeKey = useMemo(
    () =>
      buildOptimusScopeKey({
        advertiserId: selectedAdvertiserId,
        campaignId: effectiveSelectedCampaignId,
        platform: selectedPlatformTab || "all",
        range: resolvedRange,
      }),
    [effectiveSelectedCampaignId, resolvedRange, selectedAdvertiserId, selectedPlatformTab]
  );
  const platformTabsKey = useMemo(
    () =>
      (Array.isArray(platformTabs) ? platformTabs : [])
        .map((tab) => normalize(tab?.id))
        .filter(Boolean)
        .sort()
        .join("|"),
    [platformTabs]
  );
  const overviewRequestKey = useMemo(
    () => `${optimusScopeKey || "no-scope"}|tabs:${platformTabsKey}`,
    [optimusScopeKey, platformTabsKey]
  );

  useEffect(() => {
    currentOptimusScopeRef.current = optimusScopeKey;
  }, [optimusScopeKey]);

  // -----------------------------
  // Reactive: overview metrics + saved recs
  // -----------------------------
  useEffect(() => {
    const controller = new AbortController();
    const scopeKey = optimusScopeKey;
    const requestKey = overviewRequestKey;

    if (!selectedAdvertiserId) {
      currentOptimusScopeRef.current = scopeKey;
      overviewRequestStateRef.current = { inFlightKey: "", settledKey: "" };
      resetMetricsAndRecsRef.current();
      setIsLoading(false);
      setOverviewLoading(false);
      return () => controller.abort();
    }

    /*
     * Flight requested and not yet knowable: hold WITHOUT erroring. Issuing the
     * request here would query a different window under a FLIGHT label, which
     * is the very thing this hold exists to prevent, and publishing an error
     * here is what produced the red flash on first paint.
     */
    if (isFlightPending) {
      currentOptimusScopeRef.current = scopeKey;
      overviewRequestStateRef.current = { inFlightKey: "", settledKey: "" };
      resetMetricsAndRecsRef.current();
      setIsLoading(false);
      /* Neutral loading, not a failure: the page does not know yet. */
      setOverviewLoading(true);
      setApiError("");
      return () => controller.abort();
    }

    // Flight requested but unresolvable: show nothing rather than show another
    // window's KPIs under a FLIGHT label.
    if (isFlightUnresolved) {
      currentOptimusScopeRef.current = scopeKey;
      overviewRequestStateRef.current = { inFlightKey: "", settledKey: "" };
      resetMetricsAndRecsRef.current();
      setIsLoading(false);
      setOverviewLoading(false);
      setApiError(flightUnresolvedNotice);
      return () => controller.abort();
    }

    if (
      overviewRequestStateRef.current.inFlightKey === requestKey ||
      overviewRequestStateRef.current.settledKey === requestKey
    ) {
      return () => controller.abort();
    }

    const requestSeq = optimusRequestSeqRef.current + 1;
    optimusRequestSeqRef.current = requestSeq;
    currentOptimusScopeRef.current = scopeKey;
    const isCurrentRequest = () =>
      !controller.signal.aborted &&
      currentOptimusScopeRef.current === scopeKey &&
      optimusRequestSeqRef.current === requestSeq &&
      overviewRequestStateRef.current.inFlightKey === requestKey;

    overviewRequestStateRef.current.inFlightKey = requestKey;
    resetMetricsAndRecsRef.current();
    setIsLoading(false);
    setOverviewLoading(true);
    setKpis((prev) => ({ ...prev, activePlatformsLabel: platformLabel(selectedPlatformTab || "all") }));

    const platform = selectedPlatformTab || "all";

    (async () => {
      const { datePreset, startDate, endDate } = resolvedRange;
      try {

      if (platform === "all") {
        const built = await buildAllPlatformsTotalsRef.current({
          advertiserId: selectedAdvertiserId,
          campaignId: effectiveSelectedCampaignId,
          datePreset,
          startDate,
          endDate,
          signal: controller.signal,
        });

        if (!isCurrentRequest()) return;

        const totals = built?.totals || {};
        setOptimusGrading(built?.optimusGrading || null);
        setKpis((prev) => ({
          ...prev,
          currency: totals.currency ?? null,
          spendBasis: totals.spendBasis ?? null,
          financialAuthority: totals.financialAuthority ?? null,
          reportingEvidence: totals.reportingEvidence ?? null,
          totalSpend: totals.spend ?? null,
          billedSpend: totals.billedSpend ?? null,
          clientFacingSpend: totals.clientFacingSpend ?? null,
          impressions: totals.impressions ?? null,
          clicks: totals.clicks ?? null,
          blendedCtr: totals.ctr ?? null,
          blendedCpm: totals.cpm ?? null,
          videoViews: totals.videoViews ?? null,
          engagements: totals.engagements ?? null,
          engagementRate: totals.engagementRate ?? null,
          videoViewRate: totals.videoViewRate ?? null,
          completions: totals.completions ?? null,
          completionRate: totals.completionRate ?? null,
          ticketSales: totals.ticketSales ?? null,
          qualifiedVideoViews: totals.qualifiedVideoViews ?? null,
          costPerQualifiedVideoView: totals.costPerQualifiedVideoView ?? null,
          qualifiedVideoSpendBasisLabel:
            totals.qualifiedVideoSpendBasisLabel ?? null,
          qualifiedVideoComplete: totals.qualifiedVideoComplete ?? null,
          canonicalReportingComplete: built?.canonicalReportingComplete ?? null,
          activePlatformsLabel: platformLabel("all"),
          totalSpendAll: typeof totals.spend === "number" ? totals.spend : null,
          spendSharePct: typeof totals.spend === "number" ? 100 : null,
          byPlatformMetrics: built?.byPlatform || {},
          byPlatformAttribution: built?.attributionByPlatform || {},
          byPlatformDataSources: built?.byPlatformDataSources || {},
        }));

        const rawCurrentMetrics = {
          spend: totals.spend ?? null,
          impressions: totals.impressions ?? null,
          clicks: totals.clicks ?? null,
          ctr: totals.ctr ?? null,
          cpm: totals.cpm ?? null,
          videoViews: totals.videoViews ?? null,
          engagements: totals.engagements ?? null,
          engagementRate: totals.engagementRate ?? null,
          videoViewRate: totals.videoViewRate ?? null,
          completions: totals.completions ?? null,
          completionRate: totals.completionRate ?? null,
          ticketSales: totals.ticketSales ?? null,
        };
        const currentMetrics = buildClientSafeMetricsPayloadRef.current({
          platform: "all",
          metrics: rawCurrentMetrics,
          allContext: built,
        });

        if (built?.sources?.usedLiveMeta) {
          setOverviewSourceLabel("Current report window");
        } else if (built?.sources?.usedIngested) {
          setOverviewSourceLabel("Current report window");
        } else {
          setOverviewSourceLabel("Current report window");
        }

        await loadSavedRecommendationsRef.current({
          advertiserId: selectedAdvertiserId,
          campaignId: effectiveSelectedCampaignId,
          platform,
          datePreset,
          startDate,
          endDate,
          signal: controller.signal,
          scopeKey,
          requestSeq,
          currentMetrics,
        });
        return;
      }

      const built = await buildMetricsForPlatformRef.current({
        platform,
        advertiserId: selectedAdvertiserId,
        campaignId: effectiveSelectedCampaignId,
        datePreset,
        startDate,
        endDate,
        signal: controller.signal,
      });

      const metrics = built?.metrics || {};

      const builtAll = await buildAllPlatformsTotalsRef.current({
        advertiserId: selectedAdvertiserId,
        campaignId: effectiveSelectedCampaignId,
        datePreset,
        startDate,
        endDate,
        signal: controller.signal,
      });

      const currentMetrics = buildClientSafeMetricsPayloadRef.current({
        platform,
        metrics: {
          ...metrics,
          ticketSales: builtAll?.totals?.ticketSales ?? null,
        },
        allContext: builtAll,
      });
      const allMetricsPayload = buildClientSafeMetricsPayloadRef.current({
        platform: "all",
        metrics: builtAll?.totals || {},
        allContext: builtAll,
      });
      const allSpend = allMetricsPayload?.spend ?? null;
      const spendSharePct =
        typeof allSpend === "number" && typeof currentMetrics.spend === "number" && (!normalizeProviderSurfaceKey(platform) || metrics.currency === "USD")
          ? computeSpendSharePct(currentMetrics.spend, allSpend)
          : null;

      if (!isCurrentRequest()) return;

      // Selected-platform grading must remain in its own scope, including unavailable replies.
      setOptimusGrading(built?.optimusGrading || null);

      setKpis((prev) => ({
        ...prev,
        currency: metrics.currency ?? null,
        spendBasis: metrics.spendBasis ?? null,
        financialAuthority: metrics.financialAuthority ?? null,
        reportingEvidence: metrics.reportingEvidence ?? null,
        totalSpend: metrics.spend ?? null,
        billedSpend: metrics.billedSpend ?? null,
        clientFacingSpend: metrics.clientFacingSpend ?? null,
        impressions: metrics.impressions ?? null,
        clicks: metrics.clicks ?? null,
        blendedCtr: metrics.ctr ?? null,
        blendedCpm: metrics.cpm ?? null,
        videoViews: metrics.videoViews ?? null,
        engagements: metrics.engagements ?? null,
        engagementRate: metrics.engagementRate ?? null,
        videoViewRate: metrics.videoViewRate ?? null,
        completions: metrics.completions ?? null,
        completionRate: metrics.completionRate ?? null,
        ticketSales: builtAll?.totals?.ticketSales ?? null,
        activePlatformsLabel: platformLabel(platform),
        totalSpendAll: typeof allSpend === "number" ? allSpend : prev.totalSpendAll,
        spendSharePct,
        byPlatformMetrics: builtAll?.byPlatform || {},
        byPlatformAttribution: builtAll?.attributionByPlatform || {},
        byPlatformDataSources: builtAll?.byPlatformDataSources || {},
      }));

      setOverviewSourceLabel("Current report window");

      await loadSavedRecommendationsRef.current({
        advertiserId: selectedAdvertiserId,
        campaignId: effectiveSelectedCampaignId,
        platform,
        datePreset,
        startDate,
        endDate,
        signal: controller.signal,
        scopeKey,
        requestSeq,
        currentMetrics,
      });
      } catch (err) {
        if (err?.name !== "AbortError" && isCurrentRequest()) {
          console.warn("[Client Optimus] Performance overview load failed:", err);
        }
      } finally {
        if (isCurrentRequest()) {
          overviewRequestStateRef.current = { inFlightKey: "", settledKey: requestKey };
          setOverviewLoading(false);
        }
      }
    })();

    return () => {
      controller.abort();
      if (overviewRequestStateRef.current.inFlightKey === requestKey) {
        overviewRequestStateRef.current.inFlightKey = "";
      }
    };
  }, [
    selectedAdvertiserId,
    effectiveSelectedCampaignId,
    selectedPlatformTab,
    resolvedRange,
    resolvedRange.datePreset,
    resolvedRange.endDate,
    resolvedRange.startDate,
    isFlightPending,
    isFlightUnresolved,
    flightUnresolvedNotice,
    optimusScopeKey,
    overviewRequestKey,
  ]);




  // -----------------------------
  // Bench helpers
  // -----------------------------
  /*
   * Both resolution sites now normalize through the canonical utility. The
   * pre-Phase-1 code normalized here but not below, so the two disagreed.
   */
  const benchPlatformKey = useMemo(
    () => normalizeOptimusPlatformKey(selectedPlatformTab || "all"),
    [selectedPlatformTab]
  );

  const selectedBackendPlatformAssessment = useMemo(
    () =>
      benchPlatformKey === "all"
        ? null
        : (optimusGrading?.platforms || []).find(
            (platform) => platform?.platform === benchPlatformKey
          ) || null,
    [benchPlatformKey, optimusGrading]
  );

  const backendMetricStatuses = useMemo(() => {
    const comparisons =
      benchPlatformKey === "all"
        ? optimusGrading?.campaignForecastComparisons || []
        : selectedBackendPlatformAssessment?.forecastComparisons || [];
    const status = (metric, forecastMetric = metric) => {
      const excluded = selectedBackendPlatformAssessment?.excludedFromGrade?.find(
        (entry) => entry?.metric === metric
      );
      if (excluded?.state === "NOT_APPLICABLE") {
        return { label: "Not applicable", tone: "neutral", state: "NOT_APPLICABLE" };
      }
      const comparison = comparisons.find((entry) => entry?.metric === forecastMetric);
      if (!comparison) {
        /*
         * "No approved target" read as a defect in the approved plan. The
         * actual condition is that no approved FORECAST has been persisted for
         * this campaign -- true for every campaign on the platform today --
         * which is an absence of a stored expectation, not a planning failure.
         */
        return { label: "Benchmark not established", tone: "neutral", state: "NO_BENCHMARK" };
      }
      if (comparison.actual === null || comparison.state === "NOT_MEASURED") {
        return { label: "Not measured", tone: "neutral", state: "NOT_MEASURED" };
      }
      if (comparison.verdict === "ABOVE_OR_ON_FORECAST") {
        return { label: "At/above approved forecast", tone: "good", state: comparison.state };
      }
      if (comparison.verdict === "NEAR_FORECAST") {
        return { label: "Near approved forecast", tone: "ok", state: comparison.state };
      }
      if (comparison.verdict === "BELOW_FORECAST") {
        return { label: "Below approved forecast", tone: "bad", state: comparison.state };
      }
      return { label: "Benchmark not established", tone: "neutral", state: comparison.state };
    };
    return {
      ctr: status("ctr"),
      cpm: status("cpm", "plannedCpm"),
      completionRate: status("completionRate"),
    };
  }, [benchPlatformKey, optimusGrading, selectedBackendPlatformAssessment]);

  const ctrStatus = backendMetricStatuses.ctr;
  const cpmStatus = backendMetricStatuses.cpm;

  /*
   * The backend-owned blended eCPM benchmark, presented through a contract
   * mirrored byte-for-byte with the other portal so the two can never word the
   * same verdict differently. The portal derives nothing: the authority tier,
   * the arithmetic and the verdict all come from the server, so a derived
   * planned blend is never shown as an approved target.
   */
  const blendedEcpmBenchmark = optimusGrading?.blendedEcpmBenchmark || null;
  const blendedCpmStatus = useMemo(
    () => (isAllPlatformsView && blendedEcpmBenchmark ? resolveBlendedEcpmBadge(blendedEcpmBenchmark) : null),
    [blendedEcpmBenchmark, isAllPlatformsView]
  );
  /*
   * The blended benchmark answers the ALL view; a single-channel tab still
   * uses the per-platform approved-forecast comparison.
   */
  const effectiveCpmStatus = blendedCpmStatus || cpmStatus;
  const blendedEcpmSubline = useMemo(
    () => resolveBlendedEcpmSubline(blendedEcpmBenchmark, { formatMoney: formatCurrency }),
    [blendedEcpmBenchmark]
  );
  const blendedEcpmUnavailableReason = useMemo(
    () => resolveBlendedEcpmUnavailableReason(blendedEcpmBenchmark),
    [blendedEcpmBenchmark]
  );
  /*
   * The decomposition is what stops the blended number being read as a verdict
   * on the whole media architecture: one channel can be beating its planned
   * rate while another drives the aggregate above plan.
   */
  const blendedEcpmDecomposition = useMemo(
    () => resolveBlendedEcpmDecomposition(blendedEcpmBenchmark),
    [blendedEcpmBenchmark]
  );
  /*
   * The spend card renders the SAME pacing status the grade hero does,
   * resolved once in the shared utility. It used to re-derive its own
   * label from the raw pacing score on private thresholds, so a campaign
   * 13% ahead of the approved curve scored 87 and read "Ahead" at the top
   * of the page and "Pacing requires review" in this card — one number,
   * two contradictory verdicts.
   */
  const pacingStatus = useMemo(
    () => resolveOptimusPacingStatus(optimusGrading),
    [optimusGrading]
  );

  /*
   * A canonical rate WINS over the local whole-scope division: the snapshot
   * rollup's engagementRate/videoViewRate are eligibility-scoped (only the
   * channels that report the evidence divide), and recomputing them here from
   * summed totals would silently re-blend non-reporting delivery back in.
   * The local math survives only for payloads carrying no canonical rate.
   */
  /*
   * No local division left.
   *
   * The fallback divided the engagement count by the campaign's IMPRESSIONS,
   * and those are two different populations the moment any inventory in the
   * mix cannot be engaged with. Now that the count is the governed pair's —
   * YouTube's ad interactions — dividing it by CTV and Display screens as
   * well would restate the exact dilution the pair exists to remove: 43,399
   * over 5.24M reads 0.83% for a platform whose real rate is 29.79%.
   *
   * The canonical rate is co-scoped with the count by construction. When it
   * is absent the honest answer is that no compatible aggregate exists, and
   * the card says so rather than inventing one.
   */
  const engagementRate = useMemo(
    () => resolveGovernedRate(kpis.engagementRate, null),
    [kpis.engagementRate]
  );

  const viewRate = useMemo(
    () =>
      resolveGovernedRate(kpis.videoViewRate, () => {
        if (typeof kpis.videoViews !== "number" || typeof kpis.impressions !== "number" || kpis.impressions <= 0)
          return null;
        return (kpis.videoViews / kpis.impressions) * 100;
      }),
    [kpis.videoViewRate, kpis.videoViews, kpis.impressions]
  );

  const shouldShowCompletionCards = useMemo(() => {
    const p = normalize(selectedPlatformTab || "all");
    if (p === "ctv" || p === "video" || p === "youtube" || p === "rumble") return true;
    if (typeof kpis.completions === "number" || typeof kpis.completionRate === "number") return true;
    return false;
  }, [selectedPlatformTab, kpis.completions, kpis.completionRate]);

  /*
   * The qualified total is a CROSS-PLATFORM claim, so it heads the video card
   * only on the ALL view and only when the registry actually resolved one. A
   * single-platform tab has one measurement and states it natively.
   */
  const showQualifiedVideoHeadline = useMemo(
    () => isAllPlatformsView && typeof kpis.qualifiedVideoViews === "number",
    [isAllPlatformsView, kpis.qualifiedVideoViews]
  );

  /*
   * The headline never travels without its provenance. The sentence is the
   * one the canonical basis generated from the signal registry — which
   * platforms contributed, and what each calls its own signal — so it stays
   * correct when the campaign's mix changes. There is deliberately no
   * platform branching here: a `=== "ctv"` test in this file would be a
   * second copy of a ruling that lives in the registry.
   */
  /*
   * CPQV is a cross-platform claim for the same reason the qualified total is:
   * its numerator is the spend of every platform that carried a qualified
   * signal, so it only means anything where those platforms are all in scope.
   * On a single-platform tab the campaign-level cost would sit beside one
   * platform's counts and read as that platform's cost — the same scoping trap
   * the qualified headline already refuses.
   */
  const showCostPerQualifiedVideoView = useMemo(
    () => isAllPlatformsView && typeof kpis.costPerQualifiedVideoView === "number",
    [isAllPlatformsView, kpis.costPerQualifiedVideoView]
  );

  const qualifiedVideoBasisNote = useMemo(() => {
    if (!showQualifiedVideoHeadline) return "";
    const explainer = String(kpis.qualifiedVideoExplainer || "").trim();
    return explainer || "Governed cross-platform video, blended by declared signal";
  }, [showQualifiedVideoHeadline, kpis.qualifiedVideoExplainer]);

  const pillClass = getOptimusPillClass;



  // -----------------------------
  // Targeting & Flight Specs resolver
  // -----------------------------
  const activeTargetingSpec = useMemo(() => {
    const key = normalize(selectedPlatformTab || "all");
    const provider = normalizeProviderSurfaceKey(key);
    const base = provider === "yahoo_dsp" || provider === "viant"
      ? { objective: "Approved campaign objective", optimizationGoal: "Recommendations unavailable", billing: "Governed billing evidence required", audience: "Approved plan", geo: "Approved plan", placements: "Approved inventory channels", devices: "Approved plan", frequency: "Approved plan", notes: "Saved reporting does not establish provider verification or enable optimization actions." }
      : TARGETING_SPECS_BY_PLATFORM[key] || TARGETING_SPECS_BY_PLATFORM.all;
    const planSpec = resolveApprovedPlanSpecFields(selectedPlanInput || plannerCampaignContextInput);

    const objective = formatGoalLabel(
      planSpec.objective || resolvedPlannerObjective || base.objective || "—"
    );

    const profile = buildChannelProfileForPrompt(key);
    const fallbackKpisText =
      Array.isArray(profile?.primaryKPIs) && profile.primaryKPIs.length
        ? profile.primaryKPIs.map((x) => String(x)).join(", ")
        : "—";
    const kpisText = planSpec.primaryKPIs || fallbackKpisText;

    return {
      ...base,
      objective,
      flight: optimusFlightLabel,
      primaryKPIs: kpisText,
      optimizationGoal: planSpec.optimizationGoal || base.optimizationGoal,
      billing: planSpec.billing || base.billing,
      audience: planSpec.audience || base.audience,
      geo: planSpec.geo || base.geo,
      placements: planSpec.placements || base.placements,
      devices: planSpec.devices || base.devices,
      frequency: planSpec.frequency || base.frequency,
      notes: planSpec.notes || base.notes,
      platformLabel: platformLabel(key),
      planSourceLabel: resolvedPlannerSourceLabel,
    };
  }, [
    selectedPlatformTab,
    selectedPlanInput,
    plannerCampaignContextInput,
    resolvedPlannerObjective,
    resolvedPlannerSourceLabel,
    optimusFlightLabel,
  ]);

  /*
   * Strategy roles and forecast comparisons arrive from the backend-owned
   * Optimus contract. The portal does not project benchmark bands or infer a
   * channel role from a platform stereotype.
   */
  const optimusPageAwareness = useMemo(() => {
    const platformKey = normalize(selectedPlatformTab || "all") || "all";
    const platformDisplay = platformLabel(platformKey);
    const timeframeLabel =
      resolvedRange.mode === "flight"
        ? formatFlightRange(resolvedRange.startDate, resolvedRange.endDate) || "Flight"
        : currentRangeLabel;

    return {
      routeKey: "/optimus",
      pageTitle: "Optimus",
      pageType: "optimus",
      advertiserId: selectedAdvertiserId ?? null,
      advertiserName: selectedAdvertiserName || getAdvertiserRecord?.name || "",
      advertiserVertical: resolvedAdvertiserVertical,
      advertiserVerticalContext: serializeVerticalIntelligence(resolvedAdvertiserVertical),
      campaignId: effectiveSelectedCampaignId,
      campaignName: selectedCampaignNameForContext || "",
      timeframe: {
        label: timeframeLabel,
        start: resolvedRange.startDate || null,
        end: resolvedRange.endDate || null,
        datePreset: resolvedRange.datePreset || null,
      },
      platformKey,
      platformLabel: platformDisplay,
      pageCapabilities: [
        "optimization recommendations",
        "pacing read",
        "grade interpretation",
        "platform efficiency",
      ],
      toplineMetrics: [
        {
          id: "platform",
          label: "Platform",
          value: platformDisplay,
          meta: "Current optimization scope",
        },
        selectedCampaignNameForContext
          ? {
              id: "campaign",
              label: "Campaign",
              value: selectedCampaignNameForContext,
              meta: "Active campaign filter",
            }
          : null,
        {
          id: "window",
          label: "Window",
          value: timeframeLabel,
          meta: overviewSourceLabel || "Current range",
        },
        clientDisplaySpend != null
          ? {
              id: "spend",
              label: "Billed Spend",
              value: formatSelectedSpend(clientDisplaySpend),
              meta: "Client-billed spend",
            }
          : null,
        kpis.blendedCtr != null
          ? {
              id: "ctr",
              label: "CTR",
              value: formatPercentage(kpis.blendedCtr),
              meta: "Blended performance",
            }
          : null,
        clientFacingEfficiencyCpm != null
          ? {
              id: "cpm",
              label: clientFacingCpmLabel,
              value: formatSelectedSpend(clientFacingEfficiencyCpm),
              meta: isAllPlatformsView ? "Aggregate efficiency" : "Current delivery efficiency",
            }
          : null,
      ].filter(Boolean),
      metadata: {
        recommendationsCount: recommendations.length,
        apiError,
      },
    };
  }, [
    apiError,
    currentRangeLabel,
    effectiveSelectedCampaignId,
    getAdvertiserRecord,
    clientFacingCpmLabel,
    clientFacingEfficiencyCpm,
    kpis.blendedCtr,
    clientDisplaySpend,
    formatSelectedSpend,
    isAllPlatformsView,
    overviewSourceLabel,
    recommendations.length,
    resolvedRange.datePreset,
    resolvedRange.endDate,
    resolvedRange.mode,
    resolvedRange.startDate,
    selectedAdvertiserId,
    selectedAdvertiserName,
    resolvedAdvertiserVertical,
    selectedCampaignNameForContext,
    selectedPlatformTab,
  ]);

  usePageAwareness(optimusPageAwareness);

  // -----------------------------
  // ✅ Optimus Grade — backend-owned premium presentation
  // -----------------------------
  const optimusGrade = useMemo(() => {
    const backend = optimusGrading?.presentation;
    if (backend && typeof backend === "object" && backend.hero && typeof backend.hero === "object") {
      return {
        backendOwned: true,
        hero: backend.hero,
        dimensionCards: Array.isArray(backend.dimensionCards) ? backend.dimensionCards : [],
        planVsPerformance:
          backend.planVsPerformance && typeof backend.planVsPerformance === "object"
            ? backend.planVsPerformance
            : null,
        insights:
          backend.insights && typeof backend.insights === "object"
            ? backend.insights
            : { opportunities: [], watchouts: [] },
        gradingDetails:
          backend.gradingDetails && typeof backend.gradingDetails === "object"
            ? backend.gradingDetails
            : null,
        methodologyVersion: backend.methodologyVersion || optimusGrading?.methodologyVersion || null,
      };
    }

    /* Fail closed: no portal-side benchmark math may manufacture a grade. */
    return {
      backendOwned: false,
      hero: {
        state: "GRADE_PENDING",
        kicker: "Optimus Grade",
        grade: null,
        score: null,
        headline: "Grade pending",
        summary: [],
        pendingReason:
          "Canonical grading evidence is unavailable for this view. No browser fallback is used.",
        confidence: null,
        gradeBasis: null,
        tone: "neutral",
        campaignState: null,
      },
      dimensionCards: [],
      planVsPerformance: null,
      insights: { opportunities: [], watchouts: [] },
      gradingDetails: null,
      methodologyVersion: null,
    };
  }, [optimusGrading]);

  const strategyRecommendations = useMemo(
    () =>
      (Array.isArray(optimusGrading?.recommendations)
        ? optimusGrading.recommendations
        : []
      ).filter((recommendation) => {
        if (normalizeProviderSurfaceKey(selectedPlatformTab)) return false;
        return !normalizeProviderSurfaceKey(recommendation.platform);
      }).map((recommendation, index) => ({
        id: `${recommendation.platform || "all"}:${recommendation.metric || "review"}:${index}`,
        category: String(recommendation.type || "Diagnostic Review")
          .replace(/_/g, " ")
          .toLowerCase()
          .replace(/\b\w/g, (c) => c.toUpperCase()),
        title: `${platformLabel(recommendation.platform || "all")} · ${recommendation.metricLabel || recommendation.metric || "Strategy attainment"}`,
        summary: recommendation.rationale,
        /*
         * The evidence line arrives ALREADY FORMATTED from the governed
         * comparison contract (optimus_comparison_semantics_v1) — currency at
         * two decimals for money, grouped counts for volume. The portal
         * prints it; it does not re-format the raw operands beside it, which
         * is how "$65.408" reached a client screen. When the backend cannot
         * establish the comparison there is no line to print — no fallback
         * formatting, no fabricated relationship.
         */
        evidence: recommendation.evidence?.evidenceDisplay || null,
        sourceLabel: "Backend strategy-attainment contract",
        dataWindow: optimusGrading?.canonicalEvidence?.timeframe
          ? `${optimusGrading.canonicalEvidence.timeframe.start || "—"} – ${optimusGrading.canonicalEvidence.timeframe.end || "—"}`
          : null,
        generatedAt: optimusGrading?.canonicalEvidence?.sourceTimestamp || null,
      })),
    [optimusGrading, selectedPlatformTab]
  );

  return (
    <div className="optx-page-container">
      <div className="optx-page-content">
        <PoweredByLogo
          alt="Powered by Cphere"
          className="optx-poweredby-logo"
          onClick={() => navigate("/dashboard")}
        />

        <div className="cphere-governed-hero-selector-stack">
          <div className="optx-hero-container optx-dashboard-masthead">
            <div className="optx-hero-pill optx-dashboard-masthead__pill">
              <span className="optx-hero-label optx-dashboard-masthead__label cphere-hero-pill-wordmark">Campaign</span>
              <span className="optx-hero-tag optx-dashboard-masthead__tag cphere-hero-pill-tag">Optimizer</span>
            </div>
            <div className="optx-hero-shimmer optx-dashboard-masthead__shimmer" />
          </div>

          <div className="cphere-governed-advertiser-selector-stage">
            <AdvertiserIdentityTrigger
              advertiser={currentAdvertiser}
              advertiserName={governedAdvertiserName}
              apiBase={API_BASE_URL}
              disabled={selectorDisabled}
              loading={advertiserCatalogLoading}
              empty={selectorEmpty}
              modalOpen={advertiserSelectorOpen}
              onClick={openAdvertiserSelector}
            />
          </div>
        </div>

        {advertiserSelectorOpen && visibleAdvertisers.length ? (
          <GovernedAdvertiserSelectorModal
            advertisers={visibleAdvertisers}
            selectedAdvertiserId={selectedAdvertiserId}
            selectedAdvertiserName={governedAdvertiserName}
            apiBase={API_BASE_URL}
            onSelect={handleAdvertiserSelect}
            onClose={closeAdvertiserSelector}
          />
        ) : null}

        <div className="optx-form-container">
          <div className="optx-controls">
            <div className="optx-controls-row optx-controls-row--campaign-only">
              <div className="optx-control-group">
                <label className="optx-label">
                  Campaign
                  {/*
                    * The campaign is only optional when the advertiser has none
                    * to choose. With eligible campaigns present, Campaign
                    * Optimizer optimizes against a governed campaign.
                    */}
                  {hasCampaignOptions ? null : (
                    <span style={{ opacity: 0.8 }}> (Optional)</span>
                  )}
                  {campaignsLoading ? " (Loading…)" : ""}
                </label>
                <div className="optx-select-shell">
                  <select
                    className="optx-select"
                    value={selectedCampaignId}
                    onChange={(e) => setSelectedCampaignId(e.target.value)}
                    disabled={!selectedAdvertiserId || campaignsLoading}
                  >
                    {/*
                      * The prompt row exists only while nothing is selected.
                      * Governed auto-selection persists the campaign's own id,
                      * so once it lands this row disappears rather than sitting
                      * alongside the campaign it resolved to.
                      */}
                    {selectedCampaignId === "auto" ? (
                      <option value="auto">{autoCampaignOptionLabel}</option>
                    ) : null}

                    {/*
                      * Advertiser-level totals are a different analytical SCOPE,
                      * not a campaign. They stay available as their own
                      * deliberate capability — never as the default, and never
                      * as the stand-in for a campaign lookup that failed — but
                      * they are not offered when a single governed campaign
                      * makes the optimization target unambiguous. There is
                      * nothing to disambiguate, so the extra row only competes
                      * with the campaign-specific path.
                      */}
                    {!hasCampaignOptions ? (
                      <option value="">
                        {!selectedAdvertiserId
                          ? "Select advertiser first"
                          : campaignsError
                            ? "Campaign list unavailable"
                            : "No campaigns available"}
                      </option>
                    ) : campaigns.length > 1 ? (
                      <option value="">Advertiser totals (no campaign)</option>
                    ) : null}

                    {campaigns.map((camp) => (
                      <option key={camp.id} value={camp.id}>
                        {formatCampaignOptionLabel(camp)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* One gate for every error surface: publishable only from a
              settled ERROR lifecycle, never during initialization. */}
          {apiError && canPublishOptimusError(optimusLifecycle) && (
            <div className="optx-error-banner">
              {apiError}
            </div>
          )}

          <div className="optx-layout">
            {/*
              No grading-authority panel here, by decision.

              Which approved plan revision graded which period — its effective
              timestamps, per-revision client-billed plan figure, revision
              identifier and segmentation methodology — is internal governance
              evidence. It stays on superadmin surfaces. The backend still
              authors `optimusGrading.gradingRevisionSelection` and
              `.planRevisionAuthority`, this route still receives them, and the
              grade derived from them still renders below. Only the audit
              spine is withheld.
            */}
            {/* Optimus Grade — hero */}
            <OptimusSectionBoundary name="Optimus Grade">
            <section className="optx-panel optx-grade-hero-panel">
              <div className="optx-grade-hero">
                <div className="optx-grade-hero-top">
                  <div className="optx-grade-hero-kicker">
                    {presentOptimusGradeText(optimusGrade.hero.kicker) || "Optimus Grade"}
                  </div>
                  {optimusGrade.hero.campaignStrategy ? (
                    <div className="optx-grade-state-chips">
                      <span
                        className={`${getOptimusPillClass(optimusGrade.hero.campaignStrategy.tone)} optx-pill-compact optx-grade-authority-pill`}
                      >
                        {presentOptimusGradeText(
                          `${optimusGrade.hero.campaignStrategy.label} (${optimusGrade.hero.campaignStrategy.value})`
                        )}
                      </span>
                    </div>
                  ) : null}
                </div>

                {optimusGrade.hero.state === "GRADED" ? (
                  <div className="optx-grade-hero-main">
                    <div className={`optx-grade-hero-letter optx-grade-letter--${optimusGrade.hero.tone}`}>
                      {optimusGrade.hero.grade}
                    </div>
                    <div className="optx-grade-hero-score">{optimusGrade.hero.score}</div>
                    {optimusGrade.hero.gradeBasisPill ? (
                      <span
                        className={`${getOptimusPillClass(optimusGrade.hero.gradeBasisPill.tone)} optx-pill-compact optx-grade-basis-pill`}
                        title={optimusGrade.hero.gradeBasisPill.detail}
                      >
                        {presentOptimusGradeText(optimusGrade.hero.gradeBasisPill.label)}
                      </span>
                    ) : null}
                    <div className="optx-grade-hero-copy">
                      <div className="optx-grade-hero-headline">
                        {presentOptimusGradeText(optimusGrade.hero.headline)}
                      </div>
                      {(optimusGrade.hero.summary || []).map((line, index) => (
                        <div className="optx-grade-hero-line" key={`hero-line-${index}`}>
                          {presentOptimusGradeText(line)}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="optx-grade-hero-main optx-grade-hero-main--pending">
                    <div className="optx-grade-hero-pending">
                      {presentOptimusGradeText(optimusGrade.hero.headline) || "Grade pending"}
                    </div>
                    {optimusGrade.hero.pendingReason ? (
                      <div className="optx-grade-hero-line">
                        {presentOptimusGradeText(optimusGrade.hero.pendingReason)}
                      </div>
                    ) : null}
                  </div>
                )}

                {optimusGrade.dimensionCards.length ? (
                  <div className="optx-grade-dimensions">
                    {optimusGrade.dimensionCards.map((card, index) =>
                      renderGradeDimensionCard(card, `grade-dimension-${index}`)
                    )}
                  </div>
                ) : null}
              </div>
            </section>
            </OptimusSectionBoundary>

            {/* Targeting & Flight Specs */}
            <OptimusSectionBoundary name="Targeting & Flight Specs">
            <section className="optx-panel">
              <h2 className="optx-panel-title">Targeting &amp; Flight Specs</h2>
              <p className="optx-panel-subtitle">Select a platform tab for channel-specific specs. All Platforms shows totals.</p>

              {!selectedAdvertiserId ? (
                <div className="optx-panel-subtitle">Select an advertiser above.</div>
              ) : visiblePlatformTabs.length === 0 ? (
                <div className="optx-panel-subtitle">
                  No active platform performance is available for this view yet. Active platforms will appear here once campaign signal is available.
                </div>
              ) : (
                <>
                  <div className="optx-platform-tabs">
                    {visiblePlatformTabs.map((tab) => (
                      <button
                        key={tab.id}
                        className={`optx-platform-tab ${normalize(selectedPlatformTab) === normalize(tab.id) ? "is-active" : ""}`}
                        onClick={() => setSelectedPlatformTab(tab.id)}
                        type="button"
                      >
                        <span className="optx-platform-tab-content">
                          {platformIconSrc(tab.id) ? (
                            <img
                              className="optx-platform-tab-icon"
                              src={platformIconSrc(tab.id)}
                              alt=""
                              aria-hidden="true"
                            />
                          ) : null}
                          <span className="optx-platform-tab-label">{tab.label}</span>
                        </span>
                      </button>
                    ))}
                  </div>

                  <div className="optx-targeting-grid">
                    <div className="optx-targeting-card">
                      <div className="optx-targeting-label">Objective</div>
                      <div className="optx-targeting-value">{activeTargetingSpec.objective || "—"}</div>
                    </div>

                    <div className="optx-targeting-card">
                      <div className="optx-targeting-label">Primary KPIs</div>
                      <div className="optx-targeting-value">{activeTargetingSpec.primaryKPIs || "—"}</div>
                    </div>

                    <div className="optx-targeting-card">
                      <div className="optx-targeting-label">Optimization Goal</div>
                      <div className="optx-targeting-value">{activeTargetingSpec.optimizationGoal || "—"}</div>
                    </div>

                    <div className="optx-targeting-card">
                      <div className="optx-targeting-label">Billing</div>
                      <div className="optx-targeting-value">{activeTargetingSpec.billing || "—"}</div>
                    </div>

                    <div className="optx-targeting-card">
                      <div className="optx-targeting-label">Audience</div>
                      <div className="optx-targeting-value">{activeTargetingSpec.audience || "—"}</div>
                    </div>

                    <div className="optx-targeting-card">
                      <div className="optx-targeting-label">Geo</div>
                      <div className="optx-targeting-value">{activeTargetingSpec.geo || "—"}</div>
                    </div>

                    <div className="optx-targeting-card">
                      <div className="optx-targeting-label">Placements</div>
                      <div className="optx-targeting-value">{activeTargetingSpec.placements || "—"}</div>
                    </div>

                    <div className="optx-targeting-card">
                      <div className="optx-targeting-label">Devices</div>
                      <div className="optx-targeting-value">{activeTargetingSpec.devices || "—"}</div>
                    </div>

                    <div className="optx-targeting-card">
                      <div className="optx-targeting-label">Frequency</div>
                      <div className="optx-targeting-value">{activeTargetingSpec.frequency || "—"}</div>
                    </div>

                    <div className="optx-targeting-card">
                      <div className="optx-targeting-label">Flight</div>
                      <div className="optx-targeting-value optx-targeting-value--flight">
                        <div className="optx-flight-dates">
                          <span>{activeTargetingSpec.flight || "—"}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/*
                    * Notes are operator prose and have carried internal
                    * economics: campaign 50's note rendered here as "YouTube:
                    * $3,500 CTV: $5,250 Total Net Media: $8,750", putting net
                    * media in front of a client. The server is the enforcement
                    * point; this is the second line. Cortex has no legitimate
                    * need to display an unlabelled dollar figure -- every
                    * client-billed number on this page comes from a governed
                    * field that carries its basis.
                    */}
                  <div className="optx-targeting-notes">
                    <strong>Sherloc Notes:</strong>{" "}
                    {sanitizeClientFacingNotes(activeTargetingSpec.notes) ||
                      "Select a channel tab to see platform-specific specs."}
                  </div>
                </>
              )}
            </section>
            </OptimusSectionBoundary>

            {/* Performance Overview */}
            <OptimusSectionBoundary name="Performance Overview">
            <section className="optx-panel">
              <div>
                <h2 className="optx-panel-title" style={{ marginBottom: 6 }}>
                  Performance Overview
                </h2>

                <p className="optx-panel-subtitle" style={{ marginTop: 0, marginBottom: 12 }}>
                  KPI totals for selected range • <strong>{currentRangeLabel}</strong>
                  {isFlightUnresolved ? " • flight dates unresolved" : ""}
                </p>

                <div className="optx-platform-tabs" style={{ marginTop: 0, justifyContent: "flex-start" }}>
                  {RANGE_PRESET_RENDER_ORDER.map((key) => {
                    const cfg = RANGE_PRESETS[key];
                    if (!cfg) return null;
                    return (
                    <button
                      key={key}
                      className={`optx-platform-tab ${normalize(overviewRangeKey) === normalize(key) ? "is-active" : ""} ${
                        key === "flight" && isFlightUnresolved ? "is-unresolved" : ""
                      }`}
                      onClick={() => setOverviewRangeKey(key)}
                      type="button"
                      data-range-unresolved={key === "flight" && isFlightUnresolved ? "true" : undefined}
                      title={
                        key === "flight"
                          ? isFlightUnresolved
                            ? flightUnresolvedNotice
                            : "Total Flight (uses the approved campaign flight dates)"
                          : `Totals for ${cfg.label}`
                      }
                    >
                      {cfg.label}
                    </button>
                    );
                  })}
                </div>

                {isFlightUnresolved && (
                  <p className="optx-panel-subtitle optx-range-unresolved-note" role="status" aria-live="polite">
                    {flightUnresolvedNotice}
                  </p>
                )}
              </div>

              <div className="optx-kpi-overview-body" aria-busy={overviewStatsBusy}>
                {showOverviewRefreshIndicator ? (
                  <div className="optx-kpi-refresh-indicator" role="status" aria-live="polite">
                    <span className="optx-kpi-spinner optx-kpi-spinner--small" aria-hidden="true" />
                    <span>Refreshing performance stats…</span>
                  </div>
                ) : null}

                {showInitialOverviewLoading ? (
                  <div className="optx-kpi-loading-state" role="status" aria-live="polite">
                    <span className="optx-kpi-spinner" aria-hidden="true" />
                    <span className="optx-kpi-loading-title">Loading performance overview…</span>
                    <span className="optx-kpi-loading-copy">This can take up to a minute.</span>
                  </div>
                ) : showOverviewNoData ? (
                  <div className="optx-kpi-loading-state optx-kpi-empty-state" role="status" aria-live="polite">
                    <span className="optx-kpi-loading-title">No performance overview data yet.</span>
                    <span className="optx-kpi-loading-copy">
                      Optimus could not find usable stats for this advertiser, campaign, and window after the fetch completed.
                    </span>
                  </div>
                ) : (
                  <div className="optx-kpi-grid">
                    <div className="optx-kpi-card optx-kpi-card--spotlight">
                  <div className="optx-kpi-label">Active View</div>
                  <div className="optx-kpi-value">{kpis.activePlatformsLabel}</div>
                  <div className="optx-panel-subtitle" style={{ marginTop: 6, marginBottom: 0, opacity: 0.9 }}>
                    {selectedCampaignNameForContext
                      ? `Campaign: ${selectedCampaignNameForContext}`
                      : "Campaign: Totals"}
                  </div>
                </div>

                <div className="optx-kpi-card optx-kpi-card--spotlight">
                  <div className="optx-kpi-label">Billed Spend</div>
                  <div className="optx-kpi-value">{formatSelectedSpend(clientDisplaySpend)}</div>
                  <div className={pillClass(pacingStatus.tone)} title={pacingStatus.title}>{pacingStatus.label}</div>
                  <div className="optx-panel-subtitle" style={{ marginTop: 6, marginBottom: 0, opacity: 0.9 }}>
                    {typeof spendComparison.value === "number"
                      ? `${spendComparison.label}: ${formatCurrency(spendComparison.value)}`
                      : !selectedSpendComparableToUsd
                      ? "Currency comparison unavailable"
                      : !isAllPlatformsView
                      ? "Planned platform allocation not found"
                      : "Current report window"}
                  </div>
                </div>

                <div className="optx-kpi-card">
                  <div className="optx-kpi-label">{clientFacingCpmLabel}</div>
                  <div className="optx-kpi-value">{formatSelectedSpend(clientFacingEfficiencyCpm)}</div>
                  <div className={pillClass(effectiveCpmStatus.tone)}>{effectiveCpmStatus.label}</div>
                  <div className="optx-panel-subtitle" style={{ marginTop: 6, marginBottom: 0, opacity: 0.9 }}>
                    {clientFacingEfficiencyCpm == null && clientFacingEfficiencyReasonText
                      ? clientFacingEfficiencyReasonText
                      : isAllPlatformsView
                      ? /*
                         * "Active channels" described the plan; the blend is
                         * over the channels that actually DELIVERED in the
                         * selected range, which is the set the arithmetic
                         * used. The backend owns this sentence so the portal
                         * cannot drift from the contract it describes.
                         */
                        blendedEcpmBenchmark?.description ||
                        "Blended client-billed cost per 1,000 impressions across channels delivering in the selected range."
                      : "Current delivery efficiency"}
                  </div>
                  {isAllPlatformsView && blendedEcpmSubline ? (
                    <div
                      className="optx-panel-subtitle optx-blended-benchmark-note"
                      style={{ marginTop: 4, marginBottom: 0, opacity: 0.75 }}
                    >
                      {blendedEcpmSubline}
                    </div>
                  ) : null}
                  {isAllPlatformsView && blendedEcpmUnavailableReason ? (
                    <div
                      className="optx-panel-subtitle optx-blended-benchmark-note"
                      style={{ marginTop: 4, marginBottom: 0, opacity: 0.7 }}
                    >
                      {blendedEcpmUnavailableReason}
                    </div>
                  ) : null}
                </div>

                <div className="optx-kpi-card">
                  <div className="optx-kpi-label">Impressions</div>
                  <div className="optx-kpi-value">{formatInteger(kpis.impressions)}</div>
                </div>

                <div className="optx-kpi-card">
                  <div className="optx-kpi-label">Clicks</div>
                  <div className="optx-kpi-value">{formatInteger(kpis.clicks)}</div>
                </div>

                <div className="optx-kpi-card">
                  <div className="optx-kpi-label">CTR (Blended)</div>
                  <div className="optx-kpi-value">{formatPercentage(kpis.blendedCtr)}</div>
                  <div className={pillClass(ctrStatus.tone)}>{ctrStatus.label}</div>
                </div>

                {/*
                  * On the ALL view this is the campaign-level qualified total —
                  * CTV completed views plus YouTube TrueView views, blended by
                  * governed declaration. The card previously led with the raw
                  * `videoViews`, which is YouTube's count alone: 31,499 offered
                  * as the video story of a flight that had delivered 196,583
                  * qualified outcomes, with a 24.41% View Rate underneath it
                  * that described only the smaller of the two platforms.
                  *
                  * A single-platform tab keeps the native count and its native
                  * rate; there is no blend to state when there is one source.
                  */}
                <div className="optx-kpi-card">
                  <div className="optx-kpi-label">
                    {showQualifiedVideoHeadline ? "Qualified Video Views" : "Video Views"}
                  </div>
                  <div className="optx-kpi-value">
                    {formatInteger(
                      showQualifiedVideoHeadline ? kpis.qualifiedVideoViews : kpis.videoViews
                    )}
                  </div>
                  <div className="optx-panel-subtitle" style={{ marginTop: 6, marginBottom: 0, opacity: 0.9 }}>
                    {showQualifiedVideoHeadline
                      ? qualifiedVideoBasisNote
                      : `View Rate: ${viewRate === null ? "—" : `${viewRate.toFixed(2)} %`}`}
                  </div>
                </div>

                <div className="optx-kpi-card">
                  <div className="optx-kpi-label">Engagements</div>
                  <div className="optx-kpi-value">{formatInteger(kpis.engagements)}</div>
                  <div className="optx-panel-subtitle" style={{ marginTop: 6, marginBottom: 0, opacity: 0.9 }}>
                    Engage Rate: {engagementRate === null ? "—" : `${engagementRate.toFixed(2)} %`}
                  </div>
                </div>

                {/*
                  * The completion COUNT card left this strip; the rate stayed,
                  * and it still answers to the count.
                  *
                  * The count was the weakest of the three video figures here:
                  * on a CTV+YouTube flight it restates CTV's completed views,
                  * which the qualified total above already contains. What a
                  * campaign-level reader lacks is the price of that video, so
                  * the slot now carries CPQV. The count is still on the
                  * platform rows and is still the numerator this rate names.
                  *
                  * The `typeof kpis.completions === "number"` guard is NOT
                  * vestigial. A completion rate printed beside a count the
                  * page does not have asks a reader to accept 98.41% of an
                  * unstated quantity — the two halves were once resolved on
                  * different platform populations, so one could survive the
                  * other's absence. They are co-scoped upstream now, and this
                  * guard is what keeps the rate from outliving its basis.
                  */}
                {showCostPerQualifiedVideoView && (
                  <div className="optx-kpi-card">
                    <div className="optx-kpi-label">Cost Per Qualified Video View</div>
                    <div className="optx-kpi-value">
                      {formatCostPerCompletedView(kpis.costPerQualifiedVideoView)}
                    </div>
                    <div className="optx-panel-subtitle" style={{ marginTop: 6, marginBottom: 0, opacity: 0.9 }}>
                      {kpis.qualifiedVideoSpendBasisLabel || "Spend basis unavailable"}
                    </div>
                  </div>
                )}

                {shouldShowCompletionCards && (
                  <div className="optx-kpi-card">
                    <div className="optx-kpi-label">Video Completion Rate</div>
                    <div className="optx-kpi-value">
                      {typeof kpis.completions === "number"
                        ? formatPercentage(kpis.completionRate)
                        : "—"}
                    </div>
                  </div>
                )}

                  </div>
                )}

              </div>
            </section>
            </OptimusSectionBoundary>

            {/* Sherloc AI Recommendations */}
            <OptimusSectionBoundary name="Sherloc AI Recommendations">
            <section className="optx-panel optx-sherloc-panel">
              <h2 className="optx-panel-title">Sherloc AI Recommendations</h2>
              <p className="optx-panel-subtitle">
                Read-only diagnostics derived from the current approved strategy, matching forecast, and canonical campaign evidence.
              </p>

              {strategyRecommendations.length === 0 ? (
                <div className="optx-panel-subtitle">
                  {normalizeProviderSurfaceKey(selectedPlatformTab)
                    ? "Provider recommendations and live optimization actions are unavailable. Saved reporting does not establish provider verification or governed optimization eligibility."
                    : "Strategy-attainment analysis is read-only. Optimization remains unavailable unless the backend safety contract explicitly authorizes it."}
                </div>
              ) : (
                <div className="optx-recommendations-list">
                  {strategyRecommendations.map((rec) => (
                    <div className="optx-recommendation-card" key={rec.id}>
                      <div className="optx-recommendation-header">
                        <span>{rec.category}</span>
                        <span>
                          {typeof rec.confidence === "number"
                            ? `Conf: ${rec.confidence.toFixed(2)}`
                            : rec.sourceType === "insufficient_data"
                            ? "No confidence"
                            : ""}
                        </span>
                      </div>
                      <h3 className="optx-recommendation-title">{rec.title}</h3>
                      <p>{rec.summary}</p>
                      {rec.evidence ? (
                        <div className="optx-recommendation-evidence">{rec.evidence}</div>
                      ) : null}
                      <div className="optx-recommendation-meta">
                        <span>{rec.sourceLabel || recsSourceLabel || "Analysis"}</span>
                        {rec.dataWindow ? <span>Window: {rec.dataWindow}</span> : null}
                        {rec.generatedAt ? (
                          <span>Generated: {formatRecommendationGeneratedAt(rec.generatedAt)}</span>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
            </OptimusSectionBoundary>

            {/* Plan vs Performance */}
            <OptimusSectionBoundary name="Plan vs Performance">
            <section className="optx-panel optx-log-panel">
              <h2 className="optx-panel-title">Plan vs Performance</h2>
              <p className="optx-panel-subtitle">
                How execution is tracking the approved plan: allocation, delivery pacing, KPI attainment, and platform roles.
              </p>

              {optimusGrade.planVsPerformance ? (
                <div className="optx-grade-pvp-grid">
                  {optimusGrade.planVsPerformance.allocation?.state === "EVALUATED" ? (
                    <div className="optx-grade-card optx-grade-pvp-card">
                      <div className="optx-grade-card-label">Allocation Adherence</div>
                      {optimusGrade.planVsPerformance.allocation.adherenceDisplay ? (
                        <div className="optx-grade-pvp-score">
                          {optimusGrade.planVsPerformance.allocation.adherenceDisplay}
                        </div>
                      ) : null}
                      <div className="optx-grade-mix-table">
                        <div className="optx-grade-mix-row optx-grade-mix-row--head">
                          <span>Platform</span>
                          <span>Approved</span>
                          <span>Actual</span>
                        </div>
                        {optimusGrade.planVsPerformance.allocation.rows.map((row, index) => (
                          <div className="optx-grade-mix-row" key={`alloc-${row.platform}-${index}`}>
                            <span>
                              <OptimusPlatformIdentity
                                platform={row.platform}
                                label={presentOptimusGradeText(row.label)}
                              />
                            </span>
                            <span>{presentOptimusGradeText(row.approvedDisplay) || "—"}</span>
                            <span>{presentOptimusGradeText(row.actualDisplay) || "—"}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {optimusGrade.planVsPerformance.pacingDetail?.rows?.length ? (
                    <div className="optx-grade-card optx-grade-pvp-card">
                      <div className="optx-grade-card-label">Flight Pacing</div>
                      {optimusGrade.planVsPerformance.pacingDetail.statusLabel ? (
                        <div className="optx-grade-pvp-score">
                          {presentOptimusGradeText(optimusGrade.planVsPerformance.pacingDetail.statusLabel)}
                          {Number.isFinite(optimusGrade.planVsPerformance.pacingDetail.score)
                            ? ` · ${optimusGrade.planVsPerformance.pacingDetail.score}/100`
                            : ""}
                        </div>
                      ) : null}
                      <div className="optx-grade-detail-lines">
                        {optimusGrade.planVsPerformance.pacingDetail.rows.map((row, index) =>
                          renderGradeDetailLine(row.label, row.value, `pacing-row-${index}`)
                        )}
                      </div>
                    </div>
                  ) : null}

                  <div className="optx-grade-card optx-grade-pvp-card">
                    <div className="optx-grade-card-label">KPI Attainment</div>
                    {optimusGrade.planVsPerformance.kpiAttainment?.rows?.length ? (
                      <div className="optx-grade-detail-lines">
                        {optimusGrade.planVsPerformance.kpiAttainment.rows.map((row, index) => (
                          <div className="optx-grade-detail-line" key={`kpi-attain-${index}`}>
                            <span className="optx-grade-detail-label">
                              <OptimusPlatformIdentity
                                platform={row.platform}
                                label={presentOptimusGradeText(row.label)}
                              />
                            </span>
                            <span className="optx-grade-detail-value">
                              {presentOptimusGradeText(row.actualDisplay)} vs{" "}
                              {presentOptimusGradeText(`${row.expectedDisplay} ${row.expectedBasisLabel || "expected"}`)}
                            </span>
                            <span className={`${getOptimusPillClass(row.tone)} optx-pill-compact`}>
                              {presentOptimusGradeText(row.statusLabel)}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="optx-grade-informational-note">
                        {presentOptimusGradeText(
                          optimusGrade.planVsPerformance.kpiAttainment?.informationalNote
                        ) || "KPI attainment is informational for this campaign."}
                      </div>
                    )}
                  </div>

                  {/*
                    * Channel decomposition of the blended eCPM.
                    *
                    * It lives HERE, inside Plan vs Performance and directly
                    * under KPI Attainment, because that is the comparison it
                    * explains. Sitting under the KPI tiles it read as a
                    * standalone widget beside numbers it was not talking about.
                    *
                    * A blended figure can hide opposing channel results: on the
                    * verified production case CTV is BELOW its planned rate
                    * while YouTube is far above it, and roughly half the
                    * aggregate variance is channel mix rather than rate.
                    * Showing the blend alone would read as a uniform failure
                    * and misrepresent the channel that is beating plan.
                    *
                    * Same contract as before the move — every value is read
                    * straight off resolveBlendedEcpmDecomposition. Nothing here
                    * derives, rounds or substitutes: an absent planned rate
                    * stays an em dash and keeps its NOT COMPARABLE status.
                    */}
                  {isAllPlatformsView && blendedEcpmDecomposition ? (
                    <div className="optx-grade-card optx-grade-pvp-card optx-grade-ecpm-card">
                      <div className="optx-grade-card-label">What is driving the blended eCPM</div>
                      {blendedEcpmDecomposition.headline ? (
                        <p className="optx-panel-subtitle optx-grade-ecpm-note">
                          {blendedEcpmDecomposition.headline}
                        </p>
                      ) : null}
                      {blendedEcpmDecomposition.effectNote ? (
                        <p className="optx-panel-subtitle optx-grade-ecpm-note optx-grade-ecpm-note--effect">
                          {blendedEcpmDecomposition.effectNote}
                        </p>
                      ) : null}
                      <div
                        className="optx-grade-ecpm-table"
                        role="table"
                        aria-label="Blended eCPM channel decomposition"
                      >
                        <div className="optx-grade-ecpm-row optx-grade-ecpm-row--head" role="row">
                          <span role="columnheader">Platform</span>
                          <span role="columnheader">Actual eCPM</span>
                          <span role="columnheader">Planned eCPM</span>
                          <span role="columnheader">Variance</span>
                          <span role="columnheader">Impressions</span>
                          <span role="columnheader">Client-Billed Spend</span>
                          <span role="columnheader">Impression Mix</span>
                          <span role="columnheader">Status</span>
                        </div>
                        {blendedEcpmDecomposition.rows.map((row) => (
                          <div className="optx-grade-ecpm-row" role="row" key={row.channel}>
                            <span className="optx-grade-ecpm-channel" role="cell" data-label="Platform">
                              <OptimusPlatformIdentity platform={row.channel} label={row.label} />
                            </span>
                            <span className="optx-grade-ecpm-num" role="cell" data-label="Actual eCPM">
                              {formatCurrency(row.actualClientBilledCpm)}
                            </span>
                            <span className="optx-grade-ecpm-num" role="cell" data-label="Planned eCPM">
                              {formatCurrency(row.plannedClientBilledCpm)}
                            </span>
                            <span className="optx-grade-ecpm-num" role="cell" data-label="Variance">
                              {typeof row.variancePercent === "number"
                                ? `${row.variancePercent > 0 ? "+" : ""}${row.variancePercent}%`
                                : "\u2014"}
                            </span>
                            <span className="optx-grade-ecpm-num" role="cell" data-label="Impressions">
                              {formatInteger(row.actualImpressions)}
                            </span>
                            <span className="optx-grade-ecpm-num" role="cell" data-label="Client-Billed Spend">
                              {formatCurrency(row.actualClientBilledSpend)}
                            </span>
                            <span className="optx-grade-ecpm-num" role="cell" data-label="Impression Mix">
                              {formatImpressionMix(row.impressionShare, row.plannedImpressionShare)}
                            </span>
                            <span className="optx-grade-ecpm-status" role="cell" data-label="Status">
                              <span className={`${pillClass(row.tone)} optx-pill-compact`}>
                                {row.state === "ABOVE_PLAN"
                                  ? "Above plan"
                                  : row.state === "BELOW_PLAN"
                                  ? "Beating plan"
                                  : row.state === "ON_PLAN"
                                  ? "On plan"
                                  : "Not comparable"}
                              </span>
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {optimusGrade.planVsPerformance.roles?.rows?.length ? (
                    <div className="optx-grade-card optx-grade-pvp-card">
                      <div className="optx-grade-card-label">Platform Roles</div>
                      {/*
                        * Platform Roles renders through its OWN grid, not the
                        * shared flex-wrap detail line: that made the row
                        * structure depend on how long the copy was, so the
                        * longer YouTube description pushed its status pill onto
                        * a second line while CTV stayed inline. The
                        * optx-grade-detail-* classes stay for typography; the
                        * optx-grade-role-* classes own the geometry.
                        */}
                      <div className="optx-grade-detail-lines optx-grade-role-rows">
                        {optimusGrade.planVsPerformance.roles.rows.map((row, index) => (
                          <div
                            className="optx-grade-role-row"
                            key={`role-${row.platform}-${index}`}
                            data-optimus-role-row={row.platform || ""}
                          >
                            <span className="optx-grade-detail-label optx-grade-role-platform">
                              {presentOptimusGradeText(row.label)}
                            </span>
                            <span className="optx-grade-detail-value optx-grade-role-description">
                              {presentOptimusGradeText(row.roleLabel)}
                            </span>
                            <span
                              className={`${getOptimusPillClass(row.tone)} optx-pill-compact optx-grade-role-status`}
                            >
                              {presentOptimusGradeText(row.statusLabel)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="optx-panel-subtitle">
                  Plan-versus-performance evidence is unavailable for this view.
                </div>
              )}

              {optimusGrade.insights.opportunities.length || optimusGrade.insights.watchouts.length ? (
                <div className="optx-grade-insights">
                  {optimusGrade.insights.opportunities.length ? (
                    <div className="optx-grade-insight-column optx-grade-insight-column--opportunity">
                      <div className="optx-grade-card-label">Opportunities</div>
                      <ul className="optx-grade-insight-list">
                        {optimusGrade.insights.opportunities.map((item, index) =>
                          renderGradeInsight(item, `opportunity-${index}`)
                        )}
                      </ul>
                    </div>
                  ) : null}
                  {optimusGrade.insights.watchouts.length ? (
                    <div className="optx-grade-insight-column optx-grade-insight-column--watchout">
                      <div className="optx-grade-card-label">Watchouts</div>
                      <ul className="optx-grade-insight-list">
                        {optimusGrade.insights.watchouts.map((item, index) =>
                          renderGradeInsight(item, `watchout-${index}`)
                        )}
                      </ul>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {optimusGrade.gradingDetails ? (
                <details className="optx-grade-details">
                  <summary className="optx-grade-details-summary">Grading Details</summary>
                  <div className="optx-grade-detail-lines">
                    {renderGradeDetailLine(
                      "Methodology",
                      optimusGrade.gradingDetails.methodologyVersion,
                      "detail-methodology"
                    )}
                    {renderGradeDetailLine(
                      "Weighting",
                      optimusGrade.gradingDetails.weighting
                        ? `Plan ${Math.round(
                            (optimusGrade.gradingDetails.weighting.configured?.plan_attainment || 0) * 100
                          )}% · Pacing ${Math.round(
                            (optimusGrade.gradingDetails.weighting.configured?.pacing_delivery || 0) * 100
                          )}% · Performance ${Math.round(
                            (optimusGrade.gradingDetails.weighting.configured?.performance || 0) * 100
                          )}% (${optimusGrade.gradingDetails.weighting.version})`
                        : null,
                      "detail-weighting"
                    )}
                    {renderGradeDetailLine(
                      "Minimum evidence",
                      optimusGrade.gradingDetails.minimumEvidence
                        ? `${
                            optimusGrade.gradingDetails.minimumEvidence.satisfied
                              ? "Satisfied"
                              : "Not satisfied"
                          } (${optimusGrade.gradingDetails.minimumEvidence.version})`
                        : null,
                      "detail-evidence"
                    )}
                    {renderGradeDetailLine(
                      "Confidence",
                      optimusGrade.gradingDetails.confidence,
                      "detail-confidence"
                    )}
                    {renderGradeDetailLine(
                      "Metrics excluded from grading",
                      Array.isArray(optimusGrade.gradingDetails.excludedMetrics) &&
                        optimusGrade.gradingDetails.excludedMetrics.length
                        ? `${optimusGrade.gradingDetails.excludedMetrics.length} metric readings carry zero grade weight (not applicable to their platform, or not measured in canonical evidence).`
                        : "None reported.",
                      "detail-exclusions"
                    )}
                    {/* Where every Performance target came from: the governing
                        approved strategy revision and its approval. The same
                        provenance the Campaign Strategy microsite states beside
                        its Platform Detail, so the two surfaces reconcile. */}
                    {renderGradeDetailLine(
                      "Forecast authority",
                      optimusGrade.gradingDetails.forecastAuthority?.headline,
                      "detail-forecast-authority"
                    )}
                    {(optimusGrade.gradingDetails.forecastProvenance || [])
                      .filter((row) => row && row.metric !== "reach")
                      .map((row) =>
                        renderGradeDetailLine(
                          `${row.platformLabel} ${row.metricLabel} target`,
                          [
                            row.targetDisplay,
                            row.sourceLabel,
                            row.strategyRevisionShort ? `revision ${row.strategyRevisionShort}` : null,
                            row.approvedAtDisplay ? `approved ${row.approvedAtDisplay}` : null,
                          ]
                            .filter(Boolean)
                            .join(" · "),
                          `detail-forecast-${row.platform}-${row.metric}`
                        )
                      )}
                    {/* Cost basis (optimus_client_billed_presentation_v1): every
                        monetary comparison is client-billed on both sides. One
                        backend-authored row per cost comparison — approved vs
                        actual, both client-billed, and the result. The portal
                        converts nothing and names no other basis. */}
                    {renderGradeDetailLine(
                      "Cost basis",
                      optimusGrade.gradingDetails.costBasis
                        ? [
                            optimusGrade.gradingDetails.costBasis.label,
                            optimusGrade.gradingDetails.costBasis.note,
                          ]
                            .filter(Boolean)
                            .join(" · ")
                        : null,
                      "detail-cost-basis"
                    )}
                    {(optimusGrade.gradingDetails.costComparisons || []).map((row, index) =>
                      renderGradeDetailLine(
                        `${row.platformLabel} ${row.metricLabel}`,
                        [
                          `approved ${row.approvedDisplay}`,
                          `actual ${row.actualDisplay}`,
                          row.attainmentDisplay && row.attainmentDisplay !== "—"
                            ? `${row.attainmentDisplay} attained`
                            : null,
                          row.resultLabel,
                        ]
                          .filter(Boolean)
                          .join(" · "),
                        `detail-cost-${row.platform}-${row.metric}-${index}`
                      )
                    )}
                  </div>
                  {optimusGrade.gradingDetails.goalBreakdown &&
                  optimusGrade.gradingDetails.goalBreakdown.state === "EVALUATED" ? (
                    <div className="optx-grade-goal-breakdown">
                      <div className="optx-grade-goal-breakdown-title">
                        {presentOptimusGradeText(
                          optimusGrade.gradingDetails.goalBreakdown.coverageDisplay
                            ? `Performance vs Optimization Goal · ${optimusGrade.gradingDetails.goalBreakdown.coverageDisplay}`
                            : "Performance vs Optimization Goal"
                        )}
                      </div>
                      {optimusGrade.gradingDetails.goalBreakdown.rows.map((row) => (
                        <div className="optx-grade-goal-row" key={`goal-${row.metric}`}>
                          <div className="optx-grade-goal-head">
                            <span className="optx-grade-goal-name">
                              {presentOptimusGradeText(row.goal)}
                            </span>
                            <span
                              className={`${getOptimusPillClass(
                                row.status === "GRADED" ? "good" : "neutral"
                              )} optx-pill-compact`}
                            >
                              {presentOptimusGradeText(
                                row.status === "GRADED"
                                  ? `Score ${row.score} · Weight ${row.weightDisplay}`
                                  : "Not graded"
                              )}
                            </span>
                          </div>
                          {row.status === "GRADED" ? (
                            <>
                              {row.members.map((member) => (
                                <div
                                  className="optx-grade-goal-member"
                                  key={`goal-${row.metric}-${member.platform}`}
                                >
                                  {presentOptimusGradeText(
                                    `${member.platformLabel}: ${member.actualDisplay} actual vs ${member.expectedToDateDisplay} ${member.expectedBasisLabel || "expected to date"} · ${member.attainmentDisplay} attained · score ${member.score}`
                                  )}
                                </div>
                              ))}
                              <div className="optx-grade-goal-authority">
                                {presentOptimusGradeText(row.authority)}
                              </div>
                            </>
                          ) : (
                            <div className="optx-grade-goal-member">
                              {presentOptimusGradeText(row.reason)}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : null}
                </details>
              ) : null}
            </section>
            </OptimusSectionBoundary>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Optimus;
